import {
  enableHorizontalScroll,
  formatPrice
} from "./chunk--chunk-7X5PGIXI.vbt.js";
import {
  addRemoveGwpItems,
  updateCartDiscountCodes
} from "./chunk--chunk-GBPFPGUS.vbt.js";
import {
  cartDataFncs,
  getCartData,
  hasTimeElapsed,
  setCartDataLocalStorage,
  shapeCartData
} from "./chunk--chunk-UDTFVZUR.vbt.js";
import {
  cartActions,
  cartAddItems,
  cartChange,
  cartMutateSequence,
  cartUpdateQuantities
} from "./chunk--chunk-TT72TRRF.vbt.js";
import {
  CART_LINE_ITEM_QUANTITY_MAX_DEFAULT,
  COMPLETE_REGIMEN_DISCOUNT_CODE_PREFIX,
  LOCAL_STORAGE_KEY_BYOB_CONFIG,
  ON_CHANGE_DEBOUNCE_TIMER,
  PRODUCT_PROPERTY_KEYS,
  PRODUCT_SELLING_PLAN_DEFAULT_ID,
  PUB_SUB_EVENTS,
  PUB_SUB_PAYLOADS,
  cartDebug,
  cartDebugHttp,
  debounce,
  debugOutlineElements,
  getCartDebugState,
  getLocalStorage,
  publish,
  setLocalStorage,
  subscribe
} from "./chunk--chunk-Z3ZRRM3S.vbt.js";
import "./chunk--chunk-Z7A6LSGX.vbt.js";

// src/js/helpers/ComponentConstructor.js
function initComponentOnConnectedComponentLoad(componentToInit, handleToWatch, selectorDispatch, element, dataSelect) {
  document.addEventListener(handleToWatch + " loaded", function handler() {
    const component = new componentToInit(element, dataSelect);
    if (component.init)
      component.init();
    document.dispatchEvent(new CustomEvent(selectorDispatch + " loaded"));
    this.removeEventListener(handleToWatch + " loaded", handler);
  });
}
function initComponentOnConnectedComponentDomEvent(componentToInit, handleToWatch, trigger, selectorDispatch, element, dataSelect) {
  document.querySelectorAll("[data-" + handleToWatch + "]").forEach(
    (elementTrigger) => elementTrigger.addEventListener(trigger, function handler() {
      const component = new componentToInit(element, dataSelect);
      if (component.init)
        component.init();
      document.dispatchEvent(new CustomEvent(selectorDispatch + " loaded"));
      this.removeEventListener(trigger, handler);
    })
  );
}
function initComponentOnSelfDomEvent(componentToInit, trigger, selectorDispatch, element, dataSelect) {
  if (window.innerWidth < 768 && trigger === "mouseover") {
    initComponentOnSelfIntersection(
      componentToInit,
      selectorDispatch,
      element,
      dataSelect
    );
  } else {
    element.addEventListener(trigger, function handler() {
      const component = new componentToInit(element, dataSelect);
      if (component.init)
        component.init();
      document.dispatchEvent(new CustomEvent(selectorDispatch + " loaded"));
      this.removeEventListener(trigger, handler);
    });
  }
}
function initComponentOnSelfIntersection(componentToInit, selectorDispatch, element, dataSelect) {
  const component = new componentToInit(element, dataSelect);
  if (component.init) {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        component.init();
        document.dispatchEvent(new CustomEvent(selectorDispatch + " loaded"));
        observer.disconnect();
      }
    }, {
      rootMargin: "300px 0px 0px 0px"
    });
    observer.observe(element);
  }
}
function ComponentConstructor(components) {
  Object.entries(components).forEach(([selector, Component]) => {
    const dataSelect = "data-" + selector;
    const matchedElements = document.querySelectorAll("[" + dataSelect + "]");
    matchedElements.forEach((element) => {
      if (Array.isArray(Component)) {
        if (Component.filter((option) => typeof option === "string" && option.includes("url:")).length > 0) {
          const urls = Component.map((option) => typeof option === "string" && option.includes("url:") ? option.split("url:")[1] : false);
          let shouldInit = false;
          for (const url of urls) {
            if (url && window.location.href.includes(url)) {
              shouldInit = true;
            }
          }
          if (shouldInit) {
            initComponentOnSelfIntersection(
              Component[0],
              selector,
              element,
              dataSelect
            );
            return;
          }
        }
        Component.forEach((option, i) => {
          if (i === 0 || option.includes("url:"))
            return;
          if (option.includes(":")) {
            const triggerType = option.split(":")[0];
            const triggerSelector = option.split(":")[1];
            if (triggerType === "load") {
              initComponentOnConnectedComponentLoad(
                Component[0],
                triggerSelector,
                selector,
                element,
                dataSelect
              );
            } else {
              initComponentOnConnectedComponentDomEvent(
                Component[0],
                triggerSelector,
                triggerType,
                selector,
                element,
                dataSelect
              );
            }
          } else {
            initComponentOnSelfDomEvent(
              Component[0],
              option,
              selector,
              element,
              dataSelect
            );
          }
        });
        return;
      }
      initComponentOnSelfIntersection(Component, selector, element, dataSelect);
    });
  });
}

// src/js/customElements/accessibility.js
var fixAlt = (el) => {
  if (!el.hasAttribute("alt"))
    el.setAttribute("alt", "");
};
document.querySelectorAll("img").forEach(fixAlt);
new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE)
        return;
      if (node.tagName === "IMG")
        fixAlt(node);
      node.querySelectorAll("img").forEach(fixAlt);
    });
  });
}).observe(document.body, { childList: true, subtree: true });

// src/js/customElements/cart.js
var CartRemoveButton = class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", (event) => {
      event.preventDefault();
      this?.classList.toggle("opacity-40");
      this?.classList.toggle("pointer-events-none");
      cartActions.cartChange({
        id: this.dataset.key,
        line: void 0,
        quantity: 0,
        sellingPlanId: void 0,
        properties: void 0
      });
    });
  }
};
customElements.define("cart-remove-button", CartRemoveButton);
var CartRemoveRegimenGroup = class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", (event) => {
      event.preventDefault();
      const itemKeys = (this.dataset.itemKeys || "").split(",").filter(Boolean);
      if (!itemKeys.length) {
        cartDebug("regimen-group remove skipped: no item keys");
        return;
      }
      this?.classList.toggle("opacity-40");
      this?.classList.toggle("pointer-events-none");
      const itemKeyQuantityMap = itemKeys.reduce((acc, itemKey) => {
        acc[itemKey] = 0;
        return acc;
      }, {});
      cartActions.cartUpdateQuantities(itemKeyQuantityMap);
    });
  }
};
customElements.define("cart-remove-regimen-group", CartRemoveRegimenGroup);
var CartLoadingSpinner = class extends HTMLElement {
  constructor() {
    super();
    this.cartItemKey = this.dataset["cartItemKey"];
  }
  connectedCallback() {
    this.cartLoadingUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartInFlight,
      (payload) => {
        if (this.cartItemKey && payload.id !== this.cartItemKey) {
          return;
        }
        if (payload.isInFlight === true) {
          this.enableLoading();
        } else {
          this.disableLoading();
        }
      },
      "cartLoadingSpinner"
    );
  }
  disconnectedCallback() {
    if (this.cartLoadingUnsubscriber) {
      this.cartLoadingUnsubscriber();
    }
  }
  enableLoading() {
    this.classList.remove("hidden");
  }
  disableLoading() {
    this.classList.add("hidden");
  }
};
customElements.define("cart-loading-spinner", CartLoadingSpinner);
var CartCustomElement = class extends HTMLElement {
  constructor() {
    super();
    this.cartInFlightDebugUnsubscriber = null;
  }
  connectedCallback() {
    this.cartInFlightDebugUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartInFlight,
      (payload) => {
        if (payload.isInFlight === true) {
          this.enableDebugOutline();
        }
      },
      "cartInFlight"
    );
  }
  disconnectedCallback() {
    if (this.cartInFlightDebugUnsubscriber) {
      this.cartInFlightDebugUnsubscriber();
    }
  }
  enableDebugOutline() {
    if (getCartDebugState() === true) {
      debugOutlineElements([this.firstElementChild], "yellow", 0);
    }
  }
};
customElements.define("cart-custom-element", CartCustomElement);
var CartInFlight = class extends HTMLElement {
  constructor() {
    super();
    this.cartLoadingUnsubscriber = null;
    this.cartItemKey = this.dataset["cartItemKey"];
    this.spinner = this.querySelector(".loading__spinner_spinner");
    this.childToHide = this.querySelector(".loading__spinner_child");
    this.mask = this.querySelector(".loading__spinner_mask");
    this.showSpinner = this.dataset["cartInFlightShowSpinner"] === "true";
    this.maskChild = this.dataset["cartInFlightMaskChild"] === "true";
    this.dimChild = this.dataset["cartInFlightDimChild"] === "true";
    this.hideChild = this.dataset["cartInFlightHideChild"] === "true";
    this.classToDimChild = this.hideChild ? "opacity-0" : "opacity-40";
  }
  connectedCallback() {
    this.cartLoadingUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartInFlight,
      (payload) => {
        if (this.cartItemKey && payload.id !== this.cartItemKey) {
          return;
        }
        if (payload.isInFlight === true) {
          this.enableLoading();
        } else {
          this.disableLoading();
        }
      },
      "cartInFlight"
    );
  }
  disconnectedCallback() {
    if (this.cartLoadingUnsubscriber) {
      this.cartLoadingUnsubscriber();
    }
  }
  enableLoading() {
    if (getCartDebugState() === true) {
      debugOutlineElements([this.childToHide], "blue", 0);
    }
    this.showSpinner && this.spinner?.classList.remove("hidden");
    this.showSpinner && this.spinner?.classList.add("flex");
    this.maskChild && this.mask?.classList.remove("hidden");
    this.dimChild && this.childToHide?.classList.add(this.classToDimChild);
  }
  disableLoading() {
    this.spinner?.classList.add("hidden");
    this.spinner?.classList.remove("flex");
    this.mask?.classList.add("hidden");
    this.childToHide?.classList.remove(this.classToDimChild);
  }
};
customElements.define("cart-in-flight", CartInFlight);
var CartInFlightOverlay = class extends HTMLElement {
  constructor() {
    super();
    this.cartLoadingUnsubscriber = null;
    this.hideOverlayTimeout = null;
    this.spinner = this.querySelector(".cart-in-flight-overlay__spinner");
    this.mask = this.querySelector(".cart-in-flight-overlay__mask");
    this.childToHide = this.querySelector(".cart-in-flight-overlay__child");
    this.showSpinner = this.dataset["cartInFlightShowSpinner"] === "true";
    this.maskChild = this.dataset["cartInFlightMaskChild"] === "true";
    this.dimChild = this.dataset["cartInFlightDimChild"] === "true";
    this.hideChild = this.dataset["cartInFlightHideChild"] === "true";
    this.persistAfterInFlight = this.dataset["cartInFlightPersistAfterInFlight"] === "true";
    const hideDelayMs = Number.parseInt(
      this.dataset["cartInFlightPersistHideDelayMs"] || "5000",
      10
    );
    this.persistHideDelayMs = Number.isNaN(hideDelayMs) ? 5e3 : hideDelayMs;
    this.classToDimChild = this.hideChild ? "opacity-0" : "opacity-40";
  }
  connectedCallback() {
    this.cartLoadingUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartInFlight,
      (payload) => {
        if (payload.isInFlight === true && payload.action == "add") {
          this.enableLoading();
          return;
        }
        this.disableLoading();
      },
      "cartInFlightOverlay"
    );
  }
  disconnectedCallback() {
    if (this.cartLoadingUnsubscriber) {
      this.cartLoadingUnsubscriber();
    }
    this.clearHideOverlayTimeout();
  }
  clearHideOverlayTimeout() {
    if (this.hideOverlayTimeout) {
      clearTimeout(this.hideOverlayTimeout);
      this.hideOverlayTimeout = null;
    }
  }
  showOverlay() {
    if (getCartDebugState() === true) {
      debugOutlineElements([this.mask], "blue", 0);
    }
    this.showSpinner && this.spinner?.classList.remove("hidden");
    this.showSpinner && this.spinner?.classList.add("flex");
    this.maskChild && this.mask?.classList.remove("hidden");
    this.dimChild && this.childToHide?.classList.add(this.classToDimChild);
  }
  hideOverlay() {
    this.spinner?.classList.add("hidden");
    this.spinner?.classList.remove("flex");
    this.mask?.classList.add("hidden");
    this.childToHide?.classList.remove(this.classToDimChild);
  }
  enableLoading() {
    this.clearHideOverlayTimeout();
    this.showOverlay();
  }
  disableLoading() {
    this.clearHideOverlayTimeout();
    if (this.persistAfterInFlight) {
      this.hideOverlayTimeout = setTimeout(() => {
        this.hideOverlay();
        this.hideOverlayTimeout = null;
      }, this.persistHideDelayMs);
      return;
    }
    this.hideOverlay();
  }
};
customElements.define("cart-in-flight-overlay", CartInFlightOverlay);
var CartItems = class extends HTMLElement {
  constructor() {
    super();
    this.cartDebugState = getCartDebugState();
  }
  connectedCallback() {
    this.cartLoadingUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartInFlight,
      (payload) => {
        if (payload.isInFlight === true) {
          this.enableLoading({ key: payload.id, line: payload.line });
        } else {
          this.disableLoading({ key: payload.id, line: payload.line });
        }
      },
      "cartItems"
    );
    if (this.cartDebugState === true) {
      this.addDebugUI();
    }
  }
  disconnectedCallback() {
    if (this.cartLoadingUnsubscriber) {
      this.cartLoadingUnsubscriber();
    }
  }
  addStylesheet(styleString) {
    var styleEl = document.createElement("style");
    styleEl.innerText = styleString;
    this.prepend(styleEl);
  }
  addDebugUI() {
    this.addStylesheet(`
      .debug-controls {
        border: 2px solid yellow;
      }
      .debug-controls button {
        padding: 4px;
      }
    `);
    const debugControls = document.createElement("div");
    debugControls.classList.add("debug-controls");
    const refreshCart = document.createElement("button");
    refreshCart.textContent = "Refresh Cart";
    refreshCart.addEventListener("click", () => {
      cartActions.cartRefresh();
    });
    debugControls.append(refreshCart);
    const clearCart = document.createElement("button");
    clearCart.textContent = "Clear Cart";
    clearCart.addEventListener("click", () => {
      cartActions.cartClear();
    });
    debugControls.append(clearCart);
    this.prepend(debugControls);
  }
  enableLoading({ key, line }) {
    this.classList.add("pointer-events-none");
    document.activeElement?.blur();
  }
  disableLoading({ key, line }) {
    this.classList.remove("pointer-events-none");
  }
};
customElements.define("cart-items", CartItems);

// src/js/customElements/free-shipping-bar.js
var CURRENCY_PREFIXES = {
  AUD: "$",
  CAD: "$",
  EUR: "\u20AC",
  GBP: "\xA3",
  MYR: "RM",
  USD: "$"
};
var FreeShippingBar = class extends HTMLElement {
  constructor() {
    super();
    this.cartSubtotal = this.toCents(this.dataset["cartSubtotal"]);
    this.cartItemCount = parseInt(this.dataset["cartItemCount"] ?? "0");
    this.hasSubscription = this.dataset["hasSubscription"] === "true";
    this.currencyCode = this.dataset["currencyCode"] || "USD";
    this.currencySymbol = this.dataset["currencySymbol"] || "$";
    this.promotions = this.getJson("[data-promotions]", []).map((promotion, index) => ({
      index,
      threshold: Math.max(this.toCents(promotion.threshold), 0),
      label: promotion.label || promotion.description || "",
      description: promotion.description || promotion.label || "",
      type: `${promotion.type || "discount"}`
    })).filter((promotion) => promotion.label || promotion.description).sort((a, b) => a.threshold - b.threshold || a.index - b.index).slice(0, 3);
    this.strings = {
      addMore: this.dataset["addMore"] || "Add [amount] more to unlock [promotion]",
      earnedAddMore: this.dataset["earnedAddMore"] || "You've earned [earned]. Add [amount] more to unlock [promotion]",
      earnedOnly: this.dataset["earnedOnly"] || "You've earned [earned].",
      allUnlocked: this.dataset["allUnlocked"] || "Congrats! You've unlocked [earned].",
      defaultPromotion: this.dataset["defaultPromotion"] || "this promotion",
      earnedSeparator: this.dataset["earnedSeparator"] || " and "
    };
    this.elProgress = this.querySelector("[data-progress]");
    this.elMessage = this.querySelector("[data-message]");
    this.elMarkers = this.querySelector("[data-progress-markers]");
    this.elProgressWrap = this.querySelector("[data-progress-wrap]");
  }
  connectedCallback() {
    this.updateElements({
      subtotal: this.cartSubtotal,
      itemCount: this.cartItemCount,
      hasSubscription: this.hasSubscription
    });
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartMutationComplete,
      (payload) => {
        this.onCartUpdate(payload.cartData.cartRaw);
      },
      "freeShippingBar"
    );
  }
  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }
  getJson(selector, fallback) {
    try {
      const el = this.querySelector(selector);
      return el ? JSON.parse(el.textContent) : fallback;
    } catch (err) {
      console.error("FreeShippingBar: invalid JSON config", err);
      return fallback;
    }
  }
  toCents(value) {
    const parsedValue = parseInt(value ?? "0");
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }
  onCartUpdate(cartRaw) {
    this.updateElements({
      subtotal: this.toCents(cartRaw.items_subtotal_price ?? cartRaw.total_price),
      itemCount: parseInt(cartRaw.item_count ?? "0"),
      hasSubscription: cartRaw.items?.some((item) => item.selling_plan_allocation)
    });
  }
  formatMoney(cents) {
    const amount = Math.max(cents, 0) / 100;
    const maximumFractionDigits = amount % 1 === 0 ? 0 : 2;
    const currencyPrefix = CURRENCY_PREFIXES[this.currencyCode.toUpperCase()];
    try {
      if (currencyPrefix) {
        const formattedAmount = new Intl.NumberFormat(
          document.documentElement.lang || void 0,
          { maximumFractionDigits }
        ).format(amount);
        return `${currencyPrefix}${formattedAmount}`;
      }
      return new Intl.NumberFormat(document.documentElement.lang || void 0, {
        style: "currency",
        currency: this.currencyCode,
        maximumFractionDigits
      }).format(amount);
    } catch (err) {
      const formattedAmount = amount.toFixed(maximumFractionDigits);
      return `${this.currencySymbol}${formattedAmount}`;
    }
  }
  isFreeShippingPromotion(promotion) {
    return promotion.type.toLowerCase().replace(/[\s-]+/g, "_") === "free_shipping";
  }
  isPromotionUnlocked(promotion, subtotal, hasSubscription) {
    if (this.isFreeShippingPromotion(promotion) && hasSubscription) {
      return true;
    }
    return subtotal >= promotion.threshold;
  }
  getPromotionDescription(promotion) {
    return promotion?.description || promotion?.label || this.strings.defaultPromotion;
  }
  formatList(items) {
    const filteredItems = items.filter(Boolean);
    if (filteredItems.length <= 1) {
      return filteredItems;
    }
    if (filteredItems.length === 2) {
      return [filteredItems[0], this.strings.earnedSeparator, filteredItems[1]];
    }
    return filteredItems.flatMap((item, index) => {
      if (index === 0) {
        return [item];
      }
      if (index === filteredItems.length - 1) {
        return [",", this.strings.earnedSeparator, item];
      }
      return [", ", item];
    });
  }
  appendTextFragment(parent, text, isStrong = false) {
    if (!text) {
      return;
    }
    if (isStrong) {
      const strong = document.createElement("strong");
      strong.textContent = text;
      parent.append(strong);
      return;
    }
    parent.append(document.createTextNode(text));
  }
  appendTemplate(parent, template, replacements) {
    const tokenPattern = /\[(amount|promotion|earned)\]/g;
    let lastIndex = 0;
    let match;
    while ((match = tokenPattern.exec(template)) !== null) {
      this.appendTextFragment(parent, template.slice(lastIndex, match.index));
      const replacement = replacements[match[1]];
      const replacementItems = Array.isArray(replacement) ? replacement : [replacement];
      replacementItems.forEach((item) => {
        if (typeof item === "string") {
          this.appendTextFragment(parent, item);
          return;
        }
        this.appendTextFragment(parent, item.text, item.strong);
      });
      lastIndex = tokenPattern.lastIndex;
    }
    this.appendTextFragment(parent, template.slice(lastIndex));
  }
  renderMessage(nextPromotion, earnedPromotions, allUnlocked, subtotal, hasVisiblePromotions = true) {
    if (!this.elMessage) {
      return;
    }
    this.elMessage.replaceChildren();
    const earnedItems = this.formatList(
      earnedPromotions.map((promotion) => ({
        text: this.getPromotionDescription(promotion),
        strong: true
      }))
    );
    if (!hasVisiblePromotions && earnedItems.length) {
      this.appendTemplate(this.elMessage, this.strings.earnedOnly, {
        earned: earnedItems
      });
      return;
    }
    if (allUnlocked) {
      this.appendTemplate(this.elMessage, this.strings.allUnlocked, {
        earned: earnedItems
      });
      return;
    }
    const nextPromotionItem = {
      text: this.getPromotionDescription(nextPromotion),
      strong: true
    };
    if (earnedItems.length) {
      this.appendTemplate(this.elMessage, this.strings.earnedAddMore, {
        amount: this.formatMoney(nextPromotion.threshold - subtotal),
        promotion: nextPromotionItem,
        earned: earnedItems
      });
      return;
    }
    this.appendTemplate(this.elMessage, this.strings.addMore, {
      amount: this.formatMoney(nextPromotion.threshold - subtotal),
      promotion: nextPromotionItem
    });
  }
  updateElements({ subtotal = 0, itemCount = 0, hasSubscription = false }) {
    if (!this.promotions.length) {
      this.style.display = "none";
      return;
    }
    const visiblePromotions = hasSubscription ? this.promotions.filter((promotion) => !this.isFreeShippingPromotion(promotion)) : this.promotions;
    const maxThreshold = Math.max(...visiblePromotions.map((promotion) => promotion.threshold), 0);
    const percent = maxThreshold === 0 ? 100 : Math.min(Math.max(subtotal / maxThreshold * 100, itemCount > 0 ? 1 : 0), 100);
    if (this.elProgress) {
      this.elProgress.style.width = `${percent}%`;
    }
    const promotionsWithState = visiblePromotions.map((promotion) => ({
      ...promotion,
      unlocked: this.isPromotionUnlocked(promotion, subtotal, hasSubscription)
    }));
    const allPromotionsWithState = this.promotions.map((promotion) => ({
      ...promotion,
      unlocked: this.isPromotionUnlocked(promotion, subtotal, hasSubscription)
    }));
    const nextPromotion = allPromotionsWithState.find((promotion) => !promotion.unlocked);
    const allUnlocked = !nextPromotion;
    const earnedPromotions = allPromotionsWithState.filter((promotion) => promotion.unlocked);
    this.renderMessage(
      nextPromotion,
      earnedPromotions,
      allUnlocked,
      subtotal,
      visiblePromotions.length > 0
    );
    if (this.elProgressWrap) {
      this.elProgressWrap.classList.toggle("hidden", !visiblePromotions.length);
    }
    if (this.elMarkers) {
      this.elMarkers.replaceChildren(
        ...promotionsWithState.map((promotion) => this.createMarker(promotion, maxThreshold))
      );
    }
  }
  createMarker(promotion, maxThreshold) {
    const marker = document.createElement("div");
    const position = maxThreshold === 0 ? 0 : Math.min(promotion.threshold / maxThreshold * 100, 100);
    const edge = position <= 2 ? "start" : position >= 98 ? "end" : "";
    marker.className = `free-shipping-progress__marker${promotion.unlocked ? " is-unlocked" : ""}`;
    marker.style.setProperty("--marker-position", `${position}%`);
    if (edge) {
      marker.dataset.edge = edge;
    }
    const threshold = document.createElement("span");
    threshold.className = "free-shipping-progress__threshold eyebrow-2 text-text-primary";
    threshold.textContent = this.formatMoney(promotion.threshold);
    const dot = document.createElement("span");
    dot.className = "free-shipping-progress__dot";
    const bubble = document.createElement("span");
    bubble.className = "free-shipping-progress__bubble body-copy-3 text-text-primary";
    const bubbleText = document.createElement("span");
    bubbleText.className = "free-shipping-progress__bubbleText";
    bubbleText.textContent = promotion.label || promotion.description;
    bubble.append(bubbleText);
    marker.append(threshold, dot, bubble);
    return marker;
  }
};
customElements.define("free-shipping-bar", FreeShippingBar);

// src/js/customElements/input-select-subscription.js
var InputSelectSubscription = class extends HTMLElement {
  constructor() {
    super();
    this.productFormId = this.dataset["productFormId"];
    this.defaultSellingPlanId = this.dataset["defaultSellingPlanId"];
    this.itemKey = this.dataset["itemKey"];
    this.line = this.dataset["line"];
    this.quantity = this.dataset["quantity"];
    this.productId = this.dataset["productId"];
    this.elSelect = this.querySelector("select");
    this.elOptions = this.querySelectorAll("option");
    this.elFacadeSpan = this.querySelector(
      "[data-input-select-subscription-facade] > span"
    );
    this.elMask = this.querySelector("[data-input-select-subscription-mask]");
    this.elAdditionalSubscriptionInfo = document.querySelector(
      `[data-input-select-subscription-additionalSubscriptionInfo="${this.itemKey}"]`
    );
    this.elAdditionalSubscriptionInfoSpan = this.elAdditionalSubscriptionInfo?.querySelector("span");
    this.stringSeparatorForAdditionalSubscriptionInfo = "- ";
  }
  connectedCallback() {
    if (this.defaultSellingPlanId) {
      this.updateProductFormSellingPlan(this.defaultSellingPlanId);
    }
    this.updateFacadeTextContent();
    this.elSelect?.addEventListener("change", this.handleSelectChange);
  }
  disconnectedCallback() {
    this.elSelect?.removeEventListener("change", this.handleSelectChange);
  }
  updateFacadeTextContent = () => {
    for (let el of this.elOptions) {
      let isSelected = el.selected;
      if (isSelected) {
        let textFromSelectOption = el.textContent ?? "";
        let separateSubscriptionDurationAndAdditionalInfo = textFromSelectOption.split(
          this.stringSeparatorForAdditionalSubscriptionInfo
        );
        let textFromSelectOptionWithoutAdditionalInfo = separateSubscriptionDurationAndAdditionalInfo[0];
        if (this.elFacadeSpan) {
          this.elFacadeSpan.textContent = textFromSelectOptionWithoutAdditionalInfo;
        }
        let additionalInfo = separateSubscriptionDurationAndAdditionalInfo[1];
        if (this.elAdditionalSubscriptionInfo && this.elAdditionalSubscriptionInfoSpan) {
          if (additionalInfo) {
            this.elAdditionalSubscriptionInfoSpan.textContent = additionalInfo;
            this.elAdditionalSubscriptionInfo.classList.remove("hidden");
          } else {
            this.elAdditionalSubscriptionInfoSpan.textContent = "";
            this.elAdditionalSubscriptionInfo.classList.add("hidden");
          }
        }
        break;
      }
    }
  };
  updateProductFormSellingPlan = (sellingPlanId = PRODUCT_SELLING_PLAN_DEFAULT_ID) => {
    publish(
      PUB_SUB_EVENTS.productSellingPlanChange,
      {
        productFormId: this.productFormId,
        sellingPlanId
      },
      "input-select-subscription"
    );
  };
  updateCartLineItemSellingPlan = (sellingPlanId = PRODUCT_SELLING_PLAN_DEFAULT_ID) => {
    if (this.elSelect) {
      this.elSelect.disabled = true;
    }
    if (this.elMask) {
      this.elMask.classList.remove("hidden");
    }
    cartChange({
      id: this.itemKey,
      quantity: this.quantity,
      sellingPlanId,
      properties: {
        [PRODUCT_PROPERTY_KEYS.isSubscription]: sellingPlanId ? true : false
      }
    });
  };
  handleSelectChange = (event) => {
    console.log(event.target.value);
    event.stopPropagation();
    this.updateFacadeTextContent();
    let options = event.target.options;
    let selectedIndex = event.target.selectedIndex;
    let selectedOption = options[selectedIndex];
    let sellingPlanId = selectedOption.getAttribute(
      "data-option-sellingPlanId"
    );
    const cartItems = this.closest("cart-items");
    if (this.itemKey && cartItems) {
      this.updateCartLineItemSellingPlan(sellingPlanId);
    } else {
      this.updateProductFormSellingPlan(sellingPlanId);
    }
  };
};
customElements.define("input-select-subscription", InputSelectSubscription);

// src/js/customElements/input-select-subscription-regimen-group.js
var InputSelectSubscriptionRegimenGroup = class extends HTMLElement {
  constructor() {
    super();
    this.itemKeys = (this.dataset["itemKeys"] || "").split(",").filter(Boolean);
    this.itemQuantities = (this.dataset["itemQuantities"] || "").split(",").filter(Boolean);
    this.elSelect = this.querySelector("select");
    this.elOptions = this.querySelectorAll("option");
    this.elFacadeSpan = this.querySelector(
      "[data-input-select-subscription-regimen-group-facade] > span"
    );
    this.elMask = this.querySelector(
      "[data-input-select-subscription-regimen-group-mask]"
    );
  }
  connectedCallback() {
    this.updateFacadeTextContent();
    this.elSelect?.addEventListener("change", this.handleSelectChange);
  }
  disconnectedCallback() {
    this.elSelect?.removeEventListener("change", this.handleSelectChange);
  }
  updateFacadeTextContent = () => {
    for (const el of this.elOptions) {
      if (el.selected && this.elFacadeSpan) {
        this.elFacadeSpan.textContent = el.textContent ?? "";
        break;
      }
    }
  };
  /**
   * @param {string | null} rawSellingPlanId
   * @returns {number | null}
   */
  normalizeSellingPlanId = (rawSellingPlanId) => {
    if (!rawSellingPlanId) {
      return PRODUCT_SELLING_PLAN_DEFAULT_ID;
    }
    const parsedSellingPlanId = parseInt(rawSellingPlanId, 10);
    if (Number.isNaN(parsedSellingPlanId)) {
      return PRODUCT_SELLING_PLAN_DEFAULT_ID;
    }
    return parsedSellingPlanId;
  };
  /**
   * @param {string | null} rawSellingPlanIds
   * @returns {(number | null)[]}
   */
  normalizeSellingPlanIds = (rawSellingPlanIds) => {
    if (!rawSellingPlanIds) {
      return [];
    }
    return rawSellingPlanIds.split(",").map((rawSellingPlanId) => {
      const parsedSellingPlanId = parseInt(rawSellingPlanId, 10);
      if (Number.isNaN(parsedSellingPlanId)) {
        return PRODUCT_SELLING_PLAN_DEFAULT_ID;
      }
      return parsedSellingPlanId;
    });
  };
  /**
   * @returns {Promise<void>}
   */
  waitForCartMutationResult = () => {
    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) {
          return;
        }
        settled = true;
        unsubscribeComplete();
        unsubscribeError();
        resolve();
      };
      const unsubscribeComplete = subscribe(
        PUB_SUB_EVENTS.cartMutationComplete,
        () => finish(),
        "input-select-subscription-regimen-group"
      );
      const unsubscribeError = subscribe(
        PUB_SUB_EVENTS.cartMutationError,
        () => finish(),
        "input-select-subscription-regimen-group"
      );
    });
  };
  /**
   * @param {{ fallbackSellingPlanId?: number | null, sellingPlanIds?: (number | null)[], itemKeys?: string[], itemQuantities?: string[] }} params
   */
  updateCartLineItemsSellingPlan = async ({
    fallbackSellingPlanId = PRODUCT_SELLING_PLAN_DEFAULT_ID,
    sellingPlanIds = [],
    itemKeys = this.itemKeys,
    itemQuantities = this.itemQuantities
  } = {}) => {
    const hasItemSpecificSellingPlanIds = sellingPlanIds.length > 0;
    if (!itemKeys.length) {
      cartDebug("regimen-group selling plan update skipped: no item keys");
      return;
    }
    cartDebug("regimen-group selling plan update", {
      itemCount: itemKeys.length,
      fallbackSellingPlanId,
      hasItemSpecificSellingPlanIds
    });
    if (this.elSelect) {
      this.elSelect.disabled = true;
    }
    if (this.elMask) {
      this.elMask.classList.remove("hidden");
    }
    for (let i = 0; i < itemKeys.length; i += 1) {
      const itemKey = itemKeys[i];
      const quantity = parseInt(itemQuantities[i] ?? "1", 10) || 1;
      const itemSellingPlanId = hasItemSpecificSellingPlanIds ? sellingPlanIds[i] : fallbackSellingPlanId;
      if (hasItemSpecificSellingPlanIds && itemSellingPlanId === void 0) {
        cartDebug("regimen-group selling plan update skipped: missing item plan", {
          itemKey,
          itemIndex: i
        });
        continue;
      }
      cartChange({
        id: itemKey,
        line: void 0,
        quantity,
        sellingPlanId: itemSellingPlanId,
        properties: {
          [PRODUCT_PROPERTY_KEYS.isSubscription]: itemSellingPlanId ? true : false
        }
      });
      await this.waitForCartMutationResult();
    }
  };
  /**
   * @param {Event} event
   */
  handleSelectChange = (event) => {
    event.stopPropagation();
    this.updateFacadeTextContent();
    const options = event.target.options;
    const selectedIndex = event.target.selectedIndex;
    const selectedOption = options[selectedIndex];
    const fallbackSellingPlanId = this.normalizeSellingPlanId(
      selectedOption.dataset.optionSellingPlanId
    );
    const sellingPlanIds = this.normalizeSellingPlanIds(
      selectedOption.dataset.optionSellingPlanIds
    );
    const optionItemKeys = (selectedOption.dataset.optionItemKeys || "").split(",").filter(Boolean);
    const optionItemQuantities = (selectedOption.dataset.optionItemQuantities || "").split(",").filter(Boolean);
    const resolvedItemKeys = optionItemKeys.length ? optionItemKeys : this.itemKeys;
    const resolvedItemQuantities = optionItemQuantities.length ? optionItemQuantities : this.itemQuantities;
    cartDebug("regimen-group subscription select changed", {
      selectedIndex,
      fallbackSellingPlanId,
      sellingPlanIds,
      optionItemKeysCount: optionItemKeys.length,
      resolvedItemKeysCount: resolvedItemKeys.length
    });
    this.updateCartLineItemsSellingPlan({
      fallbackSellingPlanId,
      sellingPlanIds,
      itemKeys: resolvedItemKeys,
      itemQuantities: resolvedItemQuantities
    });
  };
};
customElements.define(
  "input-select-subscription-regimen-group",
  InputSelectSubscriptionRegimenGroup
);

// src/js/customElements/subscription-widget.js
var SubscriptionWidget = class extends HTMLElement {
  constructor() {
    super();
    this.productFormId = this.dataset["productFormId"];
    this.elSelect = this.querySelector("input-select-subscription select");
    this.elInputOnetime = this.querySelector("input#purchaseType--onetime");
    this.elInputSubscription = this.querySelector(
      "input#purchaseType--subscription"
    );
  }
  updateProductFormSellingPlan = (sellingPlanId = PRODUCT_SELLING_PLAN_DEFAULT_ID) => {
    publish(
      PUB_SUB_EVENTS.productSellingPlanChange,
      {
        productFormId: this.productFormId,
        sellingPlanId
      },
      "subscription-widget"
    );
  };
  handleChangePurchaseTypeOneTime = (event) => {
    const value = event.target.value;
    cartDebug("SubscriptionWidget handleChangePurchaseType:", value);
    this.updateProductFormSellingPlan(PRODUCT_SELLING_PLAN_DEFAULT_ID);
  };
  handleChangePurchaseTypeSubscription = (event) => {
    const value = event.target.value;
    cartDebug("SubscriptionWidget handleChangePurchaseType:", value);
    if (this.elSelect) {
      let options = this.elSelect.options;
      let selectedIndex = this.elSelect.selectedIndex;
      let selectedOption = options[selectedIndex];
      let sellingPlanId = selectedOption.getAttribute(
        "data-option-sellingPlanId"
      );
      this.updateProductFormSellingPlan(sellingPlanId);
    }
  };
  connectedCallback() {
    this.elInputSubscription?.addEventListener(
      "input",
      this.handleChangePurchaseTypeSubscription
    );
    this.elInputOnetime?.addEventListener(
      "input",
      this.handleChangePurchaseTypeOneTime
    );
  }
  disconnectedCallback() {
    this.elInputSubscription?.removeEventListener(
      "input",
      this.handleChangePurchaseTypeSubscription
    );
    this.elInputOnetime?.removeEventListener(
      "input",
      this.handleChangePurchaseTypeOneTime
    );
  }
};
customElements.define("subscription-widget", SubscriptionWidget);

// src/js/customElements/quantity-input.js
var QuantityInput = class extends HTMLElement {
  constructor() {
    super();
    this.productFormId = this.dataset["productFormId"];
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.buttonIncrease = this.querySelector("button[data-quantity-increase]");
    this.buttonDecrease = this.querySelector("button[data-quantity-decrease]");
    this.min = parseInt(this.dataset["min"] ?? "1");
  }
  connectedCallback() {
    this.input?.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach(
      (button) => button.addEventListener("click", this.onButtonClick.bind(this))
    );
    this.updateQuantityButtonsState();
  }
  disconnectedCallback() {
    this.input?.removeEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach(
      (button) => button.removeEventListener("click", this.onButtonClick.bind(this))
    );
  }
  resetQuantityInput(id) {
    cartDebug("resetQuantityInput", id);
    if (this.input) {
      this.input.value = this.input.getAttribute("value") ?? this.min.toString();
      cartDebug("Quantity RESET to:", this.input.value);
      this.updateQuantityButtonsState();
    }
  }
  handleInvalidInput(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }
  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    const key = event.target.dataset.key;
    const min = parseInt(event.target.dataset.min);
    const max = parseInt(event.target.dataset.max);
    const step = parseInt(event.target.step);
    let message = "";
    if (inputValue < min) {
      message = window.quickOrderListStrings.min_error.replace("[min]", min);
    } else if (inputValue > max) {
      message = window.quickOrderListStrings.max_error.replace("[max]", max);
    } else if (inputValue % step !== 0) {
      message = window.quickOrderListStrings.step_error.replace("[step]", step);
    }
    if (message) {
      cartDebug({ message });
      this.handleInvalidInput(event, index, message);
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      if (this.productFormId) {
        publish(PUB_SUB_EVENTS.productQuantityChange, {
          productFormId: this.productFormId,
          quantity: inputValue
        });
      } else {
        cartChange({
          id: key,
          quantity: inputValue
        });
      }
    }
  }
  onInputChange(event) {
    this.updateQuantityButtonsState();
    cartDebug("Quantity changed to:", event.target.value);
    this.validateQuantity(event);
  }
  // note: cart.js validateQuantity() handles enforcing min/max/step and resetting invalid input; this function is somewhat redundant; consider whether to consolidate;
  onButtonClick(event) {
    event.preventDefault();
    if (!this.input)
      return;
    const previousValue = this.input.value;
    if (event.target.name === "plus") {
      if (this.min > parseInt(this.input.step) && parseInt(this.input.value) <= this.min) {
        this.input.value = this.min.toString();
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }
    if (previousValue !== this.input.value) {
      this.input.dispatchEvent(this.changeEvent);
    }
    if (this.min === parseInt(previousValue) && event.target.name === "minus") {
      this.input.value = this.min.toString();
    }
  }
  updateQuantityButtonsState() {
    const value = this.input?.value;
    const min = this.input?.dataset.min;
    const max = this.input?.dataset.max;
    if (value) {
      if (min) {
        let valAtMin = parseInt(value) <= parseInt(min);
        this.buttonDecrease?.classList.toggle("opacity-50", valAtMin);
        this.buttonDecrease?.classList.toggle("pointer-events-none", valAtMin);
      }
      if (max) {
        let valAtMax = parseInt(value) >= parseInt(max);
        this.buttonIncrease?.classList.toggle("opacity-50", valAtMax);
        this.buttonIncrease?.classList.toggle("pointer-events-none", valAtMax);
      }
    }
  }
};
customElements.define("quantity-input", QuantityInput);

// src/js/customElements/quantity-input-regimen-group.js
var QuantityInputRegimenGroup = class extends HTMLElement {
  constructor() {
    super();
    this.itemKeys = (this.dataset.itemKeys || "").split(",").filter(Boolean);
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.buttonIncrease = this.querySelector("button[data-quantity-increase]");
    this.buttonDecrease = this.querySelector("button[data-quantity-decrease]");
    this.min = parseInt(this.dataset.min ?? "1");
    this.onInputChangeHandler = this.onInputChange.bind(this);
    this.onButtonClickHandler = this.onButtonClick.bind(this);
  }
  connectedCallback() {
    this.input?.addEventListener("change", this.onInputChangeHandler);
    this.querySelectorAll("button").forEach(
      (button) => button.addEventListener("click", this.onButtonClickHandler)
    );
    this.updateQuantityButtonsState();
  }
  disconnectedCallback() {
    this.input?.removeEventListener("change", this.onInputChangeHandler);
    this.querySelectorAll("button").forEach(
      (button) => button.removeEventListener("click", this.onButtonClickHandler)
    );
  }
  resetQuantityInput() {
    cartDebug("resetQuantityInput (regimen group)");
    if (this.input) {
      this.input.value = this.input.getAttribute("value") ?? this.min.toString();
      cartDebug("Quantity RESET to:", this.input.value);
      this.updateQuantityButtonsState();
    }
  }
  handleInvalidInput(message) {
    this.input?.setCustomValidity(message);
    this.input?.reportValidity();
    this.resetQuantityInput();
    this.input?.select();
  }
  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const min = parseInt(event.target.dataset.min);
    const max = parseInt(event.target.dataset.max);
    const step = parseInt(event.target.step);
    let message = "";
    if (inputValue < min) {
      message = window.quickOrderListStrings.min_error.replace("[min]", min);
    } else if (inputValue > max) {
      message = window.quickOrderListStrings.max_error.replace("[max]", max);
    } else if (inputValue % step !== 0) {
      message = window.quickOrderListStrings.step_error.replace("[step]", step);
    }
    if (message) {
      cartDebug({ message });
      this.handleInvalidInput(message);
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.updateQuantityForGroup(inputValue);
    }
  }
  updateQuantityForGroup(newQuantity) {
    if (!this.itemKeys.length) {
      cartDebug("regimen-group quantity update skipped: no item keys");
      return;
    }
    const itemKeyQuantityMap = this.itemKeys.reduce((acc, itemKey) => {
      acc[itemKey] = newQuantity;
      return acc;
    }, {});
    cartActions.cartUpdateQuantities(itemKeyQuantityMap);
  }
  onInputChange(event) {
    this.updateQuantityButtonsState();
    cartDebug("Quantity changed to:", event.target.value);
    this.validateQuantity(event);
  }
  onButtonClick(event) {
    event.preventDefault();
    if (!this.input)
      return;
    const previousValue = this.input.value;
    if (event.target.name === "plus") {
      if (this.min > parseInt(this.input.step) && parseInt(this.input.value) <= this.min) {
        this.input.value = this.min.toString();
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }
    if (previousValue !== this.input.value) {
      this.input.dispatchEvent(this.changeEvent);
    }
    if (this.min === parseInt(previousValue) && event.target.name === "minus") {
      this.input.value = this.min.toString();
    }
  }
  updateQuantityButtonsState() {
    const value = this.input?.value;
    const min = this.input?.dataset.min;
    const max = this.input?.dataset.max;
    if (value) {
      if (min) {
        let valAtMin = parseInt(value) <= parseInt(min);
        this.buttonDecrease?.classList.toggle("opacity-50", valAtMin);
        this.buttonDecrease?.classList.toggle("pointer-events-none", valAtMin);
      }
      if (max) {
        let valAtMax = parseInt(value) >= parseInt(max);
        this.buttonIncrease?.classList.toggle("opacity-50", valAtMax);
        this.buttonIncrease?.classList.toggle("pointer-events-none", valAtMax);
      }
    }
  }
};
customElements.define("quantity-input-regimen-group", QuantityInputRegimenGroup);

// src/js/customElements/quick-add.js
var DATA_IDENTIFIER = "[data-quick-add]";
var LABELS = {
  add: "Add [fallback copy]",
  remove: "Remove [fallback copy]",
  max: "Max [fallback copy]"
};
var QuickAdd = class extends HTMLElement {
  constructor() {
    super();
    this.productFormId = this.dataset["productFormId"];
    this.sellingPlanId = this.dataset["defaultSellingPlanId"] ? parseInt(this.dataset["defaultSellingPlanId"]) : PRODUCT_SELLING_PLAN_DEFAULT_ID;
    this.variantId = parseInt(this.dataset["quickAddVariantId"] ?? "0");
    this.extraVariantIds = (this.dataset["quickAddExtraVariantIds"] ?? "").split(",").map((id) => parseInt(id.trim(), 10)).filter((id) => Number.isInteger(id) && id > 0 && id !== this.variantId);
    this.redirectToCheckout = this.dataset["quickAddRedirectToCheckout"] === "true";
    this.addQuantity = parseInt(this.dataset["quickAddQuantity"] ?? "1");
    this.maxQuantity = parseInt(
      this.dataset["quickAddMaxQuantity"] ?? `${CART_LINE_ITEM_QUANTITY_MAX_DEFAULT}`
    );
    this.quantityInCart = 0;
    this.size = this.dataset["quickAddSize"];
    this.groupSelector = this.dataset["groupSelector"];
    this.itemCartKey = this.dataset["quickAddItemCartKey"];
    this.itemInCartClass = this.dataset["quickAddItemInCartClass"] ?? "item-in-cart";
    this.labelAdd = this.dataset["quickAddLabelAdd"] ?? LABELS.add;
    this.labelAddDesktop = this.dataset["quickAddLabelAddDesktop"] ?? this.labelAdd;
    this.labelAddMobile = this.dataset["quickAddLabelAddMobile"] ?? this.labelAdd;
    this.labelRemove = this.dataset["quickAddLabelRemove"] ?? LABELS.remove;
    this.labelMax = this.dataset["quickAddLabelMax"] ?? LABELS.max;
    this.isFreeSample = this.hasAttribute("data-quick-add-free-sample");
    this.hideCartControls = this.hasAttribute(
      "data-quick-add-hide-cart-controls"
    );
    this.cartMessage = this.dataset["quickAddCartMessage"] ?? null;
    this.freeSampleMax = parseInt(this.dataset["quickAddFreeSampleMax"] ?? "0");
    this.freeSampleCartCount = parseInt(
      this.dataset["quickAddFreeSampleCartCount"] ?? "0"
    );
    this.variantPrice = null;
    this.onVariantChange = this.onVariantChange.bind(this);
    this.button = this.querySelector("button");
    this.buttonLabel = this.button?.querySelector(
      "[data-quick-add-button-label]"
    );
    this.buttonAction = "add";
    this.buttonAction = this.addItem;
    this.cartUpdateUnsubscriber = null;
    this.quantityChangeUnsubscriber = null;
  }
  async connectedCallback() {
    this.quantityInCart = await cartDataFncs.getVariantQuantitySellingPlanInCart(
      this.variantId,
      this.sellingPlanId
    );
    this.updateElements();
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartMutationComplete,
      async (payload) => {
        await this.onCartUpdate(payload.cartData);
      },
      "quickAdd"
    );
    if (this.productFormId) {
      this.quantityChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.productQuantityChange,
        ({ productFormId, quantity }) => {
          this.onQuantityChange(productFormId, quantity);
        },
        "quickAdd"
      );
      this.quantityChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.productSellingPlanChange,
        async ({ productFormId, sellingPlanId }) => {
          this.onSellingPlanChange(productFormId, sellingPlanId);
        },
        "quickAdd"
      );
      document.addEventListener("murad:variant:change", this.onVariantChange);
    }
  }
  disconnectedCallback() {
    this.removeEventListeners();
    document.removeEventListener("murad:variant:change", this.onVariantChange);
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this.quantityChangeUnsubscriber) {
      this.quantityChangeUnsubscriber();
    }
  }
  async onVariantChange({ detail }) {
    const variantId = parseInt(detail?.variantId ?? "0", 10);
    if (!this.productFormId || !Number.isInteger(variantId) || variantId <= 0) {
      return;
    }
    this.variantId = variantId;
    this.dataset.quickAddVariantId = `${variantId}`;
    this.extraVariantIds = (this.dataset["quickAddExtraVariantIds"] ?? "").split(",").map((id) => parseInt(id.trim(), 10)).filter((id) => Number.isInteger(id) && id > 0 && id !== this.variantId);
    this.quantityInCart = await cartDataFncs.getVariantQuantitySellingPlanInCart(
      this.variantId,
      this.sellingPlanId
    );
    this.variantPrice = detail?.price ?? this.variantPrice;
    await this.updateElements();
  }
  updateVariantPrice(price) {
    if (!price)
      return;
    this.querySelectorAll("[data-price-update-el]").forEach((el) => {
      if (!el.querySelector("promo-price"))
        el.innerHTML = price;
    });
  }
  onQuantityChange(productFormId, quantity) {
    cartDebug(
      "QuickAdd onQuantityChange",
      productFormId,
      this.productFormId,
      quantity
    );
    if (this.productFormId && this.productFormId === productFormId) {
      this.addQuantity = quantity;
      this.updateElements();
    }
  }
  showPriceForSellingPlan(sellingPlanId) {
    if (sellingPlanId === PRODUCT_SELLING_PLAN_DEFAULT_ID) {
      this.querySelectorAll(".product_atc_label_price_subscription").forEach(
        (el) => {
          el.classList.add("hidden");
        }
      );
      this.querySelectorAll(".product_atc_label_price_onetime").forEach(
        (el) => {
          el.classList.remove("hidden");
        }
      );
    } else {
      this.querySelectorAll(".product_atc_label_price_subscription").forEach(
        (el) => {
          el.classList.remove("hidden");
        }
      );
      this.querySelectorAll(".product_atc_label_price_onetime").forEach(
        (el) => {
          el.classList.add("hidden");
        }
      );
    }
  }
  async onSellingPlanChange(productFormId, sellingPlanId) {
    if (this.productFormId && this.productFormId === productFormId) {
      cartDebug(
        "QuickAdd onSellingPlanChange",
        productFormId,
        this.productFormId,
        sellingPlanId
      );
      this.sellingPlanId = sellingPlanId;
      this.showPriceForSellingPlan(sellingPlanId);
      this.quantityInCart = await cartDataFncs.getVariantQuantitySellingPlanInCart(
        this.variantId,
        this.sellingPlanId
      );
      this.updateElements();
    }
  }
  getAddLabelMarkup() {
    if (this.labelAddDesktop === this.labelAddMobile) {
      return this.labelAddDesktop;
    }
    return `<span class="hidden lg:inline">${this.labelAddDesktop}</span><span class="lg:hidden">${this.labelAddMobile}</span>`;
  }
  updateElements = async () => {
    if (this.button && this.buttonLabel) {
      let buttonAction = this.addItem;
      if (this.isFreeSample) {
        this.buttonLabel.innerHTML = this.labelAdd;
        buttonAction = this.addItem;
        this.button.disabled = false;
        this.button.classList.remove(this.itemInCartClass);
        if (this.freeSampleCartCount > 0) {
          if (this.itemCartKey) {
            this.buttonLabel.innerHTML = this.labelRemove;
            buttonAction = this.removeItem;
            this.button.classList.add(this.itemInCartClass);
          } else if (this.freeSampleCartCount >= this.freeSampleMax) {
            this.button.disabled = true;
          }
        }
      } else {
        if (this.quantityInCart >= this.maxQuantity) {
          this.buttonLabel.innerHTML = this.labelMax;
          this.button.disabled = true;
        } else {
          this.buttonLabel.innerHTML = this.getAddLabelMarkup();
          this.button.disabled = false;
          this.showPriceForSellingPlan(this.sellingPlanId);
        }
      }
      this.addEventListeners(buttonAction);
      this.updateVariantPrice(this.variantPrice);
    }
  };
  disableSiblingElements = () => {
    const siblingEls = document.querySelectorAll(
      `${this.groupSelector} ${DATA_IDENTIFIER} button`
    );
    siblingEls.forEach((el) => {
      if (el !== this) {
        el.classList.add("pointer-events-none");
        el.disabled = true;
      }
    });
  };
  showLoadingUI() {
    if (this.isFreeSample) {
      this.disableSiblingElements();
    }
    if (this.button) {
      this.button.classList.add("pointer-events-none");
      this.button.style.opacity = "0.8";
      this.button.disabled = true;
    }
    const loadingSpinner = this.querySelector(".loading__spinner");
    loadingSpinner?.classList.remove("hidden");
  }
  hideLoadingUI() {
    if (this.button) {
      this.button.classList.remove("pointer-events-none");
      this.button.style.opacity = "";
      this.button.disabled = false;
    }
    const loadingSpinner = this.querySelector(".loading__spinner");
    loadingSpinner?.classList.add("hidden");
  }
  countFreeSamples(items) {
    return items.filter((item) => {
      return item.properties[PRODUCT_PROPERTY_KEYS.freeSample];
    }).length;
  }
  isThisItemInCart(items) {
    return items.some((item) => {
      return item.variant_id === this.variantId;
    });
  }
  getItemCartKey = (items) => {
    let item = items.find((item2) => {
      return item2.variant_id === this.variantId;
    });
    return item ? item.key : null;
  };
  getItemCartIndex = (items) => {
    return items.findIndex((item) => {
      return item.variant_id === this.variantId;
    });
  };
  onCartUpdate = async (cartData) => {
    this.quantityInCart = await cartDataFncs.getVariantQuantitySellingPlanInCart(
      this.variantId,
      this.sellingPlanId,
      cartData
    );
    this.freeSampleCartCount = this.countFreeSamples(cartData.cartRaw.items);
    this.itemCartKey = this.getItemCartKey(cartData.cartRaw.items);
    this.hideLoadingUI();
    this.updateElements();
  };
  addEventListeners(buttonAction) {
    this.removeEventListeners();
    this.buttonAction = buttonAction;
    this.button?.addEventListener("click", this.buttonAction);
  }
  removeEventListeners() {
    this.button?.removeEventListener("click", this.buttonAction);
  }
  removeItem = (event) => {
    event.preventDefault();
    this.showLoadingUI();
    cartChange({
      id: this.itemCartKey,
      quantity: 0
    });
  };
  addItem = (event) => {
    event.preventDefault();
    this.showLoadingUI();
    const variantArray = [
      {
        id: this.variantId,
        quantity: this.addQuantity,
        selling_plan: this.sellingPlanId,
        // https://shopify.dev/docs/api/ajax/reference/cart#private-properties-and-attributes
        properties: {
          ...this.isFreeSample ? { [PRODUCT_PROPERTY_KEYS.freeSample]: true } : {},
          ...this.hideCartControls ? { _cart_hide_controls: true } : {},
          ...this.cartMessage ? { [PRODUCT_PROPERTY_KEYS.cartMessage]: this.cartMessage } : {},
          [PRODUCT_PROPERTY_KEYS.size]: this.size,
          [PRODUCT_PROPERTY_KEYS.isSubscription]: this.sellingPlanId ? true : false
        }
      },
      ...this.extraVariantIds.map((id) => ({
        id,
        quantity: 1,
        selling_plan: null,
        properties: {
          [PRODUCT_PROPERTY_KEYS.isSubscription]: false,
          [PRODUCT_PROPERTY_KEYS.cartMessage]: "Free Gift with Purchase",
          [PRODUCT_PROPERTY_KEYS.parentVariantId]: String(this.variantId),
          ...this.hideCartControls ? { _cart_hide_controls: true } : {}
        }
      }))
    ];
    publish(PUB_SUB_EVENTS.cartAddRequest, {
      variantArray,
      openDrawer: !this.redirectToCheckout,
      redirectToCheckout: this.redirectToCheckout,
      redirectUrl: `${window.Shopify.routes.root}checkout`
    });
  };
};
customElements.define("quick-add", QuickAdd);

// src/js/customElements/upsell-modal-trigger.js
var UpsellModalTrigger = class extends HTMLElement {
  constructor() {
    super();
    this.productFormId = this.dataset["productFormId"];
  }
  connectedCallback() {
    this.unsubscribe = subscribe(
      PUB_SUB_EVENTS.productSellingPlanChange,
      ({ productFormId, sellingPlanId }) => {
        if (!this.productFormId || productFormId !== this.productFormId) {
          return;
        }
        const isSubscription = !!sellingPlanId;
        this.classList.toggle("hidden", isSubscription);
        this.closest("[data-cta-container]")?.querySelector("[data-upsell-modal-quick-add-wrapper]")?.classList.toggle("hidden", !isSubscription);
      },
      "upsellModalTrigger"
    );
  }
  disconnectedCallback() {
    this.unsubscribe?.();
  }
};
customElements.define("upsell-modal-trigger", UpsellModalTrigger);

// src/js/customElements/cart-free-samples.js
var CartFreeSamples = class extends HTMLElement {
  constructor() {
    super();
    this.cartItemCount = parseInt(this.dataset["cartItemCount"] ?? "0");
    this.cartOriginalTotalPrice = parseInt(
      this.dataset["cartOriginalTotalPrice"] ?? "0"
    );
  }
  async connectedCallback() {
    this.updateElements(this.cartItemCount, this.cartOriginalTotalPrice);
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartMutationComplete,
      (payload) => {
        this.onCartUpdate(payload.cartData);
      },
      "quickAdd"
    );
  }
  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }
  onCartUpdate = (cartData) => {
    const { cartRaw } = cartData;
    const { item_count, original_total_price } = cartRaw;
    this.updateElements(item_count, original_total_price);
  };
  updateElements = async (cartItemCount, cartOriginalTotalPrice) => {
    if (cartItemCount > 0 && cartOriginalTotalPrice > 0) {
      this.classList.remove("hidden");
    } else {
      this.classList.add("hidden");
    }
  };
};
customElements.define("cart-free-samples", CartFreeSamples);

// src/js/customElements/visibility-detector.mjs
var DYNAMIC_YIELD_INTERSECTION_EVENT_NAME = "sectionIsVisible";
var VisibilityDetector = class extends HTMLElement {
  constructor() {
    super();
    this.dyEventName = DYNAMIC_YIELD_INTERSECTION_EVENT_NAME;
    this.visibilityDetectorId = this.dataset["visibilityDetectorId"] ?? null;
    this.triggeringSectionName = this.dataset["triggeringSectionName"] ?? null;
    this.triggeringSectionId = this.dataset["triggeringSectionId"] ?? null;
    this.doFireDyEvent = this.dataset["fireDyVisibilityEvent"] ? true : false;
    this.intersectionObsMinRatio = parseInt(
      this.dataset["intersectionObsMinRatio"] ?? "0"
    );
    this.intersectionObsTopMargin = parseInt(
      this.dataset["intersectionObsTopMargin"] ?? "0"
    );
    this.intersectionObserver = null;
  }
  fireDYEvent({ eventName = "", eventProps = {} }) {
    var eventPayload = {
      name: eventName,
      properties: eventProps
    };
    try {
      window.DY?.API("event", eventPayload);
    } catch (err) {
      console.error(err);
    }
  }
  fireDYEventOnIntersection() {
    const eventProps = {
      visibilityDetectorId: this.visibilityDetectorId,
      sectionName: this.triggeringSectionName,
      sectionId: this.triggeringSectionId
    };
    this.fireDYEvent({
      eventName: this.dyEventName,
      eventProps
    });
  }
  injectDummyElement() {
    let dummyEl = document.createElement("div");
    dummyEl.style.width = "1px";
    dummyEl.style.height = "1px";
    dummyEl.style.position = "absolute";
    dummyEl.style.top = "0";
    dummyEl.style.left = "0";
    dummyEl.style.pointerEvents = "none";
    dummyEl.style.opacity = "0.01";
    dummyEl.id = `${this.dyEventName}_${this.triggeringSectionName}`;
    this.visibilityDetectorId && dummyEl.setAttribute(
      "data-visibility-detector-id",
      this.visibilityDetectorId
    );
    this.triggeringSectionName && dummyEl.setAttribute(
      "data-triggering-section-name",
      this.triggeringSectionName
    );
    this.triggeringSectionId && dummyEl.setAttribute(
      "data-triggering-section-id",
      this.triggeringSectionId
    );
    this.appendChild(dummyEl);
  }
  monitorIntersection({
    targetNode = this,
    callbackIntersectionChange = (...args) => {
    },
    threshold = this.intersectionObsMinRatio,
    root = null,
    rootMargin = `0px 0px ${this.intersectionObsTopMargin}px 0px`
  }) {
    let options = {
      root,
      rootMargin,
      threshold
    };
    let intersectionCallback = (entries, observer) => {
      entries.forEach((entry) => {
        let { isIntersecting, intersectionRatio } = entry;
        callbackIntersectionChange({ isIntersecting, intersectionRatio });
        if (entry.isIntersecting) {
          observer.disconnect();
        }
      });
    };
    this.intersectionObserver = new IntersectionObserver(
      intersectionCallback,
      options
    );
    this.intersectionObserver?.observe(targetNode);
  }
  connectedCallback() {
    this.monitorIntersection({
      targetNode: this,
      callbackIntersectionChange: ({ isIntersecting, intersectionRatio }) => {
        if (isIntersecting) {
          this.injectDummyElement();
          if (this.doFireDyEvent) {
            this.fireDYEventOnIntersection();
          }
        }
      }
    });
  }
  disconnectedCallback() {
    this.intersectionObserver?.disconnect();
  }
};
customElements.define("visibility-detector", VisibilityDetector);

// src/js/customElements/yotpo-star-widget.js
var LOCAL_STORAGE_KEY_YOTPO_STAR_WIDGET_ANSWERED_QUESTIONS = "muradYotpoStarWidgetAnsweredQuestions";
var YOTPO_STAR_WIDGET_ANSWERED_QUESTIONS_STALE_AFTER_MS = 1e3 * 60 * 60;
var DEBOUNCE_MS_YOTPO_QA_DATA = 300;
function getCachedAnsweredQuestions(productId) {
  const localStorageCachedVal = getLocalStorage(
    LOCAL_STORAGE_KEY_YOTPO_STAR_WIDGET_ANSWERED_QUESTIONS
  );
  if (!localStorageCachedVal) {
    return null;
  }
  const answeredQuestionsThisProduct = localStorageCachedVal[productId];
  if (answeredQuestionsThisProduct) {
    const dataIsStale = hasTimeElapsed(
      answeredQuestionsThisProduct.lastUpdated,
      YOTPO_STAR_WIDGET_ANSWERED_QUESTIONS_STALE_AFTER_MS
    );
    if (!dataIsStale) {
      return answeredQuestionsThisProduct.answeredQuestionCount;
    }
  }
  return null;
}
function setCachedAnsweredQuestions(productId, answeredQuestions = 0) {
  const localStorageCachedVal = getLocalStorage(
    LOCAL_STORAGE_KEY_YOTPO_STAR_WIDGET_ANSWERED_QUESTIONS
  );
  setLocalStorage(LOCAL_STORAGE_KEY_YOTPO_STAR_WIDGET_ANSWERED_QUESTIONS, {
    ...localStorageCachedVal,
    [productId]: {
      answeredQuestionCount: answeredQuestions,
      lastUpdated: (/* @__PURE__ */ new Date()).toISOString()
    }
  });
}
async function getYotpoQAData(yotpoAppKey, productId) {
  try {
    const cachedAnsweredQuestionsThisProduct = getCachedAnsweredQuestions(productId);
    if (cachedAnsweredQuestionsThisProduct) {
      publish(PUB_SUB_EVENTS.yotpoQADataComplete, {
        answeredQuestionCount: cachedAnsweredQuestionsThisProduct
      });
      return;
    }
    if (!yotpoAppKey || !productId) {
      throw new Error("yotpo-star-widget: missing Yotpo App Key or Product ID");
    }
    const res = await fetch(
      `https://api.yotpo.com/products/${yotpoAppKey}/${productId}/qna_bottomline`
    );
    const json = await res.json();
    const answeredQuestionCount = json.response.total_answers;
    setCachedAnsweredQuestions(productId, answeredQuestionCount);
    publish(PUB_SUB_EVENTS.yotpoQADataComplete, { answeredQuestionCount });
  } catch (err) {
    console.error("getYotpoQAData", err);
  }
}
var debounceGetYotpoQAData = debounce(({ yotpoAppKey, productId }) => {
  getYotpoQAData(yotpoAppKey, productId);
}, DEBOUNCE_MS_YOTPO_QA_DATA);
subscribe(
  PUB_SUB_EVENTS.yotpoQADataRequest,
  debounceGetYotpoQAData,
  "yotpo-star-widget"
);
var YotpoStarWidget = class extends HTMLElement {
  constructor() {
    super();
    this.yotpoAppKey = this.dataset["yotpoAppKey"] ?? "";
    this.productId = this.dataset["productId"] ?? "";
    this.answeredQuestionsLabel = this.dataset["answeredQuestionsLabel"] ?? "";
    this.metafieldStarRating = parseInt(
      this.dataset["metafieldStarRating"] ?? "0"
    );
    this.hideYotpo = this.dataset["hideYotpo"] ? true : false;
    this.widgetObserver = null;
    this.reviewsQuestionsWasHandled = false;
    this.summaryWasHandled = false;
    this.unsubscriberReviewSummary = null;
    this.unsubscriberQAData = null;
  }
  doShowWidget(totalAnsweredQuestions = 0) {
    const hasQuestions = totalAnsweredQuestions > 0;
    const hasReviews = this.metafieldStarRating > 0;
    const hideYotpo = this.hideYotpo;
    if ((hasQuestions || hasReviews) && !hideYotpo) {
      return true;
    }
    return false;
  }
  createOnClickHandler(yotpoReviewsQATabIndex = 0) {
    return (e) => {
      const yotpoSection = document.querySelector(
        ".yotpo-widget-instance .yotpo-reviews-main-widget"
      );
      if (yotpoSection) {
        window.scrollTo({
          behavior: "smooth",
          top: yotpoSection.getBoundingClientRect().top + window.scrollY - 135
        });
        document.querySelectorAll(".ugc-storefront-widgets-tabs-container__tab")?.[yotpoReviewsQATabIndex]?.click();
      }
    };
  }
  modifyReviewsButton(reviewsButtonEl) {
    reviewsButtonEl?.addEventListener("click", this.createOnClickHandler(0));
  }
  addQAButton(totalAnsweredQuestions = 0) {
    const qaInsert = this.querySelector("[data-qa-insert]");
    if (!qaInsert || totalAnsweredQuestions === 0) {
      return;
    }
    const qaButton = qaInsert.querySelector("button");
    if (qaButton) {
      qaButton.addEventListener("click", this.createOnClickHandler(1));
      qaButton.textContent = `${totalAnsweredQuestions} ${this.answeredQuestionsLabel}`;
      qaInsert.classList.remove("hidden");
      qaInsert.classList.add("flex");
    }
  }
  cloneReviewsSummary() {
    const otherReviewsSummary = document.querySelector(
      ".yotpo-star-rating-reviews-summary-link"
    );
    if (otherReviewsSummary) {
      const clonedReviewsSummary = otherReviewsSummary.cloneNode(true);
      const otherReviewsSummaryClickable = otherReviewsSummary.querySelector(
        ".yotpo-star-rating-reviews-summary-text"
      );
      clonedReviewsSummary.addEventListener("click", (evt) => {
        evt.preventDefault();
        otherReviewsSummaryClickable?.click();
      });
      const reviewsSummaryParent = this.querySelector(
        ".yotpo-reviews-star-ratings-widget > div"
      );
      reviewsSummaryParent?.appendChild(clonedReviewsSummary);
      return true;
    }
    return false;
  }
  doesHaveReviewSummary() {
    const thisReviewsSummary = this.querySelector(
      "div.yotpo-star-rating-reviews-summary-link"
    );
    if (thisReviewsSummary) {
      return true;
    }
    return false;
  }
  prepReviewsSummary() {
    if (this.doesHaveReviewSummary()) {
      return true;
    } else {
      return this.cloneReviewsSummary();
    }
  }
  async mutationCallback(mutationRecordArray, observer) {
    const yotpoStarWidgetInjectedBlock = this.querySelector(
      "#yotpo-reviews-star-ratings-widget"
    );
    if (!yotpoStarWidgetInjectedBlock) {
      return;
    }
    if (!this.reviewsQuestionsWasHandled) {
      this.reviewsQuestionsWasHandled = true;
      publish(PUB_SUB_EVENTS.yotpoQADataRequest, {
        yotpoAppKey: this.yotpoAppKey,
        productId: this.productId
      });
    }
    if (!this.summaryWasHandled) {
      const hasReviewSummary = this.doesHaveReviewSummary();
      if (hasReviewSummary) {
        this.summaryWasHandled = hasReviewSummary;
        publish(PUB_SUB_EVENTS.yotpoReviewSummaryLoaded, { node: this });
        observer.disconnect();
      }
    }
  }
  watchForYotpoWidget() {
    const observerTargetNode = this;
    const observerOptions = {
      subtree: true,
      childList: true
    };
    this.widgetObserver = new MutationObserver(
      // note: arrow fnc required to preserve "this"
      (mutationRecordArray, observer) => this.mutationCallback(mutationRecordArray, observer)
    );
    this.widgetObserver.observe(observerTargetNode, observerOptions);
  }
  // overrideStyles() {
  //   this.querySelectorAll("svg").forEach((el) => {
  //     el.setAttribute("height", "10px");
  //     el.setAttribute("width", "10px");
  //     el.style.marginTop = "-1px";
  //     const path = el.querySelector("path");
  //     if (path) {
  //       path.style.fill = "var(--color-text-primary)";
  //       path.style.stroke = "var(--color-text-primary)";
  //     }
  //   });
  //   this.querySelectorAll("*").forEach((el) => {
  //     el.style.fontSize = "10px";
  //     el.style.lineHeight = "10px";
  //   });
  //   const bottomLineSummary = this.querySelector(
  //     ".yotpo-sr-bottom-line-summary",
  //   );
  //   if (bottomLineSummary && bottomLineSummary.firstElementChild) {
  //     bottomLineSummary.firstElementChild.style.marginRight = "5px";
  //   }
  // }
  onYotpoReviewSummaryLoaded(node) {
    this.prepReviewsSummary();
  }
  onYotpoQADataComplete(answeredQuestionCount) {
    if (this.doShowWidget(answeredQuestionCount)) {
      this.modifyReviewsButton(
        this.querySelector("button.yotpo-sr-bottom-line-button")
      );
      this.addQAButton(answeredQuestionCount);
      this.classList.remove("hidden");
      this.classList.add("flex");
    }
  }
  connectedCallback() {
    this.unsubscriberReviewSummary = subscribe(
      PUB_SUB_EVENTS.yotpoReviewSummaryLoaded,
      ({ node }) => {
        this.onYotpoReviewSummaryLoaded(node);
      },
      "yotpo-star-widget"
    );
    this.unsubscriberQAData = subscribe(
      PUB_SUB_EVENTS.yotpoQADataComplete,
      ({ answeredQuestionCount }) => {
        this.onYotpoQADataComplete(answeredQuestionCount);
      },
      "yotpo-star-widget"
    );
    this.watchForYotpoWidget();
  }
  disconnectedCallback() {
    if (this.unsubscriberReviewSummary) {
      this.unsubscriberReviewSummary();
    }
    if (this.unsubscriberQAData) {
      this.unsubscriberQAData();
    }
    if (this.widgetObserver) {
      this.widgetObserver.disconnect();
    }
  }
};
customElements.define("yotpo-star-widget", YotpoStarWidget);

// src/js/helpers/bundledProductDisclaimer.js
function updateBundledProductDisclaimers(root, variantIds = []) {
  const displayedIds = new Set(variantIds.map(String));
  root.querySelectorAll("[data-bundled-product-disclaimer]").forEach((el) => {
    const disclaimerIds = (el.dataset.variantIds || "").split(",").filter(Boolean);
    const isVisible = disclaimerIds.some((id) => displayedIds.has(id));
    el.classList.toggle("hidden", !isVisible);
  });
}

// src/js/customElements/complementary-regimen-constants.js
var API_URL_PATH_COMPLEMENTARY_REGIMEN = `/api/recommendations/completeRegimen`;
var LOCAL_STORAGE_KEY_REGIMEN_MAP = "muradComplementaryRegimen_regimenMap";
var LOCAL_STORAGE_KEY_PRODUCT_MAP = "muradComplementaryRegimen_productMap";
var REGIMEN_DATA_STALE_AFTER = 1e3 * 60 * 60 * 1;
var DEFAULT_CATEGORIES = [
  "cleanser",
  "treatment",
  "moisturizer",
  "exfoliantPeel"
];
var CATEGORY_LABELS = {
  cleanser: "Cleanse",
  treatment: "Treat",
  eyeTreatment: "Treat",
  moisturizer: "Hydrate",
  exfoliantPeel: "Nourish"
};
var CATEGORY_DESCRIPTIONS = {
  cleanser: "Remove buildup and prep skin to absorb what comes next",
  treatment: "Actives that work where skin needs them most",
  eyeTreatment: "Actives that work where skin needs them most",
  moisturizer: "Lock in moisture, strengthen, and support renewal",
  exfoliantPeel: "Targeted rituals that elevate your everyday routine"
};
var LABELS2 = {
  addComplete: "Add Complete Regimen to Bag *",
  addSome: "Add Regimen to Bag *",
  addOne: "Add to Bag *"
};
var ELEMENTS_TO_HIDE_ON_INTERSECTION = [
  "[data-sticky-cta-mobile]",
  "#fw-player",
  "#reamazejs-container",
  "#attentive_overlay"
];

// src/js/customElements/complementary-regimen-data.js
function getCachedRegimenData(targetSku, categories) {
  const cachedRegimen = getLocalStorage(LOCAL_STORAGE_KEY_REGIMEN_MAP);
  if (cachedRegimen) {
    const categoriesSortedString = [...categories].sort().join(",");
    const targetRegimen = cachedRegimen[targetSku]?.[categoriesSortedString];
    if (targetRegimen) {
      const { regimenSkus, discounts, lastModified } = targetRegimen;
      const regimenDataIsStale = hasTimeElapsed(
        lastModified,
        REGIMEN_DATA_STALE_AFTER
      );
      if (!regimenDataIsStale) {
        return { regimenSkus, discounts: discounts || [] };
      }
    }
  }
  return null;
}
function getCachedRegimenProducts(regimenSkus = []) {
  const cachedProductList = getLocalStorage(LOCAL_STORAGE_KEY_PRODUCT_MAP);
  if (regimenSkus && cachedProductList) {
    const productList = regimenSkus.map((sku) => cachedProductList[sku]);
    if (productList.every((product) => product !== void 0)) {
      return productList;
    }
  }
  return null;
}
function cacheRegimen(targetSku = "", categories = DEFAULT_CATEGORIES, regimenSkus = [], discounts = []) {
  const lastModified = (/* @__PURE__ */ new Date()).toISOString();
  const cacheEntry = { regimenSkus, discounts, lastModified };
  const cachedRegimen = getLocalStorage(LOCAL_STORAGE_KEY_REGIMEN_MAP) || {};
  if (!cachedRegimen[targetSku]) {
    cachedRegimen[targetSku] = {};
  }
  const categoriesSortedString = [...categories].sort().join(",");
  cachedRegimen[targetSku][categoriesSortedString] = cacheEntry;
  setLocalStorage(LOCAL_STORAGE_KEY_REGIMEN_MAP, cachedRegimen);
}
function cacheProductData(productList = []) {
  const cachedProductList = getLocalStorage(LOCAL_STORAGE_KEY_PRODUCT_MAP) || {};
  productList.forEach((product) => {
    cachedProductList[product.sku] = product;
  });
  setLocalStorage(LOCAL_STORAGE_KEY_PRODUCT_MAP, cachedProductList);
}
async function fetchComplementaryRegimen(skinQuizDomain, sku = "", categories = DEFAULT_CATEGORIES) {
  if (!skinQuizDomain) {
    throw new Error("No skin quiz domain provided");
  }
  const payload = {
    sku,
    categories
  };
  const options = {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payload)
  };
  try {
    const res = await fetch(
      `${skinQuizDomain}${API_URL_PATH_COMPLEMENTARY_REGIMEN}`,
      options
    );
    const json = await res.json();
    if (res.ok) {
      return json;
    } else if (json.message) {
      throw new Error(json.message);
    } else {
      throw new Error(
        `Error retrieving recommendations: ${res.status} - ${res.statusText}`
      );
    }
  } catch (err) {
    throw err;
  }
}
function centsToDollars(value) {
  if (value == null || value === "") {
    return null;
  }
  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return null;
  }
  return amount / 100;
}
async function fetchVariantPricingByIds(variantIds = []) {
  const uniqueVariantIds = [...new Set(variantIds)].map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
  if (uniqueVariantIds.length === 0) {
    return {};
  }
  const requests = uniqueVariantIds.map(async (variantId) => {
    const res = await fetch(`/variants/${variantId}.js`);
    if (!res.ok) {
      throw new Error(`Failed variant lookup for ${variantId}: ${res.status}`);
    }
    const variant = await res.json();
    const price = centsToDollars(variant?.price);
    if (price == null) {
      return null;
    }
    return {
      variantId,
      pricing: {
        price,
        compareAtPrice: centsToDollars(variant?.compare_at_price),
        // additive field, used by quiz-recommendations.js to re-validate stock for
        // known-user regimen items whose data may be stale (captured at quiz time);
        // existing callers of this function only read .price/.compareAtPrice;
        available: variant?.available ?? true
      }
    };
  });
  const settled = await Promise.allSettled(requests);
  return settled.reduce((prev, result) => {
    if (result.status === "fulfilled" && result.value) {
      return {
        ...prev,
        [result.value.variantId]: result.value.pricing
      };
    }
    return prev;
  }, {});
}

// src/js/customElements/complementary-regimen-pricing.js
function computeApplicableDiscountForCount(discounts, count) {
  if (!discounts || discounts.length === 0)
    return null;
  return [...discounts].sort((a, b) => b.minQuantity - a.minQuantity).find((d) => count >= d.minQuantity) || null;
}
function computeApplicableDiscount(regimen, discounts) {
  if (!discounts || discounts.length === 0)
    return null;
  const uniqueInCart = regimen.filter((p) => p.quantityInCart > 0).length;
  return [...discounts].sort((a, b) => b.minQuantity - a.minQuantity).find((d) => uniqueInCart >= d.minQuantity) || null;
}
function formatPrice2(subtotal, discount) {
  if (discount) {
    const discounted = subtotal * (1 - discount.percentage / 100);
    const savings = subtotal - discounted;
    return {
      original: `$${subtotal.toFixed(2)}`,
      discounted: `$${discounted.toFixed(2)}`,
      savings: `Save $${savings.toFixed(2)} (${discount.percentage}% off)`
    };
  } else {
    return {
      value: `$${subtotal.toFixed(2)}`
    };
  }
}
function calculateSelectedProductsPrice(selectedProducts) {
  return selectedProducts.reduce((sum, product) => {
    return sum + (product.price || 0);
  }, 0);
}
function calculateInCartPrice(regimen) {
  return regimen.reduce((sum, product) => {
    return product.quantityInCart > 0 ? sum + (product.price || 0) : sum;
  }, 0);
}
function extractCompleteRegimenValue(discounts) {
  const firstCode = discounts?.[0]?.code ?? "";
  if (!firstCode.startsWith(COMPLETE_REGIMEN_DISCOUNT_CODE_PREFIX)) {
    return "complete_regimen";
  }
  const codeParts = firstCode.split("_");
  return codeParts.length >= 3 ? `${codeParts[1]}_${codeParts[2]}` : "complete_regimen";
}

// src/js/customElements/complementary-regimen-cart.js
function getSelectedProducts(checkboxList) {
  const selectedProducts = [];
  checkboxList.forEach((checkbox) => {
    const cb = (
      /** @type {HTMLInputElement} */
      checkbox
    );
    if (cb.checked && cb.dataset.variantId) {
      selectedProducts.push({
        id: parseInt(cb.dataset.variantId),
        size: cb.dataset.size,
        price: parseFloat(cb.dataset.price)
      });
    }
  });
  return selectedProducts;
}
function createAddProductPayload(variantArray, complementaryRegimenObj, targetSku, concernLabel, concernColor, discounts) {
  const completeRegimenValue = extractCompleteRegimenValue(discounts);
  return variantArray.map((variant) => {
    const { id, size } = variant;
    const thisProduct = complementaryRegimenObj[id];
    const { name, category } = thisProduct || {};
    const CATEGORY_LABELS2 = {
      cleanser: "Cleanse",
      treatment: "Treat",
      eyeTreatment: "Treat",
      moisturizer: "Hydrate",
      exfoliantPeel: "Nourish"
    };
    return {
      id,
      quantity: 1,
      selling_plan: null,
      properties: {
        [PRODUCT_PROPERTY_KEYS.size]: size,
        // [PRODUCT_PROPERTY_KEYS.cartMessage]: `[*Your Complete Regimen:*] [**${CATEGORY_LABELS[category] ?? category}**]`,
        // [PRODUCT_PROPERTY_KEYS.cartMessageColor]: `${concernColor}`,
        [PRODUCT_PROPERTY_KEYS.completeRegimen]: completeRegimenValue,
        [PRODUCT_PROPERTY_KEYS.skinConcernLabel]: `${concernLabel}`,
        [PRODUCT_PROPERTY_KEYS.skinConcernColor]: `${concernColor}`,
        [PRODUCT_PROPERTY_KEYS.productCategory]: `${CATEGORY_LABELS2[category] ?? category}`
      }
    };
  });
}
function addItemsToCart(variantArray) {
  cartDebugHttp("[CR][Shopify HTTP] cartAddItems request", {
    itemCount: Array.isArray(variantArray) ? variantArray.length : 0,
    variantIds: Array.isArray(variantArray) ? variantArray.map((item) => item.id) : []
  });
  cartAddItems(variantArray);
  cartDebugHttp("[CR][Shopify HTTP] cartAddItems dispatched");
}

// src/js/customElements/complementary-regimen-ui.js
function updateAddToCartButtonState(addToCartButton, selectedProducts, complementaryRegimen, selectItemsLabel, targetSku, targetSkuAlwaysAdd) {
  if (!addToCartButton)
    return;
  const completeRegimenIsInCart = complementaryRegimen.every(
    (product) => product.quantityInCart > 0
  );
  let nonTargetSelected = selectedProducts;
  if (targetSkuAlwaysAdd) {
    nonTargetSelected = selectedProducts.filter((p) => {
      return p.sku !== targetSku;
    });
  }
  if (nonTargetSelected.length === 0 || completeRegimenIsInCart) {
    disableATCButton(addToCartButton);
  } else {
    enableATCButton(addToCartButton);
  }
  const buttonLabel = addToCartButton.querySelector("[data-button-label]");
  if (buttonLabel) {
    if (completeRegimenIsInCart) {
      buttonLabel.textContent = `Your regimen is complete`;
    } else if (nonTargetSelected.length > 0) {
      buttonLabel.textContent = `Add ${selectedProducts.length === complementaryRegimen.length ? "ALL" : ""} ${selectedProducts.length === 2 ? "both" : selectedProducts.length} TO BAG`;
    } else {
      buttonLabel.textContent = selectItemsLabel;
    }
  }
}
function disableATCButton(button) {
  if (button) {
    button.disabled = true;
    button.classList.add("pointer-events-none");
    button.classList.add("opacity-50");
  }
}
function enableATCButton(button) {
  if (button) {
    button.classList.remove("pointer-events-none");
    button.classList.remove("opacity-50");
    button.disabled = false;
  }
}
function showLoadingUI(button, spinner) {
  disableATCButton(button);
  spinner?.classList.remove("hidden");
}
function hideLoadingUI(button, spinner) {
  enableATCButton(button);
  spinner?.classList.add("hidden");
}
function updateTotalPrice(elements, addingPrice, inCartPrice, selectedProducts, complementaryRegimen, categoriesCount, discountInfo) {
  const {
    elPriceLabel,
    elPriceValue,
    elPriceOriginal,
    elPriceDiscounted,
    elPriceSavings,
    elBundle,
    elBundleLabel,
    elBundleValue,
    elBundleOriginal,
    elBundleDiscounted,
    elBundleSavings
  } = elements;
  if (!elPriceLabel || !elPriceValue)
    return;
  const completeRegimenIsInCart = complementaryRegimen.every(
    (product) => product.quantityInCart > 0
  );
  const uniqueInCart = complementaryRegimen.filter(
    (p) => p.quantityInCart > 0
  ).length;
  const effectiveCount = uniqueInCart + selectedProducts.length;
  const renderPrice = (els, subtotal, effectiveCount2) => {
    const discount = discountInfo.computeDiscount(effectiveCount2);
    const formatted = discountInfo.formatPrice(subtotal, discount);
    if (discount) {
      els.elOriginal.textContent = formatted.original;
      els.elOriginal.classList.remove("hidden");
      els.elDiscounted.textContent = formatted.discounted;
      els.elDiscounted.classList.remove("hidden");
      els.elSavings.textContent = formatted.savings;
      els.elSavings.classList.remove("hidden");
      els.elValue.textContent = "";
      els.elValue.classList.add("hidden");
    } else {
      els.elOriginal.classList.add("hidden");
      els.elDiscounted.classList.add("hidden");
      els.elSavings.classList.add("hidden");
      els.elValue.textContent = formatted.value;
      els.elValue.classList.remove("hidden");
    }
  };
  const hideDiscountEls = () => {
    elPriceOriginal?.classList.add("hidden");
    elPriceDiscounted?.classList.add("hidden");
    elPriceSavings?.classList.add("hidden");
    elPriceValue?.classList.remove("hidden");
  };
  const hideBundle = () => {
    elBundle?.classList.add("hidden");
  };
  if (addingPrice > 0 && selectedProducts.length > 0) {
    const addingCount = selectedProducts.length;
    if (uniqueInCart > 0) {
      elPriceLabel.textContent = `Adding ${addingCount}: `;
    } else {
      const allLabel = effectiveCount === categoriesCount ? "all " : "";
      elPriceLabel.textContent = `Price for ${allLabel}${addingCount}: `;
    }
    renderPrice(
      {
        elOriginal: elPriceOriginal,
        elDiscounted: elPriceDiscounted,
        elSavings: elPriceSavings,
        elValue: elPriceValue
      },
      addingPrice,
      effectiveCount
    );
    if (uniqueInCart > 0 && elBundle && elBundleLabel) {
      const bundleTotal = inCartPrice + addingPrice;
      const allLabel = effectiveCount === categoriesCount ? "all " : "";
      elBundleLabel.textContent = `Total for ${allLabel}${effectiveCount} regimen items: `;
      renderPrice(
        {
          elOriginal: elBundleOriginal,
          elDiscounted: elBundleDiscounted,
          elSavings: elBundleSavings,
          elValue: elBundleValue
        },
        bundleTotal,
        effectiveCount
      );
      elBundle.classList.remove("hidden");
    } else {
      hideBundle();
    }
  } else if (completeRegimenIsInCart) {
    hideDiscountEls();
    hideBundle();
    elPriceLabel.textContent = "\xA0";
    elPriceValue.textContent = "\xA0";
  } else {
    hideDiscountEls();
    hideBundle();
    elPriceLabel.textContent = "\xA0";
    elPriceValue.textContent = "\xA0";
  }
}
function updateProductTiles(productTiles, regimen, targetSku, targetSkuAlwaysAdd) {
  productTiles.forEach((tile, i) => {
    const t = (
      /** @type {HTMLElement} */
      tile
    );
    const productData = regimen[i];
    if (productData) {
      t.dataset.variantId = String(productData.variantId || "");
      const checkbox = t.querySelector("input[type='checkbox']");
      const elInCartMessageWrapper = tile.querySelector(
        ".product_in_cart_message_wrapper"
      );
      const elProductDetailsWrapper = tile.querySelector(
        ".product_details_wrapper"
      );
      const elListHidenWhenInCart = tile.querySelectorAll(
        ".hide_when_product_in_cart"
      );
      if (elInCartMessageWrapper && elProductDetailsWrapper) {
        if (productData.quantityInCart > 0) {
          elInCartMessageWrapper.classList.remove("hidden");
          elProductDetailsWrapper.classList.add("opacity-50");
          elListHidenWhenInCart.forEach((el) => el.classList.add("hidden"));
        } else {
          elInCartMessageWrapper.classList.add("hidden");
          elProductDetailsWrapper.classList.remove("opacity-50");
          elListHidenWhenInCart.forEach(
            (el) => el.classList.remove("hidden")
          );
        }
      }
      if (checkbox) {
        const cb = (
          /** @type {HTMLInputElement} */
          checkbox
        );
        cb.dataset.variantId = String(productData.variantId);
        cb.dataset.size = productData.size;
        cb.dataset.price = String(productData.price);
        if (targetSkuAlwaysAdd && targetSku === productData.sku && productData.quantityInCart === 0) {
          cb.disabled = true;
          cb.classList.add("hidden");
          cb.checked = true;
        } else if (productData.quantityInCart > 0) {
          cb.disabled = true;
          cb.classList.add("hidden");
          cb.checked = false;
        } else {
          cb.disabled = false;
          cb.classList.remove("hidden");
          cb.checked = true;
        }
      }
      for (const key in productData) {
        const thisTileElement = t.querySelector(`.product_${key}`);
        if (thisTileElement) {
          switch (key) {
            case "category":
              thisTileElement.textContent = CATEGORY_LABELS[productData[key]] || productData[key];
              break;
            case "price":
              thisTileElement.textContent = `$${Number(productData[key]).toFixed(2)}`;
              break;
            case "imgUrl":
              if (thisTileElement instanceof HTMLImageElement) {
                thisTileElement.src = productData[key];
                thisTileElement.classList.remove("hidden");
              }
              break;
            case "pdpUrl":
              if (thisTileElement instanceof HTMLAnchorElement) {
                thisTileElement.href = productData[key];
              }
              break;
            case "desc":
              thisTileElement.textContent = CATEGORY_DESCRIPTIONS[productData["category"]] || productData["desc"] || "";
              break;
            default:
              thisTileElement.textContent = String(productData[key]);
          }
        }
      }
      const thisItemPreface = tile.querySelector(
        ".product_name_this_item_preface"
      );
      if (thisItemPreface && productData.sku === targetSku) {
        thisItemPreface.classList.remove("hidden");
      }
      const inCartPreface = tile.querySelector(".product_in_cart");
      if (inCartPreface) {
        if (productData.quantityInCart > 0) {
          inCartPreface.classList.remove("hidden");
          const inCartCount = inCartPreface.querySelector(
            ".product_in_cart_count"
          );
          if (inCartCount) {
            inCartCount.textContent = productData.quantityInCart;
          }
        } else {
          inCartPreface.classList.add("hidden");
        }
      }
    } else {
      tile.style.display = "none";
    }
  });
}
function updateDiscountBanner(banner, discounts, effectiveCount, discountInfo) {
  if (!banner || !discounts || discounts.length === 0)
    return;
  banner.classList.remove("hidden");
  const applicableDiscount = discountInfo.computeDiscount(effectiveCount);
  const tiersEl = banner.querySelector("[data-discount-tiers]");
  if (tiersEl) {
    tiersEl.innerHTML = "";
    const sortedTiers = [...discounts].sort(
      (a, b) => a.minQuantity - b.minQuantity
    );
    sortedTiers.forEach((tier) => {
      const eligible = effectiveCount >= tier.minQuantity;
      const li = document.createElement("li");
      li.className = "flex flex-row items-center gap-2 py-[2px] select-none";
      const iconWrapper = document.createElement("span");
      iconWrapper.className = [
        "flex-shrink-0 w-[18px] h-[18px] rounded-full border-2",
        "flex items-center justify-center transition-colors duration-200",
        eligible ? "bg-text-primary border-text-primary" : "border-text-secondary opacity-40"
      ].join(" ");
      if (eligible) {
        iconWrapper.innerHTML = `<svg viewBox="0 0 12 12" fill="none" class="w-[18px] h-[18px]"><path d="M2 6l3 3 5-5" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
      }
      const label = document.createElement("span");
      label.className = `body-copy-1 transition-colors duration-200 ${eligible ? "text-text-primary" : "text-text-primary opacity-60"}`;
      label.textContent = tier.description;
      li.appendChild(iconWrapper);
      li.appendChild(label);
      tiersEl.appendChild(li);
    });
  }
  const noticeEl = banner.querySelector("[data-discount-applied-notice]");
  if (noticeEl) {
    if (applicableDiscount) {
      noticeEl.classList.remove("hidden");
      noticeEl.textContent = `${applicableDiscount.percentage}% discount auto-applied to your regimen`;
    } else {
      noticeEl.classList.add("hidden");
    }
  }
}

// src/js/customElements/complementary-regimen-visibility.js
function setupIntersectionObserver(elementToObserve, elListToHideSelectors = []) {
  const getElListToHide = () => elListToHideSelectors.map((selector) => document.querySelector(selector)).filter((el) => el !== null);
  const prepElsToHide = () => {
    elListToHide.forEach((el) => el.classList.add("transition-opacity"));
  };
  let elListToHide = getElListToHide();
  prepElsToHide();
  if (elListToHideSelectors.length === 0 || !elementToObserve) {
    return null;
  }
  const observer = new IntersectionObserver(
    ([entry]) => {
      if (elListToHide.length < elListToHideSelectors.length) {
        elListToHide = getElListToHide();
        prepElsToHide();
      }
      if (entry.intersectionRatio >= 0.5 && window.innerWidth < 1024) {
        elListToHide.forEach((el) => el.classList.add("opacity-0"));
        elListToHide.forEach(
          (el) => setTimeout(() => el.classList.add("visually-hidden"), 150)
        );
      } else {
        elListToHide.forEach((el) => el.classList.remove("opacity-0"));
        elListToHide.forEach(
          (el) => setTimeout(() => el.classList.remove("visually-hidden"), 150)
        );
      }
    },
    { threshold: 0.5 }
  );
  observer.observe(elementToObserve);
  return observer;
}
function disconnectIntersectionObserver(observer) {
  if (observer) {
    observer.disconnect();
  }
}
function reduceTheNoise(elementToObserve) {
  return setupIntersectionObserver(
    elementToObserve,
    ELEMENTS_TO_HIDE_ON_INTERSECTION
  );
}

// src/js/customElements/complementary-regimen.js
var ComplementaryRegimen = class extends HTMLElement {
  constructor() {
    super();
    this.section = this.querySelector(
      "section[data-complementary-regimen-main-section]"
    );
    this.checkboxList = this.querySelectorAll("input[type='checkbox']");
    this.addToCartButton = this.querySelector(
      "button[data-complementary-regimen-atc-button]"
    );
    this.form = this.querySelector("[data-complementary-regimen-form]");
    this.featureImage = this.querySelector("[data-feature-image]");
    this.addToCartSection = this.querySelector(
      "[data-complementary-regimen-atc-section]"
    );
    this.atcLoadingSpinner = this.addToCartSection?.querySelector("loading__spinner");
    this.loadingSpinnerTitle = this.querySelector(".loading_spinner_title");
    this.statusMessageTimeoutId = null;
    this.statusMessageEl = null;
    this.sku = this.dataset["sku"];
    this.personalization_group_id = this.dataset["personalizationGroupId"];
    this.targetSku = this.personalization_group_id || this.sku;
    this.targetSkuAlwaysAdd = this.dataset["targetSkuAlwaysAdd"] === "true";
    this.limitCompleteRegimenBundlesToOne = this.dataset["limitCompleteRegimenBundlesToOne"] === "true";
    this.concernLabel = this.dataset["concernLabel"] || "";
    this.concernColor = this.dataset["concernColor"] || "#000000";
    this.categories = this.dataset["categories"] ? this.dataset["categories"].split(",").map((cat) => cat.trim()) : DEFAULT_CATEGORIES;
    this.disableCompleteRegimenDiscountWhenOnSale = this.dataset["disableCompleteRegimenDiscountWhenOnSale"] === "true";
    this.hasSalePricedRecommendedItems = false;
    this.salePricedVariantIds = [];
    this.mainProduct = {};
    this.complementaryRegimen = [];
    this.complementaryRegimenObj = {};
    this.discounts = [];
    this.activeDiscountCode = null;
    this.cartToken = null;
    this.storefrontAccessToken = this.dataset["storefrontAccessToken"] || null;
    this.storefrontEndpoint = "/api/2026-04/graphql.json";
    this.skinQuizDomain = this.dataset["skinQuizDomain"];
    console.log(
      "node env",
      "development",
      "skin quiz domain",
      this.skinQuizDomain
    );
    this.cartUpdateUnsubscriber = null;
    this.intersectionObserver = null;
    this.strings = {
      addComplete: this.dataset["labelAddComplete"] ?? LABELS2.addComplete,
      addSome: this.dataset["labelAddSome"] ?? LABELS2.addSome,
      addOne: this.dataset["labelAddOne"] ?? LABELS2.addOne,
      regimenCompleteATC: this.dataset["labelRegimenComplete"] ?? "Your regimen is complete",
      regimenCompletePrice: this.dataset["labelRegimenCompletePrice"] ?? "",
      selectItemsToAdd: this.dataset["labelSelectItemsToAdd"] ?? "Select items to add",
      removedConflictingRegimen: this.dataset["labelRemovedConflictingRegimen"] ?? "Removed items from a different complete regimen before adding your current selection.",
      unableToUpdateRegimen: this.dataset["labelUnableToUpdateRegimen"] ?? "Could not update complete regimen items in your cart. Please try again."
    };
  }
  // ============================================================================
  // Data Loading & Caching Methods
  // ============================================================================
  /**
   * Load regimen data, using cache if available and fresh
   */
  async handleRegimenData() {
    let complementaryRegimen = [];
    const cachedRegimenData = getCachedRegimenData(
      this.targetSku,
      this.categories
    );
    const cachedRegimenProducts = getCachedRegimenProducts(
      cachedRegimenData?.regimenSkus
    );
    if (cachedRegimenData && cachedRegimenProducts) {
      complementaryRegimen = cachedRegimenProducts;
      this.discounts = cachedRegimenData.discounts || [];
    } else {
      const res = await fetchComplementaryRegimen(
        this.skinQuizDomain,
        this.targetSku,
        this.categories
      );
      if (!res.data || !res.data.regimenSkus || !res.data.productList) {
        throw new Error("Invalid regimen data from server");
      }
      const { regimenSkus, productList, discounts } = res.data;
      this.discounts = discounts || [];
      cacheRegimen(
        this.targetSku,
        this.categories,
        regimenSkus,
        this.discounts
      );
      cacheProductData(productList);
      complementaryRegimen = productList;
    }
    this.complementaryRegimen = complementaryRegimen;
    this.complementaryRegimenObj = complementaryRegimen.reduce((prev, curr) => {
      return {
        ...prev,
        [curr.variantId]: curr
      };
    }, {});
    return complementaryRegimen;
  }
  getRegimenVariantQuantitiesInCart(variantIdArray = [""], cartRaw) {
    const completeRegimenValue = extractCompleteRegimenValue(this.discounts);
    const regimenItemsInCart = cartRaw.items.filter((item) => {
      return item.properties?.[PRODUCT_PROPERTY_KEYS.completeRegimen] === completeRegimenValue && variantIdArray.includes(item.variant_id);
    });
    return variantIdArray.map((id) => {
      const item = regimenItemsInCart.find((item2) => item2.variant_id === id);
      return item ? item.quantity : 0;
    });
  }
  /**
   * Update quantity counts for products currently in cart
   */
  async updateQuantitiesInCart(cartData) {
    if (!cartData) {
      cartDebugHttp(
        "[CR][Shopify HTTP] getCartData request (updateQuantitiesInCart)"
      );
      cartData = await getCartData();
      cartDebugHttp(
        "[CR][Shopify HTTP] getCartData response (updateQuantitiesInCart)",
        {
          hasToken: Boolean(cartData?.cartRaw?.token)
        }
      );
    }
    if (!cartData) {
      console.warn("No cart data available to update quantities in cart");
      return;
    }
    if (cartData?.cartRaw?.token) {
      this.cartToken = cartData.cartRaw.token;
    }
    const variantQuantities = this.getRegimenVariantQuantitiesInCart(
      this.complementaryRegimen.map((product) => product.variantId),
      cartData.cartRaw
    );
    this.complementaryRegimen = this.complementaryRegimen.map((product, i) => ({
      ...product,
      quantityInCart: variantQuantities[i]
    }));
  }
  // ============================================================================
  // UI Update Methods
  // ============================================================================
  /**
   * Coordinate all UI updates after state changes
   */
  updateATC() {
    const selectedProducts = getSelectedProducts(this.checkboxList);
    const selectedWithProductData = selectedProducts.map((selected) => {
      const product = this.complementaryRegimenObj[selected.id];
      return { ...selected, sku: product?.sku };
    });
    this.updateTotalPriceDisplay(selectedWithProductData);
    this.updateATCButtonDisplay(selectedWithProductData);
    this.updateDiscountBannerDisplay(selectedWithProductData);
  }
  /**
   * Update the add-to-cart button state and label
   */
  updateATCButtonDisplay(selectedProducts) {
    updateAddToCartButtonState(
      this.addToCartButton,
      selectedProducts,
      this.complementaryRegimen,
      this.strings.selectItemsToAdd,
      this.targetSku,
      this.targetSkuAlwaysAdd
    );
  }
  /**
   * Update total price display with discount info
   */
  updateTotalPriceDisplay(selectedProducts) {
    const selectedPrices = selectedProducts.map((p) => {
      const product = this.complementaryRegimenObj[p.id];
      return product?.price || 0;
    });
    const addingPrice = calculateSelectedProductsPrice(
      selectedProducts.map((p) => ({
        price: selectedPrices[selectedProducts.indexOf(p)]
      }))
    );
    const inCartPrice = calculateInCartPrice(this.complementaryRegimen);
    const effectiveDiscounts = this.getEffectiveDiscounts();
    updateTotalPrice(
      {
        elPriceLabel: this.querySelector("[data-total-price-label]"),
        elPriceValue: this.querySelector("[data-total-price-value]"),
        elPriceOriginal: this.querySelector("[data-total-price-original]"),
        elPriceDiscounted: this.querySelector("[data-total-price-discounted]"),
        elPriceSavings: this.querySelector("[data-total-price-savings]"),
        elBundle: this.querySelector("[data-total-price-bundle]"),
        elBundleLabel: this.querySelector("[data-total-price-bundle-label]"),
        elBundleValue: this.querySelector("[data-total-price-bundle-value]"),
        elBundleOriginal: this.querySelector(
          "[data-total-price-bundle-original]"
        ),
        elBundleDiscounted: this.querySelector(
          "[data-total-price-bundle-discounted]"
        ),
        elBundleSavings: this.querySelector(
          "[data-total-price-bundle-savings]"
        )
      },
      addingPrice,
      inCartPrice,
      selectedProducts,
      this.complementaryRegimen,
      this.categories.length,
      {
        computeDiscount: (count) => computeApplicableDiscountForCount(effectiveDiscounts, count),
        formatPrice: (subtotal, discount) => formatPrice2(subtotal, discount)
      }
    );
  }
  /**
   * Update product tile displays
   */
  updateProductTiles() {
    const productTiles = this.querySelectorAll(
      ".complementary_regimen__product_tile"
    );
    updateProductTiles(
      productTiles,
      this.complementaryRegimen,
      this.targetSku,
      this.targetSkuAlwaysAdd
    );
  }
  /**
   * Update discount banner display
   */
  updateDiscountBannerDisplay(selectedProducts) {
    const banner = this.querySelector("[data-discount-banner]");
    const effectiveDiscounts = this.getEffectiveDiscounts();
    if (banner && effectiveDiscounts.length === 0) {
      banner.classList.add("hidden");
      return;
    }
    const uniqueInCart = this.complementaryRegimen.filter(
      (p) => p.quantityInCart > 0
    ).length;
    const effectiveCount = uniqueInCart + selectedProducts.length;
    updateDiscountBanner(banner, effectiveDiscounts, effectiveCount, {
      computeDiscount: (count) => computeApplicableDiscountForCount(effectiveDiscounts, count)
    });
  }
  shouldSuppressRegimenDiscounts() {
    return this.disableCompleteRegimenDiscountWhenOnSale && this.hasSalePricedRecommendedItems;
  }
  getEffectiveDiscounts() {
    if (this.shouldSuppressRegimenDiscounts()) {
      return [];
    }
    return this.discounts || [];
  }
  async evaluateSalePricingGuard() {
    if (!this.disableCompleteRegimenDiscountWhenOnSale) {
      return;
    }
    const variantIds = this.complementaryRegimen.map((product) => Number(product?.variantId)).filter((id) => Number.isFinite(id) && id > 0);
    if (variantIds.length === 0) {
      return;
    }
    try {
      const pricingByVariantId = await fetchVariantPricingByIds(variantIds);
      this.salePricedVariantIds = variantIds.filter((variantId) => {
        const pricing = pricingByVariantId[variantId];
        if (!pricing || pricing.compareAtPrice == null) {
          return false;
        }
        return pricing.compareAtPrice > pricing.price;
      });
      this.hasSalePricedRecommendedItems = this.salePricedVariantIds.length > 0;
      cartDebug("[CR] sale-pricing discount guard evaluation", {
        enabled: this.disableCompleteRegimenDiscountWhenOnSale,
        hasSalePricedRecommendedItems: this.hasSalePricedRecommendedItems,
        salePricedVariantIds: this.salePricedVariantIds
      });
    } catch (err) {
      this.salePricedVariantIds = [];
      this.hasSalePricedRecommendedItems = false;
      cartDebug("[CR] failed sale-pricing guard evaluation", err);
    }
  }
  // ============================================================================
  // Cart & Discount Methods
  // ============================================================================
  /**
   * Ensure there is an element to display lightweight status messages.
   * @returns {HTMLElement|null}
   */
  ensureStatusMessageElement() {
    if (this.statusMessageEl) {
      return this.statusMessageEl;
    }
    const messageEl = document.createElement("div");
    messageEl.setAttribute("data-complementary-regimen-status", "");
    messageEl.setAttribute("aria-live", "polite");
    messageEl.className = "hidden mt-2 body-copy-2 text-text-primary";
    if (this.addToCartSection) {
      this.addToCartSection.appendChild(messageEl);
    } else {
      this.appendChild(messageEl);
    }
    this.statusMessageEl = messageEl;
    return this.statusMessageEl;
  }
  /**
   * Show a short-lived status message to improve add-to-cart transparency.
   * @param {string} message
   */
  showStatusMessage(message) {
    if (!message) {
      return;
    }
    const messageEl = this.ensureStatusMessageElement();
    if (!messageEl) {
      return;
    }
    messageEl.textContent = message;
    messageEl.classList.remove("hidden");
    if (this.statusMessageTimeoutId) {
      window.clearTimeout(this.statusMessageTimeoutId);
    }
    this.statusMessageTimeoutId = window.setTimeout(() => {
      if (this.statusMessageEl) {
        this.statusMessageEl.classList.add("hidden");
      }
    }, 6e3);
  }
  /**
   * Return the current bundle's _complete_regimen value, or null if unavailable.
   * @returns {string|null}
   */
  getCurrentCompleteRegimenValue() {
    if (!this.discounts || this.discounts.length === 0) {
      return null;
    }
    const value = extractCompleteRegimenValue(this.discounts);
    return value && value.trim() !== "" ? value : null;
  }
  /**
   * Find cart line item keys to remove when they belong to a different complete regimen bundle.
   * @returns {Promise<string[]>}
   */
  async getConflictingCompleteRegimenItemKeys() {
    const currentValue = this.getCurrentCompleteRegimenValue();
    if (!currentValue) {
      return [];
    }
    cartDebugHttp(
      "[CR][Shopify HTTP] getCartData request (conflictingRegimenCheck)"
    );
    const cartData = await getCartData(true);
    cartDebugHttp(
      "[CR][Shopify HTTP] getCartData response (conflictingRegimenCheck)",
      {
        itemCount: cartData?.cartRaw?.items?.length ?? 0
      }
    );
    const cartItems = cartData?.cartRaw?.items ?? [];
    return cartItems.filter((item) => {
      const itemValue = item?.properties?.[PRODUCT_PROPERTY_KEYS.completeRegimen];
      return itemValue != null && itemValue !== "" && itemValue !== currentValue;
    }).map((item) => item.key).filter(Boolean);
  }
  /**
   * Remove cart line items by key via cart/change.js.
   * @param {string[]} itemKeys
   */
  async removeCartItemsByKey(itemKeys) {
    if (!Array.isArray(itemKeys) || itemKeys.length === 0) {
      return;
    }
    cartDebugHttp(
      "[CR][Shopify HTTP] getCartData request (resolve conflicting regimen keys)",
      {
        itemCount: itemKeys.length
      }
    );
    const freshCartData = await getCartData(true);
    const freshCartItems = freshCartData?.cartRaw?.items ?? [];
    const itemKeySet = new Set(itemKeys);
    const updates = freshCartItems.reduce((prev, item) => {
      if (itemKeySet.has(item.key)) {
        return {
          ...prev,
          [item.key]: 0
        };
      }
      return prev;
    }, {});
    const keysToRemove = Object.keys(updates);
    if (keysToRemove.length === 0) {
      cartDebugHttp(
        "[CR][Shopify HTTP] conflicting regimen items no longer in cart; skipping",
        {
          requestedItemCount: itemKeys.length
        }
      );
      return;
    }
    cartDebugHttp(
      "[CR][Shopify HTTP] cart/update request (remove conflicting regimen items)",
      {
        requestedItemCount: itemKeys.length,
        resolvedItemCount: keysToRemove.length
      }
    );
    const res = await fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify({
        updates
      })
    });
    if (!res.ok) {
      const errorText = await res.text();
      throw new Error(
        `Failed removing conflicting complete regimen items (count ${keysToRemove.length}, status ${res.status}) ${errorText}`
      );
    }
    cartDebugHttp(
      "[CR][Shopify HTTP] cart/update response (remove conflicting regimen items)",
      {
        resolvedItemCount: keysToRemove.length,
        status: res.status
      }
    );
  }
  /**
   * Remove current complete-regimen discount code before conflicting bundle cleanup.
   * @returns {Promise<boolean>} - true if a complete-regimen code was removed
   */
  async removeCompleteRegimenDiscountCodeIfPresent() {
    cartDebugHttp(
      "[CR][Shopify HTTP] getCartData request (removeCompleteRegimenDiscountCodeIfPresent)"
    );
    const cartData = await getCartData(true);
    this.cartToken = cartData?.cartRaw?.token || this.cartToken;
    const discountCodeList = Array.isArray(cartData?.cartRaw?.discount_codes) ? cartData.cartRaw.discount_codes.map((entry) => entry?.code) : [];
    const discountCodeSingle = cartData?.cartRaw?.discount_code;
    const activeCompleteCode = discountCodeList.find(
      (code) => typeof code === "string" && code.startsWith(COMPLETE_REGIMEN_DISCOUNT_CODE_PREFIX)
    ) || (typeof discountCodeSingle === "string" && discountCodeSingle.startsWith(COMPLETE_REGIMEN_DISCOUNT_CODE_PREFIX) ? discountCodeSingle : null);
    if (!activeCompleteCode || !this.cartToken) {
      return false;
    }
    cartDebugHttp(
      "[CR][Shopify HTTP] updateCartDiscountCodes request (clear complete-regimen code)",
      {
        discountCode: activeCompleteCode
      }
    );
    await updateCartDiscountCodes(this.cartToken, [], {
      endpoint: this.storefrontEndpoint,
      storefrontToken: this.storefrontAccessToken
    });
    this.activeDiscountCode = null;
    cartDebugHttp(
      "[CR][Shopify HTTP] updateCartDiscountCodes response (clear complete-regimen code)"
    );
    return true;
  }
  /**
   * Add selected items to cart and sync discount code
   */
  async addItemsToCart() {
    showLoadingUI(this.addToCartButton, this.atcLoadingSpinner);
    try {
      const selectedCheckboxes = getSelectedProducts(this.checkboxList);
      const variantArray = createAddProductPayload(
        selectedCheckboxes,
        this.complementaryRegimenObj,
        this.targetSku,
        this.concernLabel,
        this.concernColor,
        this.discounts
      );
      if (this.limitCompleteRegimenBundlesToOne) {
        const conflictingItemKeys = await this.getConflictingCompleteRegimenItemKeys();
        if (conflictingItemKeys.length > 0) {
          await this.removeCartItemsByKey(conflictingItemKeys);
          this.showStatusMessage(this.strings.removedConflictingRegimen);
        }
      }
      addItemsToCart(variantArray);
      const simulatedRegimen = this.complementaryRegimen.map((product) => {
        const match = variantArray.find((v) => v.id === product.variantId);
        if (match) {
          return {
            ...product,
            quantityInCart: (product.quantityInCart || 0) + (match.quantity || 1)
          };
        }
        return product;
      });
    } catch (err) {
      hideLoadingUI(this.addToCartButton, this.atcLoadingSpinner);
      this.showStatusMessage(this.strings.unableToUpdateRegimen);
      console.error("Failed to add complementary regimen items:", err);
    }
  }
  /**
   * Apply/remove discount code based on items in cart
   */
  async syncDiscountCode(regimenOverride = null) {
    if (!this.discounts || this.discounts.length === 0)
      return;
    if (this.shouldSuppressRegimenDiscounts()) {
      await this.removeCompleteRegimenDiscountCodeIfPresent();
      this.activeDiscountCode = null;
      return;
    }
    const effectiveDiscounts = this.getEffectiveDiscounts();
    if (!effectiveDiscounts || effectiveDiscounts.length === 0)
      return;
    const regimen = regimenOverride || this.complementaryRegimen;
    const applicable = computeApplicableDiscount(regimen, effectiveDiscounts);
    const newCode = applicable?.code || null;
    if (newCode !== this.activeDiscountCode) {
      try {
        cartDebugHttp("[CR][Shopify HTTP] discount code transition", {
          oldCode: this.activeDiscountCode,
          newCode
        });
        if (!this.cartToken) {
          cartDebugHttp(
            "[CR][Shopify HTTP] getCartData request (syncDiscountCode)"
          );
          const freshCartData = await getCartData();
          this.cartToken = freshCartData?.cartRaw?.token || null;
          cartDebugHttp(
            "[CR][Shopify HTTP] getCartData response (syncDiscountCode)",
            {
              hasToken: Boolean(this.cartToken)
            }
          );
        }
        if (this.cartToken) {
          const codes = newCode ? [newCode] : [];
          cartDebugHttp("[CR][Shopify HTTP] updateCartDiscountCodes request", {
            cartToken: this.cartToken,
            codes,
            endpoint: this.storefrontEndpoint
          });
          await updateCartDiscountCodes(this.cartToken, codes, {
            endpoint: this.storefrontEndpoint,
            storefrontToken: this.storefrontAccessToken
          });
          cartDebugHttp("[CR][Shopify HTTP] updateCartDiscountCodes response", {
            appliedCodes: codes
          });
        }
        this.activeDiscountCode = newCode;
        cartActions.cartRefresh();
      } catch (err) {
        cartDebugHttp("[CR][Shopify HTTP] updateCartDiscountCodes error", err);
        console.error(
          "Failed to apply regimen discount code via Storefront API:",
          err
        );
      }
    }
  }
  // ============================================================================
  // Event Handlers
  // ============================================================================
  /**
   * Handle cart update events
   */
  async onCartUpdate(cartData) {
    hideLoadingUI(this.addToCartButton, this.atcLoadingSpinner);
    cartDebug("complementary-regimen onCartUpdate", cartData);
    await this.updateQuantitiesInCart(cartData);
    this.updateProductTiles();
    this.updateATC();
    await this.syncDiscountCode();
  }
  /**
   * Register event listeners
   */
  addEventListeners() {
    this.checkboxList.forEach((checkbox) => {
      checkbox.addEventListener("change", this.updateATC.bind(this));
    });
    this.addToCartButton?.addEventListener(
      "click",
      this.addItemsToCart.bind(this)
    );
  }
  // ============================================================================
  // Lifecycle Methods
  // ============================================================================
  /**
   * Component mounted: initialize data, UI, and event listeners
   */
  async connectedCallback() {
    this.intersectionObserver = reduceTheNoise(this);
    try {
      const complementaryRegimen = await this.handleRegimenData();
      await this.evaluateSalePricingGuard();
      this.mainProduct = complementaryRegimen.find(
        (product) => product.sku === this.targetSku
      );
      await this.updateQuantitiesInCart();
      this.updateProductTiles();
      updateBundledProductDisclaimers(
        this,
        complementaryRegimen.map((product) => product.variantId)
      );
      if (this.form) {
        this.form.classList.remove("hidden");
      }
      if (this.featureImage) {
        this.featureImage.classList.remove("hidden");
      }
      this.updateATC();
      this.addEventListeners();
      this.cartUpdateUnsubscriber = subscribe(
        PUB_SUB_EVENTS.cartMutationComplete,
        async (payload) => {
          await this.onCartUpdate(payload.cartData);
        },
        "complementary-regimen"
      );
    } catch (err) {
      this.section?.style.setProperty("min-height", "unset");
      this.style.display = "none";
      console.error(err);
    } finally {
      this.loadingSpinnerTitle?.classList.add("hidden");
    }
  }
  /**
   * Component removed: clean up event listeners and observers
   */
  disconnectedCallback() {
    this.checkboxList.forEach((checkbox) => {
      checkbox.removeEventListener("change", this.updateATC.bind(this));
    });
    this.addToCartButton?.removeEventListener(
      "click",
      this.addItemsToCart.bind(this)
    );
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this.intersectionObserver) {
      disconnectIntersectionObserver(this.intersectionObserver);
    }
    if (this.statusMessageTimeoutId) {
      window.clearTimeout(this.statusMessageTimeoutId);
    }
  }
};
customElements.define("complementary-regimen", ComplementaryRegimen);

// src/js/customElements/countdown-timer-session.js
var CountdownTimerSession = class extends HTMLElement {
  constructor() {
    super();
    this.intervalId = null;
    this.hasRedirected = false;
    this.hasHiddenComponent = false;
    this.hasReplacedContent = false;
    this.startTimeMs = Date.now();
    this.displayEl = this.querySelector("[data-countdown-display]");
    this.contentEl = this.querySelector("[data-countdown-content]");
    this.startMinutes = this.parseStartMinutes(this.dataset["startMinutes"]);
    this.hideOnExpireEnabled = this.parseBoolean(this.dataset["hideOnExpireEnabled"]);
    this.replaceWithTextEnabled = this.parseBoolean(this.dataset["replaceWithTextEnabled"]);
    this.replaceWithText = this.dataset["replaceWithText"]?.trim() ?? "";
    this.redirectEnabled = this.parseBoolean(this.dataset["redirectEnabled"]);
    this.redirectUrl = this.dataset["redirectUrl"]?.trim() ?? "";
    const sectionId = this.dataset["sectionId"] || "header-bare";
    const path = window.location.pathname || "/";
    this.storageKey = `murad:countdown-timer-session:${sectionId}:${path}`;
    this.durationMs = this.startMinutes * 60 * 1e3;
  }
  connectedCallback() {
    this.initializeStartTime();
    this.updateDisplay();
    this.showReadyState();
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    this.intervalId = window.setInterval(() => {
      this.updateDisplay();
    }, 1e3);
  }
  disconnectedCallback() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
  /**
   * @param {string | undefined} value
   */
  parseStartMinutes(value) {
    const parsed = Number.parseInt(value ?? "", 10);
    if (!Number.isFinite(parsed) || parsed < 1) {
      return 5;
    }
    return parsed;
  }
  /**
   * @param {string | boolean | undefined} value
   */
  parseBoolean(value) {
    return value === true || value === "true" || value === "1";
  }
  initializeStartTime() {
    try {
      const existingValue = window.localStorage.getItem(this.storageKey);
      if (existingValue) {
        const existingTime = Number.parseInt(existingValue, 10);
        if (Number.isFinite(existingTime) && existingTime > 0) {
          this.startTimeMs = existingTime;
          return;
        }
      }
      this.startTimeMs = Date.now();
      window.localStorage.setItem(this.storageKey, this.startTimeMs.toString());
    } catch (_err) {
      this.startTimeMs = Date.now();
    }
  }
  showReadyState() {
    this.classList.remove("invisible");
  }
  getRemainingMs() {
    const elapsedMs = Date.now() - this.startTimeMs;
    return Math.max(this.durationMs - elapsedMs, 0);
  }
  /**
   * @param {number} msRemaining
   */
  formatMMSS(msRemaining) {
    const totalSeconds = Math.floor(msRemaining / 1e3);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  updateDisplay() {
    const remainingMs = this.getRemainingMs();
    if (this.displayEl) {
      this.displayEl.textContent = this.formatMMSS(remainingMs);
    }
    if (remainingMs === 0) {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
      if (this.hideOnExpireEnabled && !this.hasHiddenComponent) {
        this.classList.add("hidden");
        this.setAttribute("aria-hidden", "true");
        this.hasHiddenComponent = true;
      }
      if (this.contentEl && !this.hideOnExpireEnabled && this.replaceWithTextEnabled && this.replaceWithText && !this.hasReplacedContent) {
        const replacementEl = document.createElement("span");
        replacementEl.className = "eyebrow-1 whitespace-nowrap";
        replacementEl.textContent = this.replaceWithText;
        this.contentEl.replaceChildren(replacementEl);
        this.hasReplacedContent = true;
      }
      if (this.redirectEnabled && this.redirectUrl && !this.hasRedirected) {
        this.hasRedirected = true;
        window.location.assign(this.redirectUrl);
      }
    }
  }
};
customElements.define("countdown-timer-session", CountdownTimerSession);

// src/js/customElements/yotpo-points-expiring-widget.js
var LOCAL_STORAGE_KEY_YOTPO_POINTS_EXPIRING = "muradYotpoPointsExpiring";
var YOTPO_POINTS_EXPIRING_STALE_AFTER_MS = 1e3 * 60 * 15;
function getCachedPointsData(customerEmail) {
  const cachedVal = getLocalStorage(LOCAL_STORAGE_KEY_YOTPO_POINTS_EXPIRING);
  const cachedEntry = cachedVal?.[customerEmail];
  if (!cachedEntry) {
    return null;
  }
  const dataIsStale = hasTimeElapsed(
    cachedEntry.lastModified,
    YOTPO_POINTS_EXPIRING_STALE_AFTER_MS
  );
  if (dataIsStale) {
    return null;
  }
  return {
    pointsBalance: cachedEntry.pointsBalance,
    nextExpireOn: cachedEntry.nextExpireOn,
    nextExpireAmount: cachedEntry.nextExpireAmount
  };
}
function setCachedPointsData(customerEmail, pointsBalance, nextExpireOn, nextExpireAmount) {
  const cachedVal = getLocalStorage(LOCAL_STORAGE_KEY_YOTPO_POINTS_EXPIRING);
  setLocalStorage(LOCAL_STORAGE_KEY_YOTPO_POINTS_EXPIRING, {
    ...cachedVal,
    [customerEmail]: {
      pointsBalance,
      nextExpireOn,
      nextExpireAmount,
      lastModified: (/* @__PURE__ */ new Date()).toISOString()
    }
  });
}
function isOnRedeemUrlPage(redeemUrl) {
  const normalize = (path) => {
    try {
      return new URL(path, window.location.origin).pathname.replace(/\/+$/, "") || "/";
    } catch {
      return "/";
    }
  };
  return normalize(window.location.pathname) === normalize(redeemUrl);
}
var YotpoPointsExpiringWidget = class extends HTMLElement {
  constructor() {
    super();
    this.enabled = this.dataset.enabled === "true";
    this.minPoints = Number.parseInt(this.dataset.minPoints, 10) || 0;
    this.customerEmail = this.dataset.customerEmail?.trim() ?? "";
    this.apiUrl = this.dataset.apiUrl?.trim() ?? "";
    this.apiKey = this.dataset.apiKey?.trim() ?? "";
    this.template = this.dataset.template ?? "";
    this.redeemUrl = this.dataset.redeemUrl?.trim() || "/pages/rewards";
  }
  connectedCallback() {
    if (!this.enabled || !this.customerEmail || !this.apiUrl || !this.apiKey) {
      return;
    }
    this.forceFetch = isOnRedeemUrlPage(this.redeemUrl);
    this.fetchAndMaybeReplace();
  }
  async fetchAndMaybeReplace() {
    if (!this.forceFetch) {
      const cached = getCachedPointsData(this.customerEmail);
      if (cached) {
        this.maybeRender(
          cached.pointsBalance,
          cached.nextExpireOn,
          cached.nextExpireAmount
        );
        return;
      }
    }
    try {
      const res = await fetch(`${this.apiUrl}/api/customers/points-expiration`, {
        method: "POST",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": this.apiKey
        },
        body: JSON.stringify({ customer_email: this.customerEmail })
      });
      if (!res.ok) {
        return;
      }
      const { data } = await res.json();
      const pointsBalance = Number(data?.points_balance);
      const nextExpireOn = data?.next_points_expire_on ?? null;
      const nextExpireAmount = Number(data?.next_points_expire_amount);
      setCachedPointsData(
        this.customerEmail,
        pointsBalance,
        nextExpireOn,
        nextExpireAmount
      );
      this.maybeRender(pointsBalance, nextExpireOn, nextExpireAmount);
    } catch (err) {
      console.error("yotpo-points-expiring-widget", err);
    }
  }
  /**
   * @param {number} pointsBalance
   * @param {string|null} nextExpireOn
   * @param {number|null} nextExpireAmount
   */
  maybeRender(pointsBalance, nextExpireOn, nextExpireAmount) {
    if (pointsBalance === 0) {
      this.hidePointsDisplay();
      return;
    }
    if (!Number.isFinite(pointsBalance) || pointsBalance < this.minPoints || !nextExpireOn || !Number.isFinite(nextExpireAmount)) {
      return;
    }
    this.renderExpiringLink(nextExpireAmount, nextExpireOn);
  }
  hidePointsDisplay() {
    const display = this.querySelector("[data-yotpo-points-display]");
    if (display) {
      display.classList.add("hidden");
    }
  }
  /**
   * @param {number|null} nextExpireAmount
   * @param {string|null} nextExpireOn
   */
  renderExpiringLink(nextExpireAmount, nextExpireOn) {
    if (nextExpireAmount === null || nextExpireOn === null) {
      return;
    }
    const target = this.querySelector(
      "[data-yotpo-points-rewards-link]"
    );
    if (!target) {
      return;
    }
    const copy = this.template.replace("|points|", nextExpireAmount.toLocaleString()).replace("|date|", this.formatDate(nextExpireOn));
    const link = document.createElement("a");
    link.href = this.redeemUrl;
    link.className = "yotpo-rewards-points-expiring body-copy-3 text-text-secondary-alt underline";
    link.textContent = copy;
    target.replaceChildren(link);
  }
  formatDate(dateString) {
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString(document.documentElement.lang || "en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  }
};
customElements.define("yotpo-points-expiring-widget", YotpoPointsExpiringWidget);

// src/js/customElements/quiz-recommendations-constants.js
var LOCAL_STORAGE_KEY_QUIZ_METAFIELD = "muradQuizRecommendations_metafield";
var LOCAL_STORAGE_KEY_QUIZ_LOCAL_SESSIONS = "muradQuizRecommendations_localSessions";
var MAX_CART_ITEMS_TO_PROBE = 8;
var CONCERN_LABELS = {
  concern_unknown: "Your Regimen",
  for_breakouts: "Breakouts",
  for_brightening: "Brightening",
  for_darkSpots: "Dark Spots",
  for_dryness: "Dryness",
  for_liftingFirming: "Lifting & Firming",
  for_linesWrinkles: "Lines & Wrinkles",
  for_oilinessPores: "Oiliness & Pores",
  for_scars: "Scars",
  for_sensitiveEczema: "Sensitive & Eczema-Prone"
};

// src/js/customElements/quiz-recommendations-data.js
function readMetafieldValue(embeddedJson) {
  if (embeddedJson) {
    try {
      const parsed = JSON.parse(embeddedJson);
      setLocalStorage(LOCAL_STORAGE_KEY_QUIZ_METAFIELD, parsed);
      return parsed;
    } catch (err) {
      console.error("quiz-recommendations: failed to parse embedded metafield JSON", err);
    }
  }
  return getLocalStorage(LOCAL_STORAGE_KEY_QUIZ_METAFIELD);
}
function determineKnownUserRegimen(metafieldValue) {
  const sessions = metafieldValue?.quizSessions;
  if (!Array.isArray(sessions))
    return null;
  for (let i = sessions.length - 1; i >= 0; i--) {
    const entry = sessions[i];
    if (entry?.didComplete === true && Array.isArray(entry.regimenList) && entry.regimenList.length > 0) {
      return entry;
    }
  }
  return null;
}
function getBestKnownUserEntry(embeddedMetafieldJson) {
  const metafieldValue = readMetafieldValue(embeddedMetafieldJson);
  const localValue = getLocalStorage(LOCAL_STORAGE_KEY_QUIZ_LOCAL_SESSIONS);
  const metafieldEntry = determineKnownUserRegimen(metafieldValue);
  const localEntry = determineKnownUserRegimen(localValue);
  if (!metafieldEntry)
    return localEntry;
  if (!localEntry)
    return metafieldEntry;
  return new Date(localEntry.dateComplete) > new Date(metafieldEntry.dateComplete) ? localEntry : metafieldEntry;
}
function normalizeProduct(product) {
  if (!product)
    return null;
  return {
    variantId: product.variantId,
    sku: product.sku,
    name: product.name,
    category: product.category,
    tags: product.tags ?? [],
    price: product.price,
    compareAtPrice: product.compareAtPrice ?? null,
    size: product.size,
    desc: product.desc,
    imgUrl: product.imgUrl,
    pdpUrl: product.pdpUrl,
    rating: product.rating,
    // ProductWithStats has isInStock; completeRegimen doesn't - default to true and let
    // the live revalidation step (known-user path only) correct it;
    available: product.isInStock ?? product.available ?? true,
    // mirror new-product-card.liquid's optional embellishments; absent until skin-quiz-app's
    // GoDataFeed-backed product sync is extended to capture these metafields (see plan);
    rolloverImgUrl: product.rolloverImgUrl,
    award: product.award,
    awardAltText: product.awardAltText,
    upsellText: product.upsellText
  };
}
function flattenRegimenMap(regimen) {
  return Object.values(regimen ?? {}).filter(Boolean).map(normalizeProduct);
}
async function probeCartItemRegimens(skinQuizDomain, cartItems = []) {
  const distinctItems = [];
  const seenSkus = /* @__PURE__ */ new Set();
  for (const item of cartItems) {
    if (!item?.sku || seenSkus.has(item.sku))
      continue;
    seenSkus.add(item.sku);
    distinctItems.push(item);
    if (distinctItems.length >= MAX_CART_ITEMS_TO_PROBE)
      break;
  }
  const results = await Promise.allSettled(
    distinctItems.map(async (cartItem) => {
      const cachedRegimenData = getCachedRegimenData(cartItem.sku, DEFAULT_CATEGORIES);
      const cachedRegimenProducts = getCachedRegimenProducts(cachedRegimenData?.regimenSkus);
      if (cachedRegimenData && cachedRegimenProducts) {
        return { cartItem, productList: cachedRegimenProducts.map(normalizeProduct) };
      }
      const res = await fetchComplementaryRegimen(skinQuizDomain, cartItem.sku, DEFAULT_CATEGORIES);
      if (!res?.data?.regimenSkus || !res?.data?.productList) {
        throw new Error(`Invalid completeRegimen response for sku ${cartItem.sku}`);
      }
      const { regimenSkus, productList, discounts } = res.data;
      cacheRegimen(cartItem.sku, DEFAULT_CATEGORIES, regimenSkus, discounts || []);
      cacheProductData(productList);
      return { cartItem, productList: productList.map(normalizeProduct), discounts: discounts || [] };
    })
  );
  return distinctItems.map((cartItem, i) => results[i].status === "fulfilled" ? results[i].value : null).filter(Boolean);
}

// src/js/customElements/quiz-recommendations-dedupe.js
function buildCartSkuSet(cartData) {
  return new Set((cartData?.items ?? []).map((item) => item.sku));
}
function dedupeProducts(candidateProducts, cartSkuSet) {
  const seen = /* @__PURE__ */ new Set();
  const result = [];
  for (const product of candidateProducts ?? []) {
    if (!product?.sku)
      continue;
    if (product.available === false)
      continue;
    if (cartSkuSet.has(product.sku))
      continue;
    if (seen.has(product.sku))
      continue;
    seen.add(product.sku);
    result.push(product);
  }
  return result;
}

// src/js/customElements/quiz-recommendations-ui.js
async function loadSlider() {
  const Slider = await import("./chunk--Slider-D6YNBQKI.vbt.js");
  return Slider.default;
}
async function loadTabs() {
  const Tabs = await import("./chunk--Tabs-XDGVW46J.vbt.js");
  return Tabs.default;
}
var SLIDER_OPTIONS_BASE = {
  arrows: false,
  pagination: false,
  fixedWidth: "275px",
  gap: "1.5rem",
  padding: { left: "1rem", right: "5%" },
  breakpoints: {
    1024: { fixedWidth: "320px", arrows: true },
    1440: { fixedWidth: "360px" }
  }
};
function buildSliderOptions(sliderType, compact) {
  if (compact) {
    const { breakpoints, ...base } = SLIDER_OPTIONS_BASE;
    return { ...base, type: sliderType || "slide" };
  }
  return { ...SLIDER_OPTIONS_BASE, type: sliderType || "slide" };
}
var TAGS_EXCLUDED_FROM_QUICK_ADD = ["coming-soon", "waitlist"];
function buildQuickAdd(quickAddTemplate, product) {
  const node = quickAddTemplate.content.firstElementChild.cloneNode(true);
  node.dataset.quickAddVariantId = String(product.variantId ?? "");
  if (product.size)
    node.dataset.quickAddSize = product.size;
  return node;
}
function populateSimpleTile(tileTemplate, quickAddTemplate, product) {
  const node = tileTemplate.content.firstElementChild.cloneNode(true);
  node.dataset.variantId = String(product.variantId ?? "");
  node.dataset.variantSku = product.sku ?? "";
  node.dataset.productTitle = product.name ?? "";
  const imgEl = node.querySelector(".qr-imgUrl");
  if (imgEl instanceof HTMLImageElement && product.imgUrl) {
    imgEl.src = product.imgUrl;
    imgEl.alt = product.name ?? "";
    imgEl.classList.remove("hidden");
  }
  const rolloverEl = node.querySelector(".qr-rolloverImgUrl");
  if (rolloverEl instanceof HTMLImageElement && product.rolloverImgUrl) {
    rolloverEl.src = product.rolloverImgUrl;
    rolloverEl.alt = `${product.name ?? ""} rollover image`;
    rolloverEl.classList.remove("hidden");
    rolloverEl.classList.add("lg:block");
  }
  const awardEl = node.querySelector(".qr-award");
  if (awardEl instanceof HTMLImageElement && product.award) {
    awardEl.src = product.award;
    awardEl.alt = product.awardAltText ?? "";
    awardEl.classList.remove("hidden");
  }
  const upsellEl = node.querySelector(".qr-upsellText");
  if (upsellEl && product.upsellText) {
    upsellEl.textContent = product.upsellText.toUpperCase();
    upsellEl.classList.remove("hidden");
  }
  const categoryEl = node.querySelector(".qr-category");
  if (categoryEl)
    categoryEl.textContent = product.category ?? "";
  const nameEl = node.querySelector(".qr-name");
  if (nameEl)
    nameEl.textContent = product.name ?? "";
  const sizeEl = node.querySelector(".qr-size");
  if (sizeEl)
    sizeEl.textContent = product.size ?? "";
  const priceEl = node.querySelector(".qr-price");
  if (priceEl)
    priceEl.textContent = product.price != null ? formatPrice(product.price) : "";
  const compareAtEl = node.querySelector(".qr-compareAtPrice");
  if (compareAtEl) {
    if (product.compareAtPrice && product.compareAtPrice > product.price) {
      compareAtEl.textContent = formatPrice(product.compareAtPrice);
      compareAtEl.classList.remove("hidden");
    } else {
      compareAtEl.classList.add("hidden");
    }
  }
  const pdpEl = node.querySelectorAll(".qr-pdpUrl");
  pdpEl.forEach((el) => {
    if (el instanceof HTMLAnchorElement)
      el.href = product.pdpUrl ?? "#";
  });
  const ratingEl = node.querySelector(".qr-rating");
  if (ratingEl)
    ratingEl.textContent = product.rating != null ? String(product.rating) : "";
  const quickAddSlot = node.querySelector(".qr-quickAddSlot");
  const isExcludedFromQuickAdd = (product.tags ?? []).some(
    (tag) => TAGS_EXCLUDED_FROM_QUICK_ADD.includes(tag)
  );
  if (quickAddSlot && !isExcludedFromQuickAdd) {
    quickAddSlot.appendChild(buildQuickAdd(quickAddTemplate, product));
  }
  return node;
}
function buildSimpleTilePanel(tileTemplate, quickAddTemplate, products, arrowsTemplate, sliderType, compactArrows) {
  const splide = document.createElement("div");
  splide.setAttribute("data-slider", JSON.stringify(buildSliderOptions(sliderType, compactArrows)));
  if (compactArrows) {
    splide.className = "splide";
  } else {
    splide.className = "splide arrows-wider arrows-auto-hide";
    splide.appendChild(
      /** @type {Element} */
      arrowsTemplate.content.firstElementChild.cloneNode(true)
    );
  }
  const track = document.createElement("div");
  track.className = "splide__track";
  const list = document.createElement("div");
  list.className = "splide__list";
  products.forEach((product) => {
    const slide = document.createElement("div");
    slide.className = "splide__slide";
    slide.appendChild(populateSimpleTile(tileTemplate, quickAddTemplate, product));
    list.appendChild(slide);
  });
  track.appendChild(list);
  splide.appendChild(track);
  return splide;
}
function buildBundlePanel(bundleData, showDiscountBanner) {
  const el = document.createElement("quiz-recommendation-bundle");
  el.dataset.anchorSku = bundleData.anchorCartItem?.sku ?? "";
  el.dataset.anchorName = bundleData.anchorCartItem?.product_title ?? "";
  el.dataset.showDiscountBanner = String(showDiscountBanner);
  el.dataset.products = JSON.stringify(bundleData.productList);
  el.dataset.discounts = JSON.stringify(bundleData.discounts ?? []);
  return el;
}
function buildOverrideTabPanel(pristineSlides, cartSkuSet, maxSlots, arrowsTemplate, sliderType, compactArrows) {
  const qualifying = pristineSlides.filter((slide) => {
    const sku = slide.querySelector("[data-product-card]")?.dataset.variantSku;
    return !(sku && cartSkuSet.has(sku));
  }).slice(0, maxSlots);
  if (qualifying.length === 0)
    return null;
  const splide = document.createElement("div");
  splide.setAttribute("data-slider", JSON.stringify(buildSliderOptions(sliderType, compactArrows)));
  if (compactArrows) {
    splide.className = "splide";
  } else {
    splide.className = "splide arrows-wider arrows-auto-hide";
    splide.appendChild(
      /** @type {Element} */
      arrowsTemplate.content.firstElementChild.cloneNode(true)
    );
  }
  const track = document.createElement("div");
  track.className = "splide__track";
  const list = document.createElement("div");
  list.className = "splide__list";
  qualifying.forEach((slide) => list.appendChild(slide.cloneNode(true)));
  track.appendChild(list);
  splide.appendChild(track);
  return splide;
}
function applyCta(ctaEl, cta) {
  if (!(ctaEl instanceof HTMLAnchorElement))
    return;
  if (!cta) {
    ctaEl.classList.add("hidden");
    return;
  }
  ctaEl.textContent = cta.text;
  ctaEl.href = cta.href;
  ctaEl.classList.remove("hidden");
}
async function revealCuratedTab(container, pristineSlides, cartSkuSet, maxSlots, cta, sliderType, compactArrows) {
  applyCta(container.querySelector("[data-quiz-recommendations-cta]"), cta);
  const splideEl = container.querySelector(".splide");
  const listEl = container.querySelector(".splide__list");
  if (!splideEl || !listEl) {
    container.classList.add("hidden");
    return;
  }
  splideEl.splide?.destroy();
  const promoIndex = pristineSlides.findIndex((slide) => slide.matches("[data-quiz-recommendations-promo-slide]"));
  const promoSlide = promoIndex === -1 ? null : pristineSlides[promoIndex];
  const promoIsFirst = promoIndex === 0;
  const productSlides = promoSlide ? pristineSlides.filter((_, index) => index !== promoIndex) : pristineSlides;
  const qualifying = productSlides.filter((slide) => {
    const sku = slide.querySelector("[data-product-card]")?.dataset.variantSku;
    return !(sku && cartSkuSet.has(sku));
  }).slice(0, maxSlots);
  if (qualifying.length === 0 && !promoSlide) {
    container.classList.add("hidden");
    return;
  }
  listEl.innerHTML = "";
  if (promoSlide && promoIsFirst) {
    listEl.appendChild(promoSlide.cloneNode(true));
  }
  qualifying.forEach((slide) => listEl.appendChild(slide.cloneNode(true)));
  if (promoSlide && !promoIsFirst) {
    listEl.appendChild(promoSlide.cloneNode(true));
  }
  splideEl.setAttribute("data-slider", JSON.stringify(buildSliderOptions(sliderType, compactArrows)));
  const SliderClass = await loadSlider();
  new SliderClass(splideEl, "data-slider");
  container.classList.remove("hidden");
}
function hideCuratedTab(container) {
  const splideEl = container.querySelector(".splide");
  splideEl?.splide?.destroy();
  container.classList.add("hidden");
}
var CART_TAB_NAME_CHAR_BUDGET = 60;
var CART_TAB_NAME_MIN_CHARS = 12;
function truncateProductName(name, tabCount) {
  const safeName = name ?? "";
  const maxChars = Math.max(CART_TAB_NAME_MIN_CHARS, Math.floor(CART_TAB_NAME_CHAR_BUDGET / Math.max(tabCount, 2)));
  if (safeName.length <= maxChars)
    return safeName;
  return `${safeName.slice(0, maxChars - 1).trimEnd()}\u2026`;
}
async function renderTabs(rootEl, tabs, heading, cta) {
  rootEl.innerHTML = "";
  if (!tabs.length) {
    rootEl.classList.add("hidden");
    return;
  }
  rootEl.classList.remove("hidden");
  if (heading) {
    const headingRow = document.createElement("div");
    headingRow.className = "flex items-center justify-between gap-4 mb-6 ml-4 pr-4";
    const headingEl = document.createElement("h2");
    headingEl.className = "subheadline text-text-primary";
    headingEl.textContent = heading;
    headingRow.appendChild(headingEl);
    if (cta) {
      const ctaEl = document.createElement("a");
      ctaEl.target = "_blank";
      ctaEl.className = "max-w-[40%] eyebrow-1 underline text-text-primary text-right";
      applyCta(ctaEl, cta);
      headingRow.appendChild(ctaEl);
    }
    rootEl.appendChild(headingRow);
  }
  const tabsWrapper = document.createElement("div");
  tabsWrapper.setAttribute("data-tabs", "click");
  const tablist = document.createElement("div");
  tablist.setAttribute("role", "tablist");
  tablist.className = "flex gap-6 mb-4 ml-4 pr-16 overflow-x-scroll overflow-y-hidden no-scrollbar select-none";
  enableHorizontalScroll(tablist);
  tabs.forEach((tab, i) => {
    const button = document.createElement("button");
    button.type = "button";
    button.setAttribute("role", "tab");
    button.className = `min-h-8 flex items-center shrink-0 ${i === 0 ? "active" : ""} group`;
    button.setAttribute("data-tab-toggle", `qr-tab--${i}`);
    if (typeof tab.title !== "string" && tab.title.image) {
      const imgEl = document.createElement("img");
      imgEl.src = tab.title.image;
      imgEl.alt = "";
      imgEl.className = "w-20 h-20 rounded-full object-cover shrink-0 scale-150";
      imgEl.draggable = false;
      button.appendChild(imgEl);
    }
    const textWrapper = document.createElement("div");
    textWrapper.className = "h-full p-4 border-text-primary-translucent border-r-2 border-t-2 rounded-tr group-[.active]:border-text-primary group-[.active]:bg-gradient-to-b group-[.active]:from-body-alt group-[.active]:via-body-alt/80 group-[.active]:to-transparent";
    const h2 = document.createElement("h2");
    if (typeof tab.title === "string") {
      h2.className = "whitespace-nowrap subheadline text-text-primary-alt group-[.active]:text-text-primary group-[.active]:font-semibold";
      h2.textContent = tab.title;
    } else {
      h2.className = "whitespace-nowrap";
      const prefixEl = document.createElement("span");
      prefixEl.className = "block eyebrow-2 text-text-primary-alt group-[.active]:text-text-primary";
      prefixEl.textContent = tab.title.prefix;
      const nameEl = document.createElement("span");
      nameEl.className = "block product-name text-text-primary-alt group-[.active]:text-text-primary group-[.active]:font-semibold";
      nameEl.textContent = truncateProductName(tab.title.name, tabs.length);
      h2.appendChild(prefixEl);
      h2.appendChild(nameEl);
    }
    textWrapper.appendChild(h2);
    button.appendChild(textWrapper);
    tablist.appendChild(button);
  });
  tabsWrapper.appendChild(tablist);
  tabs.forEach((tab, i) => {
    const panelWrapper = document.createElement("div");
    panelWrapper.setAttribute("role", "tabpanel");
    panelWrapper.className = i === 0 ? "active" : "";
    panelWrapper.setAttribute("data-tab-content", `qr-tab--${i}`);
    panelWrapper.appendChild(tab.panel);
    tabsWrapper.appendChild(panelWrapper);
  });
  rootEl.appendChild(tabsWrapper);
  const TabsClass = await loadTabs();
  new TabsClass(tabsWrapper, "data-tabs").init();
  const sliderEls = rootEl.querySelectorAll("[data-slider]");
  if (sliderEls.length > 0) {
    const SliderClass = await loadSlider();
    sliderEls.forEach((el) => new SliderClass(el, "data-slider"));
  }
  tabsWrapper.addEventListener("tabs:activated", (event) => {
    event.target?.querySelectorAll?.("[data-slider]").forEach((el) => {
      el.splide?.refresh();
    });
  });
}

// src/js/customElements/quiz-recommendations.js
var LOG_PREFIX = "[quiz-recommendations]";
var DEBUG = false;
function log(...args) {
  if (DEBUG)
    console.log(LOG_PREFIX, ...args);
}
var QuizRecommendations = class extends HTMLElement {
  constructor() {
    super();
    this.rootEl = this.querySelector("[data-quiz-recommendations-root]");
    this.tileTemplate = document.querySelector("template[data-quiz-recommendations-simple-tile]");
    this.quickAddTemplate = document.querySelector(
      "template[data-quiz-recommendations-quick-add-template]"
    );
    this.arrowsTemplate = document.querySelector(
      "template[data-quiz-recommendations-arrows-template]"
    );
    this.curatedContainer = this.querySelector("[data-quiz-recommendations-curated]");
    this.curatedPristineSlides = Array.from(
      this.curatedContainer?.querySelectorAll(".splide__slide") ?? []
    ).map((slide) => (
      /** @type {Element} */
      slide.cloneNode(true)
    ));
    this.overrideSourceContainer = this.querySelector("[data-quiz-recommendations-override-source]");
    this.overridePristineSlides = Array.from(
      this.overrideSourceContainer?.querySelectorAll(".splide__slide") ?? []
    ).map((slide) => (
      /** @type {Element} */
      slide.cloneNode(true)
    ));
    this.overrideMode = this.dataset.overrideMode || "";
    this.overrideTabTitle = this.dataset.overrideTabTitle || "";
    this.quizCtaTakeText = this.dataset.quizCtaTakeText || "";
    this.quizCtaRetakeText = this.dataset.quizCtaRetakeText || "";
    this.quizCtaLoginText = this.dataset.quizCtaLoginText || "";
    this.showQuizCtaWithCartItems = this.dataset.showQuizCtaWithCartItems === "true";
    this.skinQuizDomain = this.dataset.skinQuizDomain || "";
    this.maxSlots = parseInt(this.dataset.maxSlots ?? "", 10) || 6;
    this.carouselType = this.dataset.carouselType || "slide";
    this.showDiscountBanner = this.dataset.showDiscountBanner === "true";
    this.forceCuratedForAll = this.dataset.forceCuratedForAll === "true";
    this.hasCustomer = this.dataset.hasCustomer === "true";
    this.knownUserTitle = this.dataset.knownUserTitle || "";
    this.knownUserTabPrefix = this.dataset.knownUserTabPrefix || "";
    this.cartBasedTitle = this.dataset.cartBasedTitle || "";
    this.cartBasedTabPrefix = this.dataset.cartBasedTabPrefix || "";
    this.cartBasedTabShowImage = this.dataset.cartBasedTabShowImage === "true";
    this.compactArrows = this.dataset.context === "cart_drawer";
    const metafieldScript = this.querySelector("script[data-quiz-recommendations-metafield]");
    this.embeddedMetafieldJson = this.hasCustomer ? metafieldScript?.textContent?.trim() : void 0;
    this.cartUpdateUnsubscriber = null;
    log("init:", {
      hasCustomer: this.hasCustomer,
      metafieldEmbedded: Boolean(this.embeddedMetafieldJson),
      curatedSlideCount: this.curatedPristineSlides.length,
      forceCuratedForAll: this.forceCuratedForAll,
      maxSlots: this.maxSlots,
      carouselType: this.carouselType,
      showDiscountBanner: this.showDiscountBanner,
      skinQuizDomain: this.skinQuizDomain
    });
  }
  async connectedCallback() {
    if (!this.rootEl || !this.tileTemplate || !this.quickAddTemplate || !this.arrowsTemplate) {
      console.error("quiz-recommendations: missing required child elements");
      this.style.display = "none";
      return;
    }
    try {
      await this.renderCurrentState();
      this.cartUpdateUnsubscriber = subscribe(
        PUB_SUB_EVENTS.cartMutationComplete,
        async () => {
          await this.renderCurrentState();
        },
        "quiz-recommendations"
      );
    } catch (err) {
      this.style.display = "none";
      console.error("quiz-recommendations: failed to initialize", err);
    }
  }
  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber)
      this.cartUpdateUnsubscriber();
  }
  async renderCurrentState() {
    const rootEl = this.rootEl;
    if (!rootEl)
      return;
    const cartData = await getCartData();
    const cartSkuSet = buildCartSkuSet(cartData);
    const cartItemCount = cartData?.items?.length ?? 0;
    const cartHasItems = cartItemCount > 0;
    const knownUserEntry = getBestKnownUserEntry(this.embeddedMetafieldJson);
    const suppressTakeQuizCta = !knownUserEntry && cartHasItems && !this.showQuizCtaWithCartItems;
    const cta = suppressTakeQuizCta ? void 0 : this.getQuizCta(Boolean(knownUserEntry));
    if (this.overrideMode === "replace" && this.overridePristineSlides.length > 0) {
      log('status: override mode "replace" (forces override list for all shoppers)', { cartItemCount });
      return this.renderCuratedTab(rootEl, cartSkuSet, "override replace", cta);
    }
    if (this.forceCuratedForAll) {
      log("status: forced curated for all shoppers (kill-switch on)", { cartItemCount });
      return this.renderCuratedTab(rootEl, cartSkuSet, "forceCuratedForAll", cta);
    }
    if (knownUserEntry) {
      log("status: known user", {
        sessionId: knownUserEntry.sessionId,
        concernPrimary: knownUserEntry.concernPrimary,
        concernSecondary: knownUserEntry.concernSecondary,
        dateComplete: knownUserEntry.dateComplete,
        cartItemCount
      });
      return this.renderKnownUserTabs(rootEl, knownUserEntry, cartSkuSet, cta);
    }
    log("status: unknown user", {
      hasCustomer: this.hasCustomer,
      cartItemCount,
      cartHasItems
    });
    if (cartHasItems) {
      return this.renderCartBasedTabs(rootEl, cartData, cartSkuSet, cta);
    }
    return this.renderCuratedTab(rootEl, cartSkuSet, "unknown user, empty cart", cta);
  }
  /**
   * Small CTA shown to the right of whichever title is currently visible, prompting the
   * shopper toward the quiz. "Take skin quiz" is the default for everyone, logged in or not
   * (guests can take the quiz too - see the guest-results feature in
   * quiz-recommendations-data.js). Once a result IS available, the prompt depends on login
   * state: a logged-in shopper can retake it directly, but a guest with a result should log
   * in first (so it gets backfilled into their account's metafield - see setCustomerId/
   * syncGuestQuizResult in skin-quiz-app's useQuizContext.tsx) rather than retake a quiz
   * they've already completed.
   * @param {boolean} hasKnownUserEntry - true if this shopper (guest or logged-in) has a
   *   completed quiz result available right now (see getBestKnownUserEntry)
   * @returns {{text: string, href: string} | undefined} undefined when the relevant CTA text
   *   setting is blank, so a merchant clearing e.g. "Log In" Text hides that CTA entirely
   *   rather than rendering a link with just the trailing arrow and no label.
   */
  getQuizCta(hasKnownUserEntry) {
    if (!hasKnownUserEntry) {
      if (!this.quizCtaTakeText)
        return void 0;
      return { text: this.quizCtaTakeText + " \u2197", href: "/pages/skin-quiz" };
    }
    if (this.hasCustomer) {
      if (!this.quizCtaRetakeText)
        return void 0;
      return { text: this.quizCtaRetakeText + " \u2197", href: "/pages/skin-regimen-quiz?retake=1" };
    }
    if (!this.quizCtaLoginText)
      return void 0;
    return { text: this.quizCtaLoginText + " \u2197", href: "/account/login" };
  }
  /**
   * When override_mode is "prepend", insert a new first tab built from the Product Override
   * list's pristine slides ahead of the given tabs array (mutates it via unshift). No-op when
   * mode isn't "prepend", there's no override list, or nothing in it currently qualifies
   * (e.g. everything already in cart) - see buildOverrideTabPanel's own filtering.
   * @param {{title: any, panel: HTMLElement}[]} tabs
   * @param {Set<string>} cartSkuSet
   */
  maybePrependOverrideTab(tabs, cartSkuSet) {
    if (this.overrideMode !== "prepend" || this.overridePristineSlides.length === 0)
      return;
    if (!this.arrowsTemplate)
      return;
    const panel = buildOverrideTabPanel(this.overridePristineSlides, cartSkuSet, this.maxSlots, this.arrowsTemplate, this.carouselType, this.compactArrows);
    if (panel)
      tabs.unshift({ title: this.overrideTabTitle, panel });
  }
  /**
   * @param {Element} rootEl
   * @param {any} entry - QuizSessionMetafieldEntry (skin-quiz-app shape, no local JS type)
   * @param {Set<string>} cartSkuSet
   * @param {{text: string, href: string}} [cta]
   */
  async renderKnownUserTabs(rootEl, entry, cartSkuSet, cta) {
    log(`source: known-user regimenList (metafield entry ${entry.sessionId})`);
    if (this.curatedContainer)
      hideCuratedTab(this.curatedContainer);
    if (!this.tileTemplate || !this.quickAddTemplate || !this.arrowsTemplate)
      return;
    const tileTemplate = this.tileTemplate;
    const quickAddTemplate = this.quickAddTemplate;
    const arrowsTemplate = this.arrowsTemplate;
    const [regimenPrimary, regimenSecondary] = entry.regimenList;
    const flatPrimary = flattenRegimenMap(regimenPrimary);
    const flatSecondary = flattenRegimenMap(regimenSecondary);
    const allVariantIds = [...flatPrimary, ...flatSecondary].map((product) => product.variantId);
    const pricingByVariantId = await fetchVariantPricingByIds(allVariantIds);
    const revalidate = (products) => products.filter((product) => pricingByVariantId[product.variantId]).map((product) => ({ ...product, ...pricingByVariantId[product.variantId] }));
    const rawTabs = [
      {
        title: CONCERN_LABELS[entry.concernPrimary] ?? entry.concernPrimary,
        rawCount: flatPrimary.length,
        products: revalidate(flatPrimary)
      },
      {
        title: CONCERN_LABELS[entry.concernSecondary] ?? entry.concernSecondary,
        rawCount: flatSecondary.length,
        products: revalidate(flatSecondary)
      }
    ];
    log("known-user revalidation:", rawTabs.map((tab) => ({
      title: tab.title,
      rawCount: tab.rawCount,
      afterRevalidateCount: tab.products.length
    })));
    const tabs = rawTabs.map((tab) => ({ ...tab, products: dedupeProducts(tab.products, cartSkuSet).slice(0, this.maxSlots) })).filter((tab) => tab.products.length > 0).map((tab) => ({
      title: { prefix: this.knownUserTabPrefix, name: tab.title },
      panel: buildSimpleTilePanel(tileTemplate, quickAddTemplate, tab.products, arrowsTemplate, this.carouselType, this.compactArrows)
    }));
    if (tabs.length === 0) {
      return this.renderCuratedTab(rootEl, cartSkuSet, "known user: no qualifying regimen products", cta);
    }
    this.maybePrependOverrideTab(tabs, cartSkuSet);
    log(
      "known-user final tabs (after cart dedupe + maxSlots):",
      tabs.map((tab) => tab.title)
    );
    await renderTabs(rootEl, tabs, this.knownUserTitle, cta);
  }
  /**
   * @param {Element} rootEl
   * @param {any} cartData - shaped cart data from getCartData()
   * @param {Set<string>} cartSkuSet
   * @param {{text: string, href: string}} [cta]
   */
  async renderCartBasedTabs(rootEl, cartData, cartSkuSet, cta) {
    log("source: cart-based completeRegimen (probing up to 8 distinct cart SKUs)");
    if (this.curatedContainer)
      hideCuratedTab(this.curatedContainer);
    const cartItemsNonZeroCost = cartData.items.filter((item) => item.price > 0) ?? [];
    const probed = await probeCartItemRegimens(this.skinQuizDomain, cartItemsNonZeroCost);
    log("cart-based probe results:", probed.map((result) => ({
      sku: result.cartItem.sku,
      title: result.cartItem.product_title,
      rawSuggestionCount: result.productList.length
    })));
    const maxTabs = Math.min(3, this.maxSlots);
    const qualifying = [];
    const rejected = [];
    for (const result of probed) {
      const deduped = dedupeProducts(result.productList, cartSkuSet);
      if (deduped.length >= 2) {
        qualifying.push({
          anchorCartItem: result.cartItem,
          productList: deduped.slice(0, this.maxSlots),
          discounts: result.discounts
        });
      } else {
        rejected.push({ sku: result.cartItem.sku, afterDedupeCount: deduped.length });
      }
      if (qualifying.length >= maxTabs)
        break;
    }
    log("cart-based qualification:", {
      qualifying: qualifying.map((bundleData) => ({
        anchorSku: bundleData.anchorCartItem.sku,
        anchorTitle: bundleData.anchorCartItem.product_title,
        finalProductCount: bundleData.productList.length
      })),
      rejected
    });
    if (qualifying.length === 0) {
      return this.renderCuratedTab(rootEl, cartSkuSet, "cart-based: no qualifying regimens", cta);
    }
    log(
      "cart-based presentation mode:",
      this.showDiscountBanner ? "bundle/discount UI" : "plain carousel (no discount)"
    );
    const titleFor = (bundleData) => ({
      prefix: this.cartBasedTabPrefix,
      name: bundleData.anchorCartItem.product_title,
      // cart.js line items carry their own product image URL directly (`.image`) - no extra
      // lookup needed;
      image: this.cartBasedTabShowImage ? bundleData.anchorCartItem.image : void 0
    });
    let tabs;
    if (this.showDiscountBanner) {
      tabs = qualifying.map((bundleData) => ({
        title: titleFor(bundleData),
        panel: buildBundlePanel(bundleData, true)
      }));
    } else {
      const tileTemplate = this.tileTemplate;
      const quickAddTemplate = this.quickAddTemplate;
      const arrowsTemplate = this.arrowsTemplate;
      if (!tileTemplate || !quickAddTemplate || !arrowsTemplate)
        return;
      tabs = qualifying.map((bundleData) => ({
        title: titleFor(bundleData),
        panel: buildSimpleTilePanel(tileTemplate, quickAddTemplate, bundleData.productList, arrowsTemplate, this.carouselType, this.compactArrows)
      }));
    }
    this.maybePrependOverrideTab(tabs, cartSkuSet);
    await renderTabs(rootEl, tabs, this.cartBasedTitle, cta);
  }
  /**
   * @param {Element} rootEl
   * @param {Set<string>} cartSkuSet
   * @param {string} reason
   * @param {{text: string, href: string}} [cta]
   */
  async renderCuratedTab(rootEl, cartSkuSet, reason, cta) {
    log(`source: curated collection, real new-product-card markup (reason: ${reason})`);
    await renderTabs(rootEl, []);
    if (!this.curatedContainer) {
      console.error("quiz-recommendations: missing curated container");
      return;
    }
    await revealCuratedTab(this.curatedContainer, this.curatedPristineSlides, cartSkuSet, this.maxSlots, cta, this.carouselType, this.compactArrows);
    log("curated dedupe:", {
      rawCount: this.curatedPristineSlides.length,
      containerHidden: this.curatedContainer.classList.contains("hidden")
    });
  }
};
customElements.define("quiz-recommendations", QuizRecommendations);

// src/js/customElements/quiz-recommendations-bundle.js
var QuizRecommendationBundle = class extends HTMLElement {
  constructor() {
    super();
    this.cartUpdateUnsubscriber = null;
  }
  // NOTE: this element is always created dynamically via document.createElement()
  // (quiz-recommendations-ui.js's buildBundlePanel), which sets `dataset.*` AFTER
  // createElement() returns - i.e. after the constructor has already run. Reading
  // this.dataset here (rather than in connectedCallback) would see stale/empty
  // values, unlike <complementary-regimen>, which is always parsed from static
  // Liquid-rendered HTML with its data-* attributes already present pre-parse.
  readDatasetConfig() {
    this.products = JSON.parse(this.dataset.products || "[]");
    this.discounts = JSON.parse(this.dataset.discounts || "[]");
    this.showDiscountBanner = this.dataset.showDiscountBanner === "true";
    this.anchorSku = this.dataset.anchorSku || "";
    this.anchorName = this.dataset.anchorName || "";
    this.productsByVariantId = this.products.reduce((acc, product) => {
      acc[product.variantId] = product;
      return acc;
    }, {});
  }
  buildRegimenWithQuantities(cartData) {
    const qtyMap = {};
    (cartData?.items ?? []).forEach((item) => {
      qtyMap[item.variant_id] = (qtyMap[item.variant_id] || 0) + item.quantity;
    });
    return this.products.map((product) => ({
      ...product,
      quantityInCart: qtyMap[product.variantId] || 0
    }));
  }
  computeDiscount(effectiveCount) {
    return computeApplicableDiscountForCount(this.discounts, effectiveCount);
  }
  updateProductTilesDisplay() {
    updateProductTiles(Array.from(this.productTiles), this.regimen, this.anchorSku, false);
  }
  updateATC() {
    const selectedCheckboxes = getSelectedProducts(this.checkboxList);
    updateAddToCartButtonState(
      this.addToCartButton,
      selectedCheckboxes,
      this.regimen,
      "Select items to add",
      this.anchorSku,
      false
    );
    const addingPrice = calculateSelectedProductsPrice(selectedCheckboxes);
    const inCartPrice = calculateInCartPrice(this.regimen);
    const discountInfo = { computeDiscount: (count) => this.computeDiscount(count), formatPrice: formatPrice2 };
    updateTotalPrice(
      this.priceElements,
      addingPrice,
      inCartPrice,
      selectedCheckboxes,
      this.regimen,
      this.regimen.length,
      discountInfo
    );
    if (this.showDiscountBanner && this.discountBannerEl) {
      const uniqueInCart = this.regimen.filter((product) => product.quantityInCart > 0).length;
      const effectiveCount = uniqueInCart + selectedCheckboxes.length;
      updateDiscountBanner(this.discountBannerEl, this.discounts, effectiveCount, discountInfo);
    }
  }
  async handleAddToCart() {
    showLoadingUI(this.addToCartButton, this.atcLoadingSpinner);
    try {
      const selectedCheckboxes = getSelectedProducts(this.checkboxList);
      const variantArray = createAddProductPayload(
        selectedCheckboxes,
        this.productsByVariantId,
        this.anchorSku,
        `Pair with ${this.anchorName}`,
        "",
        this.discounts
      );
      addItemsToCart(variantArray);
    } catch (err) {
      hideLoadingUI(this.addToCartButton, this.atcLoadingSpinner);
      console.error("quiz-recommendation-bundle: failed to add items to cart", err);
    }
  }
  addEventListeners() {
    this.checkboxList.forEach((checkbox) => checkbox.addEventListener("change", this.updateATC.bind(this)));
    this.addToCartButton?.addEventListener("click", this.handleAddToCart.bind(this));
  }
  async connectedCallback() {
    this.readDatasetConfig();
    const panelTemplate = document.querySelector("template[data-quiz-recommendations-bundle-panel]");
    const tileTemplate = document.querySelector("template[data-quiz-recommendations-bundle-tile]");
    if (!panelTemplate || !tileTemplate) {
      console.error("quiz-recommendation-bundle: missing bundle-panel/tile template");
      this.style.display = "none";
      return;
    }
    this.appendChild(panelTemplate.content.cloneNode(true));
    const tileList = this.querySelector("[data-tile-list]");
    this.products.forEach(() => {
      tileList.appendChild(tileTemplate.content.cloneNode(true));
    });
    this.productTiles = this.querySelectorAll(".complementary_regimen__product_tile");
    this.checkboxList = this.querySelectorAll("input[type='checkbox']");
    this.addToCartButton = this.querySelector("[data-complementary-regimen-atc-button]");
    this.atcLoadingSpinner = this.querySelector(".loading__spinner_spinner");
    this.discountBannerEl = this.querySelector("[data-discount-banner]");
    this.priceElements = {
      elPriceLabel: this.querySelector("[data-total-price-label]"),
      elPriceValue: this.querySelector("[data-total-price-value]"),
      elPriceOriginal: this.querySelector("[data-total-price-original]"),
      elPriceDiscounted: this.querySelector("[data-total-price-discounted]"),
      elPriceSavings: this.querySelector("[data-total-price-savings]"),
      elBundle: this.querySelector("[data-total-price-bundle]"),
      elBundleLabel: this.querySelector("[data-total-price-bundle-label]"),
      elBundleValue: this.querySelector("[data-total-price-bundle-value]"),
      elBundleOriginal: this.querySelector("[data-total-price-bundle-original]"),
      elBundleDiscounted: this.querySelector("[data-total-price-bundle-discounted]"),
      elBundleSavings: this.querySelector("[data-total-price-bundle-savings]")
    };
    if (!this.showDiscountBanner) {
      this.discountBannerEl?.classList.add("hidden");
    }
    try {
      const cartData = await getCartData();
      this.regimen = this.buildRegimenWithQuantities(cartData);
      this.updateProductTilesDisplay();
      this.updateATC();
      this.addEventListeners();
      this.cartUpdateUnsubscriber = subscribe(
        PUB_SUB_EVENTS.cartMutationComplete,
        async (payload) => {
          hideLoadingUI(this.addToCartButton, this.atcLoadingSpinner);
          this.regimen = this.buildRegimenWithQuantities(payload.cartData);
          this.updateProductTilesDisplay();
          this.updateATC();
        },
        "quiz-recommendation-bundle"
      );
    } catch (err) {
      this.style.display = "none";
      console.error("quiz-recommendation-bundle: failed to initialize", err);
    }
  }
  disconnectedCallback() {
    this.checkboxList?.forEach((checkbox) => checkbox.removeEventListener("change", this.updateATC.bind(this)));
    this.addToCartButton?.removeEventListener("click", this.handleAddToCart.bind(this));
    if (this.cartUpdateUnsubscriber)
      this.cartUpdateUnsubscriber();
  }
};
customElements.define("quiz-recommendation-bundle", QuizRecommendationBundle);

// src/js/customElements/badge-discounts-constants.js
var API_URL_PATH_BADGE_DISCOUNTS = `/api/discounts/badges`;
var LOCAL_STORAGE_KEY_BADGE_DISCOUNTS = "muradBadgeDiscounts";
var BADGE_DISCOUNTS_STALE_AFTER = 1e3 * 60 * 5;

// src/js/customElements/badge-discounts-data.js
function getCachedBadgeDiscounts(skinQuizDomain) {
  let cached;
  try {
    cached = getLocalStorage(LOCAL_STORAGE_KEY_BADGE_DISCOUNTS);
  } catch {
    return null;
  }
  if (!cached || cached.skinQuizDomain !== skinQuizDomain)
    return null;
  const { discounts, lastModified } = cached;
  const timestamp = Date.parse(lastModified);
  if (!Number.isFinite(timestamp) || timestamp > Date.now())
    return null;
  if (hasTimeElapsed(lastModified, BADGE_DISCOUNTS_STALE_AFTER)) {
    return null;
  }
  return Array.isArray(discounts) ? discounts : null;
}
function cacheBadgeDiscounts(discounts = [], skinQuizDomain) {
  try {
    setLocalStorage(LOCAL_STORAGE_KEY_BADGE_DISCOUNTS, {
      discounts,
      skinQuizDomain,
      lastModified: (/* @__PURE__ */ new Date()).toISOString()
    });
  } catch {
  }
}
var loadedBadgeDiscounts = null;
function getLoadedBadgeDiscounts() {
  return loadedBadgeDiscounts;
}
function setLoadedBadgeDiscounts(discounts = []) {
  loadedBadgeDiscounts = Array.isArray(discounts) ? discounts : [];
}
var pageRequests = /* @__PURE__ */ new Map();
function loadBadgeDiscounts(skinQuizDomain) {
  const domain = skinQuizDomain.replace(/\/$/, "");
  if (!pageRequests.has(domain)) {
    const request = (async () => {
      const cached = getCachedBadgeDiscounts(domain);
      if (cached)
        return cached;
      const res = await fetchBadgeDiscounts(domain);
      if (res.success !== true || !Array.isArray(res.data?.discounts)) {
        throw new Error("Invalid badge discounts response");
      }
      cacheBadgeDiscounts(res.data.discounts, domain);
      return res.data.discounts;
    })();
    pageRequests.set(
      domain,
      request.finally(() => pageRequests.delete(domain))
    );
  }
  return pageRequests.get(domain);
}
async function fetchBadgeDiscounts(skinQuizDomain) {
  if (!skinQuizDomain) {
    throw new Error("No skin quiz domain provided");
  }
  try {
    const res = await fetch(
      `${skinQuizDomain}${API_URL_PATH_BADGE_DISCOUNTS}`,
      {
        cache: "no-store",
        signal: AbortSignal.timeout(1e4)
      }
    );
    const json = await res.json();
    if (res.ok) {
      return json;
    } else if (json.message) {
      throw new Error(json.message);
    } else {
      throw new Error(
        `Error retrieving badge discounts: ${res.status} - ${res.statusText}`
      );
    }
  } catch (err) {
    throw err;
  }
}

// src/js/customElements/badge-discounts.js
var BadgeDiscounts = class extends HTMLElement {
  constructor() {
    super();
    this.skinQuizDomain = this.dataset["skinQuizDomain"];
    this.discounts = [];
  }
  connectedCallback() {
    this.onResume = () => {
      if (document.visibilityState !== "hidden")
        this.refresh();
    };
    window.addEventListener("pageshow", this.onResume);
    this.refresh();
  }
  disconnectedCallback() {
    clearTimeout(this.refreshTimer);
    window.removeEventListener("pageshow", this.onResume);
  }
  async refresh() {
    if (this.refreshing)
      return;
    this.refreshing = true;
    clearTimeout(this.refreshTimer);
    if (!this.skinQuizDomain) {
      setLoadedBadgeDiscounts([]);
    }
    try {
      if (this.skinQuizDomain) {
        this.discounts = await loadBadgeDiscounts(this.skinQuizDomain);
      }
    } catch (err) {
      console.error("Failed to load badge discounts:", err);
      this.discounts = [];
    }
    this.refreshing = false;
    if (!this.isConnected)
      return;
    setLoadedBadgeDiscounts(this.discounts);
    this.dispatchEvent(
      new CustomEvent("badge-discounts:loaded", {
        bubbles: true,
        detail: { discounts: this.discounts }
      })
    );
    if (this.skinQuizDomain) {
      this.refreshTimer = setTimeout(
        () => this.onResume(),
        BADGE_DISCOUNTS_STALE_AFTER
      );
    }
  }
};
customElements.define("badge-discounts", BadgeDiscounts);

// src/js/customElements/badge-discounts-matching.js
function isProductEligibleForDiscount(discount, product) {
  const eligibility = discount?.eligibility;
  if (!eligibility || !product)
    return false;
  switch (eligibility.scope) {
    case "all":
      return true;
    case "products":
      return Array.isArray(eligibility.productIds) && eligibility.productIds.includes(product.id);
    case "collections":
      return Array.isArray(eligibility.collectionIds) && (product.collectionIds || []).some(
        (id) => eligibility.collectionIds.includes(id)
      );
    case "unknown":
    default:
      return false;
  }
}
function getEligibleDiscountsForProduct(discounts = [], product) {
  if (!Array.isArray(discounts))
    return [];
  return discounts.filter(
    (discount) => isProductEligibleForDiscount(discount, product)
  );
}

// src/js/customElements/badge-discounts-pricing.js
function isPercentageDiscount(discount) {
  return discount?.valueType === "percentage" && typeof discount.value === "number" && Number.isFinite(discount.value) && discount.value > 0 && discount.value <= 100;
}
function discountedPrice(price, discount) {
  if (!Number.isInteger(price) || price < 0 || !isPercentageDiscount(discount))
    return null;
  return Math.round(price * (100 - discount.value) / 100);
}
function formatPromoMoney(cents, currency, locale, forceTwoDecimals = false) {
  const options = { style: "currency", currency };
  if (forceTwoDecimals) {
    options.minimumFractionDigits = 2;
    options.maximumFractionDigits = 2;
  }
  return new Intl.NumberFormat(locale || "en-US", options).format(cents / 100);
}
function getPromoPurchasePrice(variant, purchaseType, sellingPlanId) {
  if (purchaseType !== "subscription")
    return variant?.price;
  const allocation = variant?.allocations?.find(
    (plan) => String(plan.id) === String(sellingPlanId)
  );
  return allocation?.price;
}

// src/js/customElements/badge-discounts-message.js
var TOKEN_PATTERN = /\[(amount|code|title)\]/g;
function formatDiscountAmount(discount, locale, fallbackCurrency) {
  if (!Number.isFinite(discount?.value))
    return "";
  if (discount.valueType === "percentage")
    return `${discount.value}%`;
  const currency = discount.currencyCode || fallbackCurrency;
  if (!currency)
    return "";
  return formatPromoMoney(
    Math.round(discount.value * 100),
    currency,
    locale,
    true
  );
}
function composeBadgeMessage(template, discount, options = {}) {
  if (typeof template !== "string" || !template.trim())
    return "";
  const values = {
    amount: formatDiscountAmount(discount, options.locale, options.currency),
    code: discount?.code || "",
    title: discount?.title || ""
  };
  return template.replace(TOKEN_PATTERN, (token, name) => values[name]).replace(/\s+/g, " ").trim();
}

// src/js/customElements/discount-badge.js
var DiscountBadge = class extends HTMLElement {
  connectedCallback() {
    const productId = Number(this.dataset.productId);
    if (!productId)
      return;
    let collectionIds = [];
    try {
      collectionIds = JSON.parse(this.dataset.collectionIds || "[]");
    } catch (err) {
      collectionIds = [];
    }
    this.product = { id: productId, collectionIds };
    this.onPurchaseChange = (event) => {
      if (event?.target?.closest && !event.target.closest("subscription-widget"))
        return;
      this.render(getLoadedBadgeDiscounts() || []);
    };
    this.onVariantChange = ({ detail }) => {
      if (this.pdpData?.variants.some(
        (variant) => variant.id === Number(detail?.variantId)
      )) {
        this.dataset.variantId = String(detail.variantId);
        this.onPurchaseChange();
      }
    };
    if (this.dataset.pageType === "pdp") {
      document.addEventListener("murad:variant:change", this.onVariantChange);
      document.addEventListener("change", this.onPurchaseChange);
      document.addEventListener("input", this.onPurchaseChange);
    }
    this.onDiscountsLoaded = (event) => this.render(event.detail?.discounts || []);
    document.addEventListener("badge-discounts:loaded", this.onDiscountsLoaded);
    const loaded = getLoadedBadgeDiscounts();
    if (loaded) {
      this.render(loaded);
    }
  }
  disconnectedCallback() {
    if (this.onDiscountsLoaded) {
      document.removeEventListener(
        "badge-discounts:loaded",
        this.onDiscountsLoaded
      );
    }
    document.removeEventListener("murad:variant:change", this.onVariantChange);
    document.removeEventListener("change", this.onPurchaseChange);
    document.removeEventListener("input", this.onPurchaseChange);
  }
  get pdpData() {
    if (this.dataset.pageType !== "pdp")
      return null;
    try {
      return JSON.parse(
        document.querySelector(`[data-promo-product="${this.product.id}"]`)?.textContent || "null"
      );
    } catch {
      return null;
    }
  }
  get purchase() {
    const data = this.pdpData;
    if (!data)
      return null;
    const widget = document.querySelector(
      `subscription-widget[data-product-form-id="${data.formId}"]`
    );
    const formVariant = document.querySelector("input[data-main-cta]")?.value;
    const variantId = Number(formVariant || this.dataset.variantId);
    const variant = data.variants.find((item) => item.id === variantId);
    const select = widget?.querySelector("select");
    const planId = select?.value || variant?.allocations[0]?.id;
    const subscription = widget ? Boolean(widget.querySelector('input[value="subscription"]:checked')) : data.defaultPurchaseType === "subscription";
    return { variant, planId, subscription };
  }
  eligibleDiscount(discounts) {
    if (!Array.isArray(discounts) || discounts.length !== 1)
      return null;
    const purchase = this.purchase;
    if (purchase && !purchase.variant)
      return null;
    const variant = purchase?.variant;
    if (variant ? variant.compareAtPrice > variant.price : Number(this.dataset.compareAtPrice) > Number(this.dataset.variantPrice))
      return null;
    const [discount] = getEligibleDiscountsForProduct(discounts, this.product);
    if (discount?.type !== "code" || typeof discount.code !== "string" || !discount.code.trim() || typeof discount.description !== "string" || !discount.description.trim())
      return null;
    if (discount.valueType === "percentage" && !isPercentageDiscount(discount))
      return null;
    return discount;
  }
  render(discounts) {
    this.classList.add("hidden");
    this.removeAttribute("data-discount-badge-active");
    this.textContent = "";
    const discount = this.eligibleDiscount(discounts);
    if (!discount)
      return;
    const useShortDescription = "shortDescription" in this.dataset;
    const useStrikethrough = this.dataset.style === "strikethrough" && isPercentageDiscount(discount) && useShortDescription;
    const text = composeBadgeMessage(
      useStrikethrough ? this.dataset.messageStrikethrough : this.dataset.message,
      discount,
      {
        locale: document.documentElement.lang,
        currency: this.dataset.currency
      }
    ) || (useStrikethrough ? discount.description_with_strikethrough_price : useShortDescription && discount.description_short ? discount.description_short : discount.description);
    if (!text)
      return;
    this.textContent = text;
    this.setAttribute("data-discount-badge-active", "");
    this.classList.remove("hidden");
  }
};
customElements.define("discount-badge", DiscountBadge);

// src/js/customElements/promo-price.js
var PromoPrice = class extends DiscountBadge {
  render(discounts) {
    const fallback = this.querySelector("[data-promo-price-fallback]");
    const output = this.querySelector("[data-promo-price-output]");
    if (!fallback || !output)
      return;
    this.originalFallback ??= fallback.innerHTML;
    const purchase = this.purchase;
    const purchaseType = this.dataset.purchaseType === "selected" ? purchase?.subscription ? "subscription" : "one-time" : this.dataset.purchaseType;
    const price = purchase ? getPromoPurchasePrice(purchase.variant, purchaseType, purchase.planId) : Number(this.dataset.basePrice);
    const format = (cents) => formatPromoMoney(
      cents,
      this.dataset.currency,
      document.documentElement.lang,
      this.dataset.pageType !== "pdp"
    );
    if (purchase && Number.isInteger(price) && (this.dataset.purchaseType === "selected" || purchase.variant?.id !== Number(this.dataset.initialVariantId) || price !== Number(this.dataset.basePrice))) {
      const comparePrice = purchaseType === "subscription" ? purchase.variant.price : purchase.variant.compareAtPrice;
      fallback.replaceChildren();
      if (this.dataset.mode === "price" && comparePrice > price) {
        const compare = document.createElement("s");
        compare.textContent = format(comparePrice);
        fallback.append(compare, " ");
      }
      fallback.append(format(price));
      this.fallbackUpdated = true;
    } else if (this.fallbackUpdated) {
      fallback.innerHTML = this.originalFallback;
      this.fallbackUpdated = false;
    }
    const discount = this.eligibleDiscount(discounts);
    const discounted = this.dataset.style === "strikethrough" ? discountedPrice(price, discount) : null;
    const active = discounted !== null;
    fallback.classList.toggle("hidden", active);
    output.classList.toggle("hidden", !active);
    output.replaceChildren();
    if (!active)
      return;
    if (this.dataset.mode === "price") {
      const compare = document.createElement("s");
      compare.textContent = format(price);
      output.append(compare, " ");
    }
    const finalPrice = document.createElement("span");
    finalPrice.textContent = format(discounted);
    output.append(finalPrice);
  }
};
customElements.define("promo-price", PromoPrice);

// src/js/customElements/build-your-own-box-constants.js
var STOREFRONT_ENDPOINT = "/api/2026-04/graphql.json";
var METAFIELD_NAMESPACE = "$app";
var CHECKOUT_CONFIGURATION_KEY = "checkout-configuration";
var BYOB_CONFIG_CACHE_TTL_MS = 1e3 * 60 * 5;
var BYOB_EDIT_QUERY_PARAM = "byob_edit";
var BYOB_TEXT_DEFAULTS = {
  // picker
  all: "All",
  add: "+ Add",
  added: "Added",
  bundle_full: "Bundle full",
  view: "View",
  // summary panel
  your_bundle: "Your Bundle",
  subtotal: "Subtotal",
  bundle_quantity: "Bundle quantity",
  empty_helper: "Add products to start saving.",
  empty_title: "Your bundle is empty",
  empty_body: "Add your first product from the left to unlock tiered savings.",
  // add-to-cart button
  add_bundle_to_cart: "Add bundle to cart",
  update_bundle: "Update bundle",
  add_items_to_start: "Add items to start",
  // [count] is the shortfall against the lowest discount tier's item threshold.
  add_more_to_continue: "Add [count] more to continue",
  // edit mode
  editing_bundle: "You're editing a bundle already in your cart.",
  // Shown when the cart already holds as many bundles as are allowed.
  bundle_limit_reached: "You've reached the limit of bundles per order. Edit the bundle already in your cart to make changes.",
  start_new_bundle: "Start a new bundle",
  // slots + itemised list
  remove_item: "Remove [item]",
  bundle_discount: "Bundle discount ([percentage]%)",
  // tier progress
  progress_start: "Start",
  progress_tier: "[percentage]%",
  progress_tier_gift: "\u{1F381} [percentage]%",
  // gift
  gift_generic: "a free gift",
  gift_value: "[gift] ([value] value)",
  gift_locked: "Add [count] products to unlock [gift]",
  gift_unlocked: "Gift unlocked! [gift]",
  // savings
  savings_line: "You saved [amount] - [percentage]% off",
  savings_line_gift: "You saved [amount] - [percentage]% off + your free gift",
  savings_recap: "You're saving [amount] with your bundle",
  savings_recap_gift: "You're saving [amount] with your bundle + gift",
  mobile_summary: "[count] of [max] added",
  mobile_count: "([count]/[max])",
  mobile_unlocked: "[percentage]% unlocked",
  // cart line item (server-rendered by cart-item-tile-byob-group.liquid)
  cart_title: "Your Bundle",
  edit: "Edit",
  free: "Free",
  // Shown against the gift line when the bundle quantity has outgrown it.
  gift_limit: "Limit [count]"
};
function byobText(key, replacements = {}) {
  const override = window.byobStrings?.[key];
  const hasOverride = typeof override === "string" && override.trim() && !override.includes("Translation missing");
  const template = hasOverride ? override : BYOB_TEXT_DEFAULTS[key] ?? "";
  return Object.entries(replacements).reduce(
    (text, [token, value]) => text.split(`[${token}]`).join(String(value)),
    template
  );
}

// src/js/customElements/build-your-own-box-data.js
function isFresh(lastFetchedAt) {
  if (!lastFetchedAt)
    return false;
  const age = Date.now() - new Date(lastFetchedAt).getTime();
  return Number.isFinite(age) && age >= 0 && age < BYOB_CONFIG_CACHE_TTL_MS;
}
function variantIdFromGid(gid) {
  return typeof gid === "string" ? gid.split("/").pop() : gid;
}
function normalizeConfig(config) {
  if (!config)
    return null;
  return {
    ...config,
    eligibleVariantIds: (config.eligibleVariantIds ?? []).map(variantIdFromGid),
    ineligibleVariantIds: (config.ineligibleVariantIds ?? []).map(variantIdFromGid),
    tiers: (config.tiers ?? []).map((tier) => ({
      ...tier,
      giftVariantIds: (tier.giftVariantIds ?? []).map(variantIdFromGid)
    }))
  };
}
function readStoredByobConfig() {
  const cached = getLocalStorage(LOCAL_STORAGE_KEY_BYOB_CONFIG);
  return normalizeConfig(cached?.config ?? null);
}
function getCachedByobConfig() {
  const cached = getLocalStorage(LOCAL_STORAGE_KEY_BYOB_CONFIG);
  if (!cached || !isFresh(cached.lastFetchedAt)) {
    return null;
  }
  return normalizeConfig(cached.config ?? null);
}
function cacheByobConfig(config) {
  setLocalStorage(LOCAL_STORAGE_KEY_BYOB_CONFIG, {
    config,
    lastFetchedAt: (/* @__PURE__ */ new Date()).toISOString()
  });
}
function storefrontHeaders(storefrontAccessToken) {
  const headers = { "Content-Type": "application/json" };
  if (storefrontAccessToken) {
    headers["X-Shopify-Storefront-Access-Token"] = storefrontAccessToken;
  }
  return headers;
}
async function fetchByobConfig(storefrontAccessToken) {
  const headers = storefrontHeaders(storefrontAccessToken);
  const query = `
    query ByobCheckoutConfiguration($namespace: String!, $key: String!) {
      shop {
        metafield(namespace: $namespace, key: $key) {
          value
        }
      }
    }
  `;
  const res = await fetch(STOREFRONT_ENDPOINT, {
    method: "POST",
    headers,
    body: JSON.stringify({
      query,
      variables: { namespace: METAFIELD_NAMESPACE, key: CHECKOUT_CONFIGURATION_KEY }
    })
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch BYOB configuration: ${res.status}`);
  }
  const json = await res.json();
  const rawValue = json?.data?.shop?.metafield?.value;
  if (!rawValue) {
    throw new Error("BYOB checkout-configuration metafield is missing or empty");
  }
  return JSON.parse(rawValue);
}
async function fetchGiftProducts(variantIds, storefrontAccessToken) {
  const ids = [...new Set(variantIds)].filter(Boolean);
  if (!ids.length)
    return /* @__PURE__ */ new Map();
  const query = `
    query ByobGiftVariants($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on ProductVariant {
          id
          title
          image { url altText }
          product { title featuredImage { url altText } }
        }
      }
    }
  `;
  try {
    const res = await fetch(STOREFRONT_ENDPOINT, {
      method: "POST",
      headers: storefrontHeaders(storefrontAccessToken),
      body: JSON.stringify({
        query,
        variables: { ids: ids.map((id) => `gid://shopify/ProductVariant/${id}`) }
      })
    });
    if (!res.ok) {
      throw new Error(`Failed to fetch BYOB gift products: ${res.status}`);
    }
    const json = await res.json();
    return new Map(
      (json?.data?.nodes ?? []).filter(Boolean).map((node) => [
        variantIdFromGid(node.id),
        {
          title: node.product?.title || node.title,
          imageUrl: node.image?.url || node.product?.featuredImage?.url || "",
          imageAlt: node.image?.altText || node.product?.featuredImage?.altText || ""
        }
      ])
    );
  } catch (err) {
    console.error("Unable to resolve BYOB gift products", err);
    return /* @__PURE__ */ new Map();
  }
}
async function fetchVariantAvailability(variantIds, storefrontAccessToken) {
  const ids = [...new Set(variantIds)].filter(Boolean);
  if (!ids.length)
    return /* @__PURE__ */ new Map();
  const query = `
    query ByobVariantAvailability($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on ProductVariant {
          id
          availableForSale
          price { amount }
          image { url }
          product { title featuredImage { url } }
        }
      }
    }
  `;
  try {
    const res = await fetch(STOREFRONT_ENDPOINT, {
      method: "POST",
      headers: storefrontHeaders(storefrontAccessToken),
      body: JSON.stringify({
        query,
        variables: { ids: ids.map((id) => `gid://shopify/ProductVariant/${id}`) }
      })
    });
    if (!res.ok) {
      throw new Error(`Failed to fetch BYOB variant availability: ${res.status}`);
    }
    const json = await res.json();
    if (json?.errors?.length) {
      throw new Error(`BYOB variant availability query failed: ${json.errors[0]?.message}`);
    }
    const found = new Map(
      (json?.data?.nodes ?? []).filter((node) => node?.id).map((node) => [variantIdFromGid(node.id), node])
    );
    return new Map(
      ids.map((variantId) => {
        const node = found.get(variantId);
        return [
          variantId,
          {
            available: Boolean(node?.availableForSale),
            product: node && {
              variantId,
              title: node.product?.title ?? "",
              priceCents: Math.round(Number(node.price?.amount ?? 0) * 100),
              imageUrl: node.image?.url || node.product?.featuredImage?.url || ""
            }
          }
        ];
      })
    );
  } catch (err) {
    console.error("Unable to check BYOB variant availability", err);
    return null;
  }
}
async function resolveByobConfig(storefrontAccessToken) {
  const cached = getCachedByobConfig();
  if (cached) {
    return cached;
  }
  try {
    const config = await fetchByobConfig(storefrontAccessToken);
    cacheByobConfig(config);
    return normalizeConfig(config);
  } catch (err) {
    console.error("Unable to resolve BYOB configuration", err);
    return null;
  }
}

// src/js/customElements/build-your-own-box-pricing.js
function isEligibleVariant(config, variantId) {
  const eligibleSet = new Set(config.eligibleVariantIds ?? []);
  const ineligibleSet = new Set(config.ineligibleVariantIds ?? []);
  return config.eligibilityMode === "deny" ? !ineligibleSet.has(variantId) : eligibleSet.has(variantId);
}
function pickTier(tiers, uniqueEligibleCount) {
  const sorted = [...tiers ?? []].sort(
    (a, b) => a.minUniqueItems - b.minUniqueItems
  );
  let matched = null;
  for (const tier of sorted) {
    if (uniqueEligibleCount >= tier.minUniqueItems) {
      matched = tier;
    }
  }
  return matched;
}
function getUniqueEligibleVariantIds(config, variantIds) {
  const unique = /* @__PURE__ */ new Set();
  for (const variantId of variantIds) {
    if (isEligibleVariant(config, variantId)) {
      unique.add(variantId);
    }
  }
  return unique;
}
function evaluateBundle(config, variantIds, variantCosts = /* @__PURE__ */ new Map()) {
  const uniqueEligibleVariantIds = getUniqueEligibleVariantIds(config, variantIds);
  const tier = pickTier(config.tiers, uniqueEligibleVariantIds.size);
  let aggregateEligibleCost = 0;
  for (const variantId of uniqueEligibleVariantIds) {
    aggregateEligibleCost += variantCosts.get(variantId) ?? 0;
  }
  if (!tier) {
    return {
      uniqueEligibleVariantIds,
      tier: null,
      aggregateEligibleCost,
      discountAmount: 0,
      giftVariantIds: []
    };
  }
  return {
    uniqueEligibleVariantIds,
    tier,
    aggregateEligibleCost,
    discountAmount: aggregateEligibleCost * tier.percentage / 100,
    giftVariantIds: tier.giftVariantIds ?? []
  };
}

// src/js/customElements/build-your-own-box-cart.js
function generateBoxId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `byob-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
function createBoxLineItemPayload(variantId, boxId, editUrl, quantity) {
  return {
    id: Number(variantId),
    quantity,
    selling_plan: null,
    properties: {
      [PRODUCT_PROPERTY_KEYS.isByob]: "true",
      [PRODUCT_PROPERTY_KEYS.byobBoxId]: boxId,
      [PRODUCT_PROPERTY_KEYS.byobEditUrl]: editUrl
    }
  };
}
function getBoxLines(cartData, boxId) {
  return (cartData.items ?? []).filter(
    (item) => item.properties?.[PRODUCT_PROPERTY_KEYS.byobBoxId] === boxId && item.properties?.[PRODUCT_PROPERTY_KEYS.isByob] === "true"
  );
}
async function addBundleToCart(selectedVariantIds, { boxId, editUrl, quantity = 1, isEditing }) {
  if (!selectedVariantIds.length)
    return;
  const selected = new Set(selectedVariantIds.map(String));
  const cartData = isEditing ? await getCartData(true) : { items: [] };
  const boxLines = getBoxLines(cartData, boxId);
  const updates = {};
  const persisting = /* @__PURE__ */ new Set();
  for (const line of boxLines) {
    const variantId = String(line.variant_id);
    const keep = selected.has(variantId);
    updates[line.key] = keep ? quantity : 0;
    if (keep)
      persisting.add(variantId);
  }
  const newItems = [...selected].filter((variantId) => !persisting.has(variantId)).map((variantId) => createBoxLineItemPayload(variantId, boxId, editUrl, quantity));
  const operations = [];
  if (Object.keys(updates).length) {
    operations.push({ action: "update", payload: { updates } });
  }
  if (newItems.length) {
    operations.push({ action: "add", payload: { items: newItems } });
  }
  cartMutateSequence(operations);
}
function getBoxIdsFromCart(cartData) {
  const boxIds = (cartData?.items ?? []).filter((item) => item.properties?.[PRODUCT_PROPERTY_KEYS.isByob] === "true").map((item) => item.properties?.[PRODUCT_PROPERTY_KEYS.byobBoxId]).filter(Boolean);
  return [...new Set(boxIds)];
}
function getBoxSelectionFromCart(cartData, boxId) {
  if (!boxId || !cartData)
    return { variantIds: [], quantity: 1 };
  const boxLines = getBoxLines(cartData, boxId);
  return {
    variantIds: boxLines.map((item) => item.variant_id?.toString()),
    quantity: boxLines[0]?.quantity ?? 1
  };
}

// src/js/customElements/build-your-own-box-ui.js
function formatMoney(cents) {
  const currency = window.Shopify?.currency?.active || "USD";
  return formatPromoMoney(cents, currency, document.documentElement.lang);
}
function renderTileStates(tiles, selectedVariantIds, isFull) {
  tiles.forEach((tile) => {
    const variantId = tile.dataset.variantId;
    const action = tile.querySelector("[data-byob-tile-action]");
    if (!action)
      return;
    const isSelected = selectedVariantIds.has(variantId);
    tile.classList.toggle("byob-tile--selected", isSelected);
    if (isSelected) {
      action.textContent = byobText("added");
      action.disabled = false;
      action.setAttribute("aria-pressed", "true");
    } else if (isFull) {
      action.textContent = byobText("bundle_full");
      action.disabled = true;
      action.setAttribute("aria-pressed", "false");
    } else {
      action.textContent = byobText("add");
      action.disabled = false;
      action.setAttribute("aria-pressed", "false");
    }
  });
}
function filterTilesByCategory(tiles, category) {
  tiles.forEach((tile) => {
    if (category === "all") {
      tile.hidden = false;
      return;
    }
    const categories = (tile.dataset.categories || "").split(",").filter(Boolean);
    tile.hidden = !categories.includes(category);
  });
}
function progressDenominator(maxItems, tiers) {
  const topTierThreshold = tiers.reduce(
    (max, tier) => Math.max(max, tier.minUniqueItems),
    0
  );
  return Math.max(maxItems || 0, topTierThreshold, 1);
}
var TICK_REACHED_CLASS = "text-text-primary font-medium";
function renderProgressTicks(container, tiers, selectedCount) {
  if (!container)
    return;
  container.innerHTML = "";
  const start = document.createElement("span");
  start.textContent = byobText("progress_start");
  if (selectedCount > 0)
    start.className = TICK_REACHED_CLASS;
  container.append(start);
  tiers.slice().sort((a, b) => a.minUniqueItems - b.minUniqueItems).forEach((tier, index, sorted) => {
    const tick = document.createElement("span");
    const isTopTier = index === sorted.length - 1;
    const hasGift = isTopTier && tier.giftVariantIds?.length;
    tick.textContent = byobText(hasGift ? "progress_tier_gift" : "progress_tier", {
      count: tier.minUniqueItems,
      percentage: tier.percentage
    });
    if (selectedCount >= tier.minUniqueItems)
      tick.className = TICK_REACHED_CLASS;
    container.append(tick);
  });
}
function describeGift(giftVariantIds, giftProducts, giftDisplayValue) {
  const names = (giftVariantIds ?? []).map((variantId) => giftProducts?.get(variantId)?.title).filter(Boolean);
  const gift = names.length ? names.join(" + ") : byobText("gift_generic");
  return giftDisplayValue ? byobText("gift_value", { gift, value: formatMoney(giftDisplayValue * 100) }) : gift;
}
function applyGiftImage(container, giftVariantIds, giftProducts) {
  const image = container.querySelector("[data-byob-gift-image]");
  if (!image)
    return;
  const product = (giftVariantIds ?? []).map((variantId) => giftProducts?.get(variantId)).find((entry) => entry?.imageUrl);
  image.hidden = !product;
  if (product) {
    image.src = product.imageUrl;
    image.alt = product.imageAlt || product.title || "";
  }
}
function removeIcon(root) {
  return root.querySelector("[data-byob-remove-icon]")?.content?.firstElementChild?.cloneNode(true);
}
function renderSlots(root, { selectedProducts, maxItems }) {
  const container = root.querySelector("[data-byob-slots]");
  if (!container)
    return;
  const slotCount = Number.isFinite(maxItems) ? maxItems : selectedProducts.length;
  container.innerHTML = "";
  for (let index = 0; index < slotCount; index += 1) {
    const product = selectedProducts[index];
    container.append(product ? filledSlot(product, root) : emptySlot());
  }
}
function slotShell(extraClass = "", tagName = "div") {
  const slot = document.createElement(tagName);
  slot.className = `relative w-20 h-20 rounded-md overflow-hidden flex items-center justify-center ${extraClass}`;
  return slot;
}
function slotImage(src, alt) {
  const image = document.createElement("img");
  image.className = "w-full h-full object-cover";
  image.src = src;
  image.alt = alt;
  return image;
}
function filledSlot(product, root) {
  const slot = slotShell("bg-body cursor-pointer", "button");
  slot.type = "button";
  slot.dataset.byobRemoveVariant = product.variantId;
  slot.setAttribute("aria-label", byobText("remove_item", { item: product.title }));
  if (product.imageUrl)
    slot.append(slotImage(product.imageUrl, ""));
  const badge = document.createElement("span");
  badge.className = "absolute top-0 right-0 w-5 h-5 p-1 flex items-center justify-center bg-text-primary text-body rounded-bl-md";
  badge.setAttribute("aria-hidden", "true");
  const icon = removeIcon(root);
  if (icon)
    badge.append(icon);
  slot.append(badge);
  return slot;
}
function emptySlot() {
  return slotShell("border border-dashed border-border2 bg-body");
}
function renderItemList(root, { selectedProducts, evaluation, giftProducts }) {
  const container = root.querySelector("[data-byob-items]");
  if (!container)
    return;
  container.innerHTML = "";
  selectedProducts.forEach((product) => {
    container.append(
      itemRow({
        label: product.title,
        value: formatMoney(product.priceCents),
        variantId: product.variantId,
        root
      })
    );
  });
  if (evaluation.tier && evaluation.giftVariantIds.length) {
    const gift = evaluation.giftVariantIds.map((variantId) => giftProducts?.get(variantId)).find(Boolean);
    container.append(
      itemRow({
        label: gift?.title ?? byobText("gift_generic"),
        value: byobText("free"),
        strikeValue: evaluation.tier.giftDisplayValue ? formatMoney(evaluation.tier.giftDisplayValue * 100) : ""
      })
    );
  }
}
function itemRow({ label, value, strikeValue = "", variantId = "", root = null }) {
  const row = document.createElement("div");
  row.className = "flex items-baseline justify-between gap-2 body-copy-2 text-text-primary";
  const name = document.createElement("span");
  name.className = "min-w-0";
  name.textContent = label;
  const trailing = document.createElement("span");
  trailing.className = "flex items-baseline gap-2 shrink-0";
  if (strikeValue) {
    const struck = document.createElement("span");
    struck.className = "line-through text-text-primary/60";
    struck.textContent = strikeValue;
    trailing.append(struck);
  }
  const amount = document.createElement("span");
  amount.textContent = value;
  trailing.append(amount);
  if (variantId) {
    const remove = document.createElement("button");
    remove.type = "button";
    remove.dataset.byobRemoveVariant = variantId;
    remove.className = "w-4 h-4 p-[3px] flex items-center justify-center text-text-primary/60";
    remove.setAttribute("aria-label", byobText("remove_item", { item: label }));
    const icon = root && removeIcon(root);
    if (icon)
      remove.append(icon);
    trailing.append(remove);
  }
  row.append(name, trailing);
  return row;
}
function renderAddButtons(root, { selectedCount, minItems, isEditing, atBundleLimit, hasChanges = true }) {
  const meetsMinimum = selectedCount >= minItems && !atBundleLimit;
  root.querySelectorAll("[data-byob-atc-button]").forEach((btn) => {
    btn.disabled = !meetsMinimum || !hasChanges;
    const label = btn.querySelector("[data-byob-atc-label]");
    if (!label)
      return;
    if (atBundleLimit) {
      label.textContent = byobText("bundle_limit_reached");
    } else if (meetsMinimum) {
      label.textContent = byobText(isEditing ? "update_bundle" : "add_bundle_to_cart");
    } else if (!selectedCount) {
      label.textContent = byobText("add_items_to_start");
    } else {
      label.textContent = byobText("add_more_to_continue", {
        count: minItems - selectedCount
      });
    }
  });
}
function renderSummary(root, state) {
  const {
    selectedCount,
    maxItems,
    tiers,
    minItems = 1,
    quantity = 1,
    atBundleLimit = false,
    isEditing = false,
    hasChanges = true,
    evaluation,
    giftProducts,
    selectedProducts = []
  } = state;
  const hasSelection = selectedCount > 0;
  const denominator = progressDenominator(maxItems, tiers);
  const progressFraction = Math.min(selectedCount / denominator, 1);
  root.querySelector("[data-byob-empty-message]")?.toggleAttribute("hidden", hasSelection);
  root.querySelector("[data-byob-empty-state]")?.toggleAttribute("hidden", hasSelection);
  root.querySelector("[data-byob-totals]")?.toggleAttribute("hidden", !hasSelection);
  const fill = root.querySelector("[data-byob-progress-fill]");
  if (fill)
    fill.style.width = `${progressFraction * 100}%`;
  renderProgressTicks(root.querySelector("[data-byob-progress-ticks]"), tiers, selectedCount);
  renderSlots(root, { selectedProducts, maxItems });
  renderItemList(root, { selectedProducts, evaluation, giftProducts });
  const savingsLine = root.querySelector("[data-byob-savings-line]");
  if (savingsLine) {
    if (evaluation.tier) {
      savingsLine.hidden = false;
      savingsLine.textContent = byobText(
        evaluation.giftVariantIds.length ? "savings_line_gift" : "savings_line",
        {
          amount: formatMoney(evaluation.discountAmount),
          percentage: evaluation.tier.percentage
        }
      );
    } else {
      savingsLine.hidden = true;
    }
  }
  const giftLocked = root.querySelector("[data-byob-gift-locked]");
  const giftUnlocked = root.querySelector("[data-byob-gift-unlocked]");
  const topTier = tiers.slice().sort((a, b) => b.minUniqueItems - a.minUniqueItems)[0];
  const giftIsUnlocked = Boolean(evaluation.tier && evaluation.giftVariantIds.length);
  if (giftLocked) {
    const showLocked = !giftIsUnlocked && topTier?.giftVariantIds?.length;
    giftLocked.hidden = !showLocked;
    const text = giftLocked.querySelector("[data-byob-gift-locked-text]");
    if (showLocked) {
      if (text) {
        text.textContent = byobText("gift_locked", {
          count: topTier.minUniqueItems,
          gift: describeGift(topTier.giftVariantIds, giftProducts, topTier.giftDisplayValue)
        });
      }
      applyGiftImage(giftLocked, topTier.giftVariantIds, giftProducts);
    }
  }
  if (giftUnlocked) {
    giftUnlocked.hidden = !giftIsUnlocked;
    const text = giftUnlocked.querySelector("[data-byob-gift-unlocked-text]");
    if (giftIsUnlocked) {
      if (text) {
        text.textContent = byobText("gift_unlocked", {
          gift: describeGift(
            evaluation.giftVariantIds,
            giftProducts,
            evaluation.tier?.giftDisplayValue
          )
        });
      }
      applyGiftImage(giftUnlocked, evaluation.giftVariantIds, giftProducts);
    }
  }
  const subtotalFinal = root.querySelector("[data-byob-subtotal-final]");
  const subtotalOriginal = root.querySelector("[data-byob-subtotal-original]");
  const savingsRecap = root.querySelector("[data-byob-savings-recap]");
  if (hasSelection) {
    const originalCents = evaluation.aggregateEligibleCost * quantity;
    const finalCents = originalCents - evaluation.discountAmount * quantity;
    if (subtotalOriginal) {
      subtotalOriginal.hidden = evaluation.discountAmount <= 0;
      subtotalOriginal.textContent = formatMoney(originalCents);
    }
    if (subtotalFinal)
      subtotalFinal.textContent = formatMoney(finalCents);
    const discountRow = root.querySelector("[data-byob-discount-row]");
    if (discountRow) {
      const showDiscount = evaluation.discountAmount > 0;
      discountRow.hidden = !showDiscount;
      if (showDiscount) {
        discountRow.querySelector("[data-byob-discount-label]").textContent = byobText(
          "bundle_discount",
          { percentage: evaluation.tier.percentage }
        );
        discountRow.querySelector("[data-byob-discount-amount]").textContent = `- ${formatMoney(evaluation.discountAmount * quantity)}`;
      }
    }
    if (savingsRecap) {
      const showRecap = evaluation.discountAmount > 0;
      savingsRecap.hidden = !showRecap;
      if (showRecap) {
        savingsRecap.textContent = byobText(
          evaluation.giftVariantIds.length ? "savings_recap_gift" : "savings_recap",
          { amount: formatMoney(evaluation.discountAmount * quantity) }
        );
      }
    }
  } else {
    if (subtotalOriginal) {
      subtotalOriginal.hidden = true;
      subtotalOriginal.textContent = "";
    }
    if (subtotalFinal)
      subtotalFinal.textContent = formatMoney(0);
    if (savingsRecap) {
      savingsRecap.hidden = true;
      savingsRecap.textContent = "";
    }
  }
  root.querySelector("[data-byob-edit-notice]")?.toggleAttribute("hidden", !isEditing);
  const quantityInput = root.querySelector("[data-byob-quantity] input");
  if (quantityInput && Number(quantityInput.value) !== quantity) {
    quantityInput.value = String(quantity);
    root.querySelector("[data-byob-quantity] quantity-input-regimen-group")?.updateQuantityButtonsState?.();
  }
  renderAddButtons(root, { selectedCount, minItems, isEditing, atBundleLimit, hasChanges });
}
function renderMobileBar(bar, state) {
  const {
    selectedCount,
    maxItems,
    tiers,
    minItems = 1,
    isEditing = false,
    atBundleLimit = false,
    hasChanges = true,
    evaluation
  } = state;
  const denominator = progressDenominator(maxItems, tiers);
  const progressFraction = Math.min(selectedCount / denominator, 1);
  const fill = bar.querySelector("[data-byob-progress-fill]");
  if (fill)
    fill.style.width = `${progressFraction * 100}%`;
  const max = Number.isFinite(maxItems) ? maxItems : denominator;
  const count = bar.querySelector("[data-byob-mobile-count]");
  if (count) {
    count.textContent = selectedCount ? byobText("mobile_count", { count: selectedCount, max }) : "";
  }
  const summary = bar.querySelector("[data-byob-mobile-summary]");
  if (summary) {
    summary.textContent = evaluation?.tier ? byobText("mobile_unlocked", { percentage: evaluation.tier.percentage }) : byobText("mobile_summary", {
      count: selectedCount,
      max
    });
  }
  renderAddButtons(bar, { selectedCount, minItems, isEditing, atBundleLimit, hasChanges });
  bar.hidden = false;
}
function showMobileSheet(sheet) {
  if (sheet)
    sheet.hidden = false;
}
function hideMobileSheet(sheet) {
  if (sheet)
    sheet.hidden = true;
}

// src/js/customElements/build-your-own-box.js
function selectionKey(variantIds, quantity) {
  return `${[...variantIds].sort().join(",")}|${quantity}`;
}
var BuildYourOwnBox = class extends HTMLElement {
  constructor() {
    super();
    const maxItemsAttr = Number(this.dataset.maxItems);
    this.configuredMaxItems = Number.isFinite(maxItemsAttr) && maxItemsAttr > 0 ? maxItemsAttr : null;
    this.storefrontAccessToken = this.dataset.storefrontAccessToken || null;
    this.allowBundleQuantity = this.dataset.bundleQuantity === "true";
    this.tiles = Array.from(this.querySelectorAll("[data-byob-tile]"));
    this.products = new Map(
      this.tiles.map((tile) => [
        tile.dataset.variantId,
        {
          variantId: tile.dataset.variantId,
          title: tile.querySelector("[data-product-card]")?.dataset.productTitle ?? "",
          priceCents: Number(tile.dataset.priceCents) || 0,
          imageUrl: tile.querySelector(".main-image img")?.getAttribute("src") ?? ""
        }
      ])
    );
    this.categoryTabs = Array.from(this.querySelectorAll("[data-byob-category-tab]"));
    this.summaryPanels = Array.from(this.querySelectorAll("[data-byob-summary]"));
    this.mobileBar = this.querySelector("[data-byob-mobile-bar]");
    this.mobileSheet = this.querySelector("[data-byob-mobile-sheet]");
    this.mobileViewButtons = Array.from(
      this.querySelectorAll("[data-byob-mobile-view-button]")
    );
    this.mobileSheetCloseButtons = Array.from(
      this.querySelectorAll("[data-byob-mobile-sheet-close], [data-byob-mobile-sheet-handle]")
    );
    this.selectedVariantIds = /* @__PURE__ */ new Set();
    this.config = null;
    this.giftProducts = /* @__PURE__ */ new Map();
    this.existingBoxIds = [];
    this.boxId = generateBoxId();
    this.isEditing = false;
    this.quantity = 1;
    this.activeCategory = "all";
    this.cartBoxSnapshot = null;
    this.cartUpdateUnsubscriber = null;
    this.handleTileClick = this.handleTileClick.bind(this);
    this.handleCategoryClick = this.handleCategoryClick.bind(this);
    this.handleAddBundle = this.handleAddBundle.bind(this);
    this.handleQuantityChange = this.handleQuantityChange.bind(this);
    this.handleStartNewBundle = this.handleStartNewBundle.bind(this);
    this.handleSummaryRemove = this.handleSummaryRemove.bind(this);
  }
  async connectedCallback() {
    const params = new URLSearchParams(window.location.search);
    const editBoxId = params.get(BYOB_EDIT_QUERY_PARAM);
    if (editBoxId) {
      this.isEditing = true;
      this.boxId = editBoxId;
    }
    try {
      this.syncFromCart(await getCartData(true));
    } catch (err) {
      console.error("Unable to restore BYOB bundle from cart", err);
    }
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartMutationComplete,
      ({ cartData }) => this.syncFromCart(cartData),
      "buildYourOwnBox"
    );
    this.config = await resolveByobConfig(this.storefrontAccessToken);
    if (!this.config) {
      this.hidden = true;
      this.cartUpdateUnsubscriber?.();
      return;
    }
    this.maxItems = this.configuredMaxItems ?? this.getTopTierThreshold();
    this.minItems = this.getMinimumItems();
    this.applyEligibility();
    this.hideEmptyCategoryTabs();
    filterTilesByCategory(this.tiles, this.activeCategory);
    this.render();
    this.bindEvents();
    this.giftProducts = await fetchGiftProducts(
      this.getAllGiftVariantIds(),
      this.storefrontAccessToken
    );
    if (this.giftProducts.size)
      this.render();
  }
  getAllGiftVariantIds() {
    return (this.config?.tiers ?? []).flatMap((tier) => tier.giftVariantIds ?? []);
  }
  disconnectedCallback() {
    this.cartUpdateUnsubscriber?.();
    this.tiles.forEach((tile) => {
      tile.querySelector("[data-byob-tile-action]")?.removeEventListener("click", this.handleTileClick);
    });
    this.categoryTabs.forEach((tab) => tab.removeEventListener("click", this.handleCategoryClick));
    this.querySelectorAll("[data-byob-atc-button]").forEach(
      (btn) => btn.removeEventListener("click", this.handleAddBundle)
    );
    this.querySelectorAll("[data-byob-quantity]").forEach(
      (el) => el.removeEventListener("change", this.handleQuantityChange)
    );
    this.querySelectorAll("[data-byob-start-new]").forEach(
      (btn) => btn.removeEventListener("click", this.handleStartNewBundle)
    );
    this.summaryPanels.forEach(
      (panel) => panel.removeEventListener("click", this.handleSummaryRemove)
    );
  }
  getTopTierThreshold() {
    return (this.config?.tiers ?? []).reduce(
      (max, tier) => Math.max(max, tier.minUniqueItems),
      1
    );
  }
  // Advisory only: the byob-bundle-limit validation function is what actually enforces the
  // cap, at checkout. Blocking here just means a shopper finds out before building a
  // bundle they can't buy.
  //
  // The real limit lives in a VALIDATION-owned metafield, which the storefront can't read,
  // so this leans on the single-bundle theme setting instead: with bundle quantity off the
  // store is already committed to one bundle per cart. A configured cap above one gets no
  // pre-emptive hint and is caught at checkout.
  isAtBundleLimit() {
    if (this.isEditing || this.allowBundleQuantity)
      return false;
    return this.existingBoxIds.length >= 1;
  }
  // A bundle below the lowest tier's threshold earns no discount at all, so it can't
  // be added — the Function would charge full price for something sold as a bundle.
  getMinimumItems() {
    const thresholds = (this.config?.tiers ?? []).map((tier) => tier.minUniqueItems).filter((count) => Number.isFinite(count) && count > 0);
    return thresholds.length ? Math.min(...thresholds) : 1;
  }
  bindEvents() {
    this.tiles.forEach((tile) => {
      tile.querySelector("[data-byob-tile-action]")?.addEventListener("click", this.handleTileClick);
    });
    this.categoryTabs.forEach((tab) => tab.addEventListener("click", this.handleCategoryClick));
    this.querySelectorAll("[data-byob-atc-button]").forEach(
      (btn) => btn.addEventListener("click", this.handleAddBundle)
    );
    this.mobileViewButtons.forEach(
      (btn) => btn.addEventListener("click", () => showMobileSheet(this.mobileSheet))
    );
    this.mobileSheetCloseButtons.forEach(
      (el) => el.addEventListener("click", () => hideMobileSheet(this.mobileSheet))
    );
    this.querySelectorAll("[data-byob-quantity]").forEach(
      (el) => el.addEventListener("change", this.handleQuantityChange)
    );
    this.querySelectorAll("[data-byob-start-new]").forEach(
      (btn) => btn.addEventListener("click", this.handleStartNewBundle)
    );
    this.summaryPanels.forEach(
      (panel) => panel.addEventListener("click", this.handleSummaryRemove)
    );
  }
  handleSummaryRemove(event) {
    const trigger = event.target.closest("[data-byob-remove-variant]");
    if (!trigger)
      return;
    this.selectedVariantIds.delete(trigger.dataset.byobRemoveVariant);
    this.render();
  }
  handleQuantityChange(event) {
    const value = Number(event.target.value);
    if (!Number.isFinite(value) || value < 1)
      return;
    this.quantity = value;
    this.render();
  }
  // The cart is the source of truth for a bundle already in it: the drawer can remove it
  // or change its quantity while the builder is open, and without this the builder would
  // keep offering to "update" a bundle that's gone. Runs on load and on every
  // cartMutationComplete, including the one that follows the builder's own add.
  syncFromCart(cartData) {
    if (!cartData)
      return;
    this.existingBoxIds = getBoxIdsFromCart(cartData);
    if (this.isEditing && !this.existingBoxIds.includes(this.boxId)) {
      this.resetToNewBundle();
    }
    if (!this.isEditing && !this.allowBundleQuantity && this.existingBoxIds[0]) {
      this.isEditing = true;
      this.boxId = this.existingBoxIds[0];
      this.cartBoxSnapshot = null;
    }
    if (this.isEditing) {
      const { variantIds, quantity } = getBoxSelectionFromCart(cartData, this.boxId);
      const snapshot = selectionKey(variantIds, quantity);
      if (snapshot !== this.cartBoxSnapshot) {
        this.cartBoxSnapshot = snapshot;
        this.selectedVariantIds = new Set(variantIds);
        this.quantity = quantity;
        this.resolveUntiledSelections(snapshot);
      }
    }
    if (this.config)
      this.render();
  }
  // A bundle restored from the cart can hold a variant with no tile here: out of stock
  // since it was added (the Liquid skips those), or on another page of the collection.
  // Only the first should leave the selection — dropping it here means the next Update
  // removes it from the cart — so availability is checked rather than inferred from the
  // missing tile. The second stays, with its details filled in from the same lookup.
  async resolveUntiledSelections(snapshot) {
    const untiled = [...this.selectedVariantIds].filter((id) => !this.products.has(id));
    if (!untiled.length)
      return;
    const results = await fetchVariantAvailability(untiled, this.storefrontAccessToken);
    if (!results || snapshot !== this.cartBoxSnapshot)
      return;
    results.forEach(({ available, product }, variantId) => {
      if (!available) {
        this.selectedVariantIds.delete(variantId);
      } else if (product) {
        this.products.set(variantId, product);
      }
    });
    if (this.config)
      this.render();
  }
  handleStartNewBundle() {
    this.resetToNewBundle();
    this.render();
  }
  resetToNewBundle() {
    this.selectedVariantIds.clear();
    this.quantity = 1;
    this.boxId = generateBoxId();
    this.isEditing = false;
    const url = new URL(window.location.href);
    url.searchParams.delete(BYOB_EDIT_QUERY_PARAM);
    window.history.replaceState({}, "", url);
    this.cartBoxSnapshot = null;
  }
  applyEligibility() {
    const eligibleSet = new Set(this.config.eligibleVariantIds ?? []);
    const ineligibleSet = new Set(this.config.ineligibleVariantIds ?? []);
    const isEligible = (variantId) => this.config.eligibilityMode === "deny" ? !ineligibleSet.has(variantId) : eligibleSet.has(variantId);
    const renderedCount = this.tiles.length;
    this.tiles = this.tiles.filter((tile) => {
      const eligible = isEligible(tile.dataset.variantId);
      if (eligible) {
        tile.hidden = false;
      } else {
        tile.remove();
      }
      return eligible;
    });
    if (renderedCount && !this.tiles.length) {
      console.error(
        `BYOB: every one of the ${renderedCount} products in this collection was filtered out as ineligible. Eligibility mode is "${this.config.eligibilityMode ?? "allow"}", with ${eligibleSet.size} allowed and ${ineligibleSet.size} denied variants configured. In allow mode a product only appears if its variant is on the allow list \u2014 set that in the app's BYOB discount settings for this store.`
      );
    }
  }
  // Tabs are derived server-side from every product on the page, but applyEligibility
  // removes the ineligible ones, which can leave a tab that filters to nothing.
  hideEmptyCategoryTabs() {
    const present = new Set(
      this.tiles.flatMap(
        (tile) => (tile.dataset.categories || "").split(",").filter(Boolean)
      )
    );
    this.categoryTabs.forEach((tab) => {
      if (tab.dataset.category === "all")
        return;
      tab.hidden = !present.has(tab.dataset.category);
    });
  }
  handleCategoryClick(event) {
    const tab = event.currentTarget;
    this.activeCategory = tab.dataset.category;
    this.categoryTabs.forEach((t) => {
      const active = t === tab;
      t.setAttribute("aria-pressed", String(active));
      t.classList.toggle("border-text-primary", active);
      t.classList.toggle("text-text-primary/70", !active);
      t.classList.toggle("border-transparent", !active);
    });
    filterTilesByCategory(this.tiles, this.activeCategory);
  }
  handleTileClick(event) {
    const tile = event.currentTarget.closest("[data-byob-tile]");
    const variantId = tile.dataset.variantId;
    if (this.selectedVariantIds.has(variantId)) {
      this.selectedVariantIds.delete(variantId);
    } else {
      if (this.selectedVariantIds.size >= this.maxItems)
        return;
      this.selectedVariantIds.add(variantId);
    }
    this.render();
  }
  // Re-sending a bundle identical to the one in the cart does nothing, so in edit mode
  // the CTA only activates once the selection or quantity departs from the cart's.
  hasUnsavedChanges() {
    if (!this.isEditing)
      return true;
    return selectionKey(this.selectedVariantIds, this.quantity) !== this.cartBoxSnapshot;
  }
  getEvaluation() {
    const costs = new Map(
      Array.from(this.selectedVariantIds).map((variantId) => [
        variantId,
        this.products.get(variantId)?.priceCents ?? 0
      ])
    );
    return evaluateBundle(this.config, Array.from(this.selectedVariantIds), costs);
  }
  render() {
    const evaluation = this.getEvaluation();
    const isFull = this.selectedVariantIds.size >= this.maxItems;
    renderTileStates(this.tiles, this.selectedVariantIds, isFull);
    const summaryState = {
      selectedCount: this.selectedVariantIds.size,
      maxItems: this.maxItems,
      tiers: this.config.tiers ?? [],
      minItems: this.minItems,
      atBundleLimit: this.isAtBundleLimit(),
      selectedProducts: Array.from(this.selectedVariantIds).map(
        (variantId) => this.products.get(variantId) ?? { variantId, title: "", priceCents: 0, imageUrl: "" }
      ),
      quantity: this.quantity,
      isEditing: this.isEditing,
      hasChanges: this.hasUnsavedChanges(),
      evaluation,
      giftProducts: this.giftProducts
    };
    this.summaryPanels.forEach((panel) => renderSummary(panel, summaryState));
    if (this.mobileBar) {
      renderMobileBar(this.mobileBar, summaryState);
    }
  }
  async handleAddBundle(event) {
    const button = event.currentTarget;
    if (button.disabled || this.selectedVariantIds.size < this.minItems || !this.hasUnsavedChanges()) {
      return;
    }
    const editUrl = `${window.location.pathname}?${BYOB_EDIT_QUERY_PARAM}=${this.boxId}`;
    await addBundleToCart(Array.from(this.selectedVariantIds), {
      boxId: this.boxId,
      editUrl,
      quantity: this.quantity,
      isEditing: this.isEditing
    });
    this.isEditing = true;
    hideMobileSheet(this.mobileSheet);
  }
};
customElements.define("build-your-own-box", BuildYourOwnBox);

// src/js/customElements/video-player.js
var CAPTION_POSITIONS = {
  top: { snapToLines: false, line: 5, lineAlign: "start" },
  middle: { snapToLines: false, line: 50, lineAlign: "center" },
  bottom: { snapToLines: true, line: "auto", lineAlign: "start" }
};
var DESKTOP_QUERY = "(min-width: 768px)";
var VideoPlayer = class extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector("video");
    if (!this.video)
      return;
    this.playButton = this.querySelector("[data-video-play]");
    this.muteButton = this.querySelector("[data-video-mute]");
    this.captionsButton = this.querySelector("[data-video-captions]");
    this.progressBar = this.querySelector("[data-progress-bar]");
    this.userPaused = false;
    this.observerPausing = false;
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.playButton?.addEventListener("click", () => this.togglePlay(), { signal });
    this.muteButton?.addEventListener(
      "click",
      () => {
        this.video.muted = !this.video.muted;
      },
      { signal }
    );
    this.captionsButton?.addEventListener("click", () => this.toggleCaptions(), { signal });
    this.video.addEventListener(
      "play",
      () => {
        this.userPaused = false;
        this.syncPlay();
      },
      { signal }
    );
    this.video.addEventListener(
      "pause",
      () => {
        this.userPaused = !this.observerPausing;
        this.observerPausing = false;
        this.syncPlay();
      },
      { signal }
    );
    this.video.addEventListener("volumechange", () => this.syncMute(), { signal });
    this.video.textTracks.addEventListener("change", () => this.syncCaptions(), { signal });
    const trackElement = this.video.querySelector("track");
    if (trackElement && (this.dataset.captionPosition || this.dataset.captionPositionMobile)) {
      this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
      this.desktopQuery.addEventListener("change", () => this.positionCaptions(), { signal });
      trackElement.addEventListener("load", () => this.positionCaptions(), { signal });
      if (trackElement.readyState === HTMLTrackElement.LOADED)
        this.positionCaptions();
    }
    if (this.progressBar) {
      this.video.addEventListener("timeupdate", () => this.syncProgress(), { signal });
    }
    const reducedMotion = this.hasAttribute("data-respect-reduced-motion") && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion && this.video.autoplay) {
      this.video.removeAttribute("autoplay");
      this.video.pause();
    }
    if (this.hasAttribute("data-autoplay") && !reducedMotion) {
      this.observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            if (!this.userPaused)
              this.play();
          } else if (!this.video.paused) {
            this.observerPausing = true;
            this.video.pause();
          }
        },
        { threshold: 0.25 }
      );
      this.observer.observe(this);
    }
    this.syncPlay();
    this.syncMute();
    this.syncCaptions();
  }
  disconnectedCallback() {
    this.abortController?.abort();
    this.observer?.disconnect();
  }
  play() {
    this.video.play()?.catch(() => {
    });
  }
  togglePlay() {
    if (this.video.paused) {
      this.play();
    } else {
      this.video.pause();
    }
  }
  toggleCaptions() {
    const track = this.video.textTracks[0];
    if (!track)
      return;
    track.mode = track.mode === "showing" ? "hidden" : "showing";
    this.syncCaptions();
  }
  positionCaptions() {
    const cues = this.video.textTracks[0]?.cues;
    if (!cues)
      return;
    const key = this.desktopQuery.matches ? this.dataset.captionPosition : this.dataset.captionPositionMobile;
    this.originalCuePositions ??= /* @__PURE__ */ new WeakMap();
    for (const cue of cues) {
      if (!this.originalCuePositions.has(cue)) {
        const { snapToLines, line, lineAlign } = cue;
        this.originalCuePositions.set(cue, { snapToLines, line, lineAlign });
      }
      Object.assign(cue, CAPTION_POSITIONS[key] ?? this.originalCuePositions.get(cue));
    }
  }
  syncPlay() {
    if (!this.playButton)
      return;
    const paused = this.video.paused;
    this.playButton.setAttribute(
      "aria-label",
      paused ? this.playButton.dataset.labelPlay : this.playButton.dataset.labelPause
    );
    this.playButton.querySelector("[data-icon-play]")?.classList.toggle("hidden", !paused);
    this.playButton.querySelector("[data-icon-pause]")?.classList.toggle("hidden", paused);
  }
  syncMute() {
    if (!this.muteButton)
      return;
    const muted = this.video.muted;
    this.muteButton.setAttribute("aria-pressed", String(muted));
    this.muteButton.querySelector("[data-icon-muted]")?.classList.toggle("hidden", !muted);
    this.muteButton.querySelector("[data-icon-unmuted]")?.classList.toggle("hidden", muted);
  }
  syncCaptions() {
    if (!this.captionsButton)
      return;
    const showing = this.video.textTracks[0]?.mode === "showing";
    this.captionsButton.setAttribute("aria-pressed", String(showing));
    this.captionsButton.querySelector("[data-icon-captions-on]")?.classList.toggle("hidden", !showing);
    this.captionsButton.querySelector("[data-icon-captions-off]")?.classList.toggle("hidden", showing);
  }
  syncProgress() {
    const { currentTime, duration } = this.video;
    if (!duration)
      return;
    this.progressBar.style.width = `${currentTime / duration * 100}%`;
  }
};
customElements.define("video-player", VideoPlayer);

// src/js/helpers/byobUtils.js
function getAllConfiguredGiftVariantIds(config) {
  const all = /* @__PURE__ */ new Set();
  for (const tier of config.tiers ?? []) {
    for (const variantId of tier.giftVariantIds ?? []) {
      all.add(variantId);
    }
  }
  return all;
}
function getByobBoxItems(cartData) {
  return (cartData.items ?? []).filter(
    (item) => item.properties?.[PRODUCT_PROPERTY_KEYS.isByob] === "true"
  );
}
function getByobItems(cartData) {
  const config = readStoredByobConfig();
  const noop = { giftsToAdd: [], giftsToRemove: {}, doAddGifts: false, doRemoveGifts: false };
  if (!config) {
    return noop;
  }
  const boxItems = getByobBoxItems(cartData);
  let wantedGiftVariantIds = [];
  let boxId = "";
  if (boxItems.length) {
    const variantIds = boxItems.map((item) => item.variant_id?.toString());
    const costs = new Map(
      boxItems.map((item) => [
        item.variant_id?.toString(),
        item.final_line_price ?? item.line_price ?? 0
      ])
    );
    const { tier, giftVariantIds } = evaluateBundle(config, variantIds, costs);
    wantedGiftVariantIds = tier ? giftVariantIds : [];
    boxId = boxItems[0]?.properties?.[PRODUCT_PROPERTY_KEYS.byobBoxId] ?? "";
  }
  const cartItemVariantIds = new Set(cartData.cartItemVariantIds ?? []);
  const allConfiguredGiftVariantIds = getAllConfiguredGiftVariantIds(config);
  const giftsToAdd = wantedGiftVariantIds.filter((variantId) => !cartItemVariantIds.has(variantId)).map((variantId) => ({
    id: Number(variantId),
    quantity: 1,
    selling_plan: null,
    properties: {
      [PRODUCT_PROPERTY_KEYS.byobBoxId]: boxId
    }
  }));
  const existingGiftLines = (cartData.items ?? []).filter((item) => {
    const variantId = item.variant_id?.toString();
    return Boolean(item.properties?.[PRODUCT_PROPERTY_KEYS.byobBoxId]) && item.properties?.[PRODUCT_PROPERTY_KEYS.isByob] !== "true" && allConfiguredGiftVariantIds.has(variantId) && !wantedGiftVariantIds.includes(variantId);
  });
  const giftsToRemove = existingGiftLines.reduce(
    (acc, item) => ({ ...acc, [item.variant_id]: 0 }),
    {}
  );
  return {
    giftsToAdd,
    giftsToRemove,
    doAddGifts: giftsToAdd.length > 0,
    doRemoveGifts: Object.keys(giftsToRemove).length > 0
  };
}
function addRemoveByobGiftItems(cartData) {
  const { doAddGifts, doRemoveGifts, giftsToAdd, giftsToRemove } = getByobItems(cartData);
  if (doRemoveGifts) {
    cartUpdateQuantities(giftsToRemove);
  }
  if (doAddGifts) {
    cartAddItems(giftsToAdd);
  }
  return { doAddGifts, doRemoveGifts, giftsToAdd, giftsToRemove };
}

// src/js/cart/cartRules.js
var CART_RULES_CONFIG = {
  // Rule 1 — max qty per paid line item
  RULE_1_ENABLED: true,
  RULE_1_CAP: window.cartItemMaxQuantity ?? CART_LINE_ITEM_QUANTITY_MAX_DEFAULT,
  // 6
  // Rule 2 — max qty per zero-dollar line item
  RULE_2_ENABLED: true,
  RULE_2_CAP: 1,
  // Rule 3 — no duplicate zero-dollar variant across line items
  RULE_3_ENABLED: true,
  // Rule 4 — max total zero-dollar qty across cart
  RULE_4_ENABLED: true,
  RULE_4_CAP: 10,
  // Rule 5 — zero-dollar-only cart is cleared
  RULE_5_ENABLED: true,
  // Rule 6 — cart-wide quantity cap per product group (from product_lower_cart_limit theme settings)
  RULE_6_ENABLED: true,
  // Rule 7A — remove complete-regimen discount code if not enough matching line items
  RULE_7A_ENABLED: false,
  // Rule 7B — cap complete_regimen line items to qty 1;
  RULE_7B_ENABLED: false
};
function isCompleteRegimenItem(item) {
  return !!item.properties?._complete_regimen;
}
function getCompleteRegimenValue(item) {
  return item.properties?._complete_regimen ?? null;
}
function parseCompleteRegimenCode(code) {
  if (!code || !code.startsWith(COMPLETE_REGIMEN_DISCOUNT_CODE_PREFIX))
    return null;
  const parts = code.split("_");
  if (parts.length < 5)
    return null;
  const minQuantity = parseInt(parts[3], 10);
  if (isNaN(minQuantity))
    return null;
  const regimenValue = `${parts[1]}_${parts[2]}`;
  return { regimenValue, minQuantity };
}
function getStorefrontConfig() {
  const endpoint = "/api/2026-04/graphql.json";
  const gwpEl = document.querySelector("[data-gwp]");
  if (gwpEl) {
    try {
      const props = JSON.parse(gwpEl.getAttribute("data-gwp"));
      if (props?.graphql_storefront_access_token) {
        return { endpoint, storefrontToken: props.graphql_storefront_access_token };
      }
    } catch (_) {
    }
  }
  const crEl = document.querySelector("complementary-regimen[data-storefront-access-token]");
  if (crEl) {
    const token = crEl.getAttribute("data-storefront-access-token");
    if (token)
      return { endpoint, storefrontToken: token };
  }
  return { endpoint, storefrontToken: null };
}
function isRuleEnabled(configDefault, lsKey) {
  if (localStorage.getItem("muradCartRules") === "0")
    return false;
  const override = localStorage.getItem(lsKey);
  if (override === "0")
    return false;
  return configDefault;
}
function isZeroDollarItem(item) {
  return item.original_price === 0;
}
function enforceCartRules(cartData) {
  const items = cartData?.cartRaw?.items ?? [];
  if (items.length === 0)
    return { messages: [], shouldClear: false, updates: {}, changes: [] };
  cartDebug("enforceCartRules started with cart data:", cartData);
  const messages = [];
  if (isRuleEnabled(CART_RULES_CONFIG.RULE_5_ENABLED, "muradCartRulesRule5") && cartData.cartTotalPrice === 0) {
    cartDebug("enforceCartRules rule 5: clearing zero-dollar-only cart");
    messages.push(
      "Your cart contained only free items and has been cleared."
    );
    return { messages, shouldClear: true, updates: {}, changes: [] };
  }
  const updates = {};
  const changes = [];
  const zeroDollarVariantsSeen = /* @__PURE__ */ new Set();
  let zeroDollarRunningTotal = 0;
  for (const item of items) {
    const variantId = item.variant_id;
    if (isZeroDollarItem(item)) {
      if (isRuleEnabled(CART_RULES_CONFIG.RULE_3_ENABLED, "muradCartRulesRule3") && zeroDollarVariantsSeen.has(variantId)) {
        const keyToRemove = item.key;
        cartDebug(
          `enforceCartRules rule 3: duplicate zero-dollar variant ${variantId} ("${item.title}") \u2014 queuing removal of line item key ${keyToRemove}`
        );
        messages.push(
          `A duplicate free item ("${item.title}") was removed from your cart.`
        );
        changes.push({ id: keyToRemove, quantity: 0 });
        continue;
      }
      zeroDollarVariantsSeen.add(variantId);
      let correctedQty = isRuleEnabled(CART_RULES_CONFIG.RULE_2_ENABLED, "muradCartRulesRule2") ? Math.min(item.quantity, CART_RULES_CONFIG.RULE_2_CAP) : item.quantity;
      if (isRuleEnabled(CART_RULES_CONFIG.RULE_2_ENABLED, "muradCartRulesRule2") && correctedQty < item.quantity) {
        cartDebug(
          `enforceCartRules rule 2: capping zero-dollar item ${variantId} ("${item.title}") from ${item.quantity} to ${correctedQty}`
        );
        messages.push(
          `Free item quantity for "${item.title}" was reduced to ${correctedQty} (maximum ${CART_RULES_CONFIG.RULE_2_CAP} per free item).`
        );
      }
      if (isRuleEnabled(CART_RULES_CONFIG.RULE_4_ENABLED, "muradCartRulesRule4")) {
        const capacityRemaining = CART_RULES_CONFIG.RULE_4_CAP - zeroDollarRunningTotal;
        if (correctedQty > capacityRemaining) {
          cartDebug(
            `enforceCartRules rule 4: total zero-dollar qty limit reached; reducing "${item.title}" from ${correctedQty} to ${capacityRemaining}`
          );
          messages.push(
            `Your cart has reached the maximum of ${CART_RULES_CONFIG.RULE_4_CAP} free items. "${item.title}" quantity was adjusted.`
          );
          correctedQty = capacityRemaining;
        }
      }
      zeroDollarRunningTotal += correctedQty;
      if (correctedQty !== item.quantity) {
        updates[variantId] = correctedQty;
      }
    } else {
      if (isRuleEnabled(CART_RULES_CONFIG.RULE_1_ENABLED, "muradCartRulesRule1") && item.quantity > CART_RULES_CONFIG.RULE_1_CAP) {
        cartDebug(
          `enforceCartRules rule 1: capping paid item ${variantId} ("${item.title}") from ${item.quantity} to ${CART_RULES_CONFIG.RULE_1_CAP}`
        );
        messages.push(
          `"${item.title}" quantity was reduced to ${CART_RULES_CONFIG.RULE_1_CAP} (maximum per item).`
        );
        updates[variantId] = CART_RULES_CONFIG.RULE_1_CAP;
      }
      if (CART_RULES_CONFIG.RULE_7B_ENABLED && isCompleteRegimenItem(item) && item.quantity > 1) {
        cartDebug(
          `enforceCartRules rule 7: capping complete_regimen item ${variantId} ("${item.title}") from ${item.quantity} to 1`
        );
        messages.push(
          `"${item.title}" is part of a skin-care regimen and has been limited to a quantity of 1.`
        );
        updates[variantId] = 1;
      }
    }
  }
  cartDebug("enforceCartRules rule 6: rule enabled =", isRuleEnabled(CART_RULES_CONFIG.RULE_6_ENABLED, "muradCartRulesRule6"), "| window.cartLowerLimits =", window.cartLowerLimits);
  if (isRuleEnabled(CART_RULES_CONFIG.RULE_6_ENABLED, "muradCartRulesRule6")) {
    const rawLimits = Array.isArray(window.cartLowerLimits) ? window.cartLowerLimits : [];
    cartDebug(`enforceCartRules rule 6: ${rawLimits.length} limit entries found`);
    for (const [entryIdx, entry] of rawLimits.entries()) {
      const entryLimit = Number(entry.limit);
      cartDebug(`enforceCartRules rule 6: entry[${entryIdx}] raw limit = ${entry.limit}, parsed = ${entryLimit}, valid = ${Number.isInteger(entryLimit) && entryLimit > 0}`);
      if (!Number.isInteger(entryLimit) || entryLimit <= 0)
        continue;
      const groupVariantIds = new Set(
        (entry.variantIds ?? []).map(Number).filter(Boolean)
      );
      cartDebug(`enforceCartRules rule 6: entry[${entryIdx}] groupVariantIds (${groupVariantIds.size}):`, [...groupVariantIds]);
      if (groupVariantIds.size === 0)
        continue;
      const groupItems = items.filter(
        (item) => !isZeroDollarItem(item) && groupVariantIds.has(item.variant_id)
      );
      cartDebug(
        `enforceCartRules rule 6: entry[${entryIdx}] groupItems (${groupItems.length}):`,
        groupItems.map((i) => ({ key: i.key, variant_id: i.variant_id, title: i.title, qty: i.quantity, zeroDollar: isZeroDollarItem(i) }))
      );
      cartDebug(
        `enforceCartRules rule 6: entry[${entryIdx}] all cart items for reference:`,
        items.map((i) => ({ key: i.key, variant_id: i.variant_id, title: i.title, qty: i.quantity, zeroDollar: isZeroDollarItem(i), inGroup: groupVariantIds.has(i.variant_id) }))
      );
      if (groupItems.length === 0)
        continue;
      const groupTotal = groupItems.reduce((sum, item) => sum + item.quantity, 0);
      cartDebug(`enforceCartRules rule 6: entry[${entryIdx}] groupTotal = ${groupTotal}, entryLimit = ${entryLimit}, violation = ${groupTotal > entryLimit}`);
      const allowedQtyByVariantId = /* @__PURE__ */ new Map();
      if (groupTotal > entryLimit) {
        cartDebug(
          `enforceCartRules rule 6: product group cart-wide total ${groupTotal} exceeds limit ${entryLimit}; applying corrections`
        );
        let remaining = entryLimit;
        for (const item of groupItems) {
          const allowedQty = Math.min(item.quantity, remaining);
          remaining -= allowedQty;
          allowedQtyByVariantId.set(
            item.variant_id,
            (allowedQtyByVariantId.get(item.variant_id) ?? 0) + allowedQty
          );
          cartDebug(
            `enforceCartRules rule 6: greedy \u2014 item key=${item.key} variant=${item.variant_id} ("${item.title}") qty=${item.quantity} allowedQty=${allowedQty} remainingAfter=${remaining} needsCorrection=${allowedQty < item.quantity}`
          );
          if (allowedQty < item.quantity) {
            cartDebug(
              `enforceCartRules rule 6: entry[${entryIdx}] variant ${item.variant_id} ("${item.title}") needs reduction from ${item.quantity} to ${allowedQty} (group limit ${entryLimit})`
            );
            messages.push(
              `Limited special offer - "${item.title}" quantity was reduced to ${allowedQty} (maximum ${entryLimit} for this special offer).`
            );
          }
        }
        cartDebug("enforceCartRules rule 6: allowedQtyByVariantId after greedy:", Object.fromEntries(allowedQtyByVariantId));
        for (const [variantId, allowedQty] of allowedQtyByVariantId) {
          const variantGroupItems = groupItems.filter((i) => i.variant_id === variantId);
          const currentVariantTotal = variantGroupItems.reduce((sum, i) => sum + i.quantity, 0);
          if (allowedQty >= currentVariantTotal)
            continue;
          if (variantGroupItems.length === 1) {
            updates[variantId] = Math.min(updates[variantId] ?? currentVariantTotal, allowedQty);
            cartDebug(`enforceCartRules rule 6: entry[${entryIdx}] updates[${variantId}] = ${updates[variantId]} (single-line, allowedQty=${allowedQty} vs currentVariantTotal=${currentVariantTotal})`);
          } else {
            cartDebug(`enforceCartRules rule 6: entry[${entryIdx}] variant ${variantId} spans ${variantGroupItems.length} lines (total=${currentVariantTotal} allowed=${allowedQty}) \u2014 deferred per-key corrections`);
            let variantAllowedRemaining = allowedQty;
            for (const item of variantGroupItems) {
              const itemAllowedQty = Math.min(item.quantity, variantAllowedRemaining);
              variantAllowedRemaining -= itemAllowedQty;
              if (itemAllowedQty < item.quantity) {
                const keyToUpdate = item.key;
                const newQty = itemAllowedQty;
                cartDebug(`enforceCartRules rule 6: entry[${entryIdx}] multi-line correction \u2014 key ${keyToUpdate} ("${item.title}") from ${item.quantity} to ${newQty}`);
                changes.push({ id: keyToUpdate, quantity: newQty });
              }
            }
          }
        }
      }
      for (const parentVariantId of groupVariantIds) {
        const parentVariantIdStr = String(parentVariantId);
        const linkedExtras = items.filter(
          (item) => item.properties?.[PRODUCT_PROPERTY_KEYS.parentVariantId] === parentVariantIdStr
        );
        cartDebug(
          `enforceCartRules rule 6: extras scan for parentVariantId=${parentVariantId} (str="${parentVariantIdStr}") \u2014 linkedExtras found: ${linkedExtras.length}`,
          linkedExtras.map((i) => ({ key: i.key, variant_id: i.variant_id, title: i.title, qty: i.quantity, _parent_variant_id: i.properties?.[PRODUCT_PROPERTY_KEYS.parentVariantId] }))
        );
        cartDebug(
          `enforceCartRules rule 6: all cart items _parent_variant_id values:`,
          items.map((i) => ({ key: i.key, variant_id: i.variant_id, _parent_variant_id: i.properties?.[PRODUCT_PROPERTY_KEYS.parentVariantId] ?? "(none)" }))
        );
        if (linkedExtras.length === 0)
          continue;
        const extraTotal = linkedExtras.reduce((sum, item) => sum + item.quantity, 0);
        const targetParentQty = allowedQtyByVariantId.has(parentVariantId) ? allowedQtyByVariantId.get(parentVariantId) : groupItems.filter((i) => i.variant_id === parentVariantId).reduce((sum, i) => sum + i.quantity, 0);
        cartDebug(`enforceCartRules rule 6: extraTotal=${extraTotal} targetParentQty=${targetParentQty} needsCleanup=${extraTotal > targetParentQty}`);
        if (extraTotal <= targetParentQty)
          continue;
        cartDebug(
          `enforceCartRules rule 6: extra items for parent variant ${parentVariantId} total ${extraTotal}, capping to ${targetParentQty}`
        );
        let extraRemaining = targetParentQty;
        for (const item of linkedExtras) {
          const allowedExtraQty = Math.min(item.quantity, extraRemaining);
          extraRemaining -= allowedExtraQty;
          cartDebug(
            `enforceCartRules rule 6: extra item key=${item.key} variant=${item.variant_id} ("${item.title}") qty=${item.quantity} allowedExtraQty=${allowedExtraQty} extraRemainingAfter=${extraRemaining} needsCorrection=${allowedExtraQty < item.quantity}`
          );
          if (allowedExtraQty < item.quantity) {
            const newQty = allowedExtraQty;
            messages.push(
              newQty === 0 ? `Limited special offer - "${item.title}" was removed with its paired product.` : `Limited special offer - "${item.title}" quantity was reduced to ${newQty} with its paired product.`
            );
            const sameVariantExtraLines = linkedExtras.filter((i) => i.variant_id === item.variant_id);
            if (sameVariantExtraLines.length === 1) {
              updates[item.variant_id] = Math.min(updates[item.variant_id] ?? item.quantity, newQty);
              cartDebug(
                `enforceCartRules rule 6: extra item updates[${item.variant_id}] = ${updates[item.variant_id]} (single-line, paired with parent ${parentVariantId})`
              );
            } else {
              const keyToUpdate = item.key;
              cartDebug(
                `enforceCartRules rule 6: extra item key=${keyToUpdate} ("${item.title}") multi-line correction from ${item.quantity} to ${newQty} (paired with parent ${parentVariantId})`
              );
              changes.push({ id: keyToUpdate, quantity: newQty });
            }
          }
        }
      }
    }
  }
  if (CART_RULES_CONFIG.RULE_7A_ENABLED) {
    const rawDiscountCodes = cartData?.cartRaw?.discount_codes ?? [];
    const completeCodes = rawDiscountCodes.map((d) => d.code).filter((code) => code && code.startsWith(COMPLETE_REGIMEN_DISCOUNT_CODE_PREFIX));
    for (const code of completeCodes) {
      const parsed = parseCompleteRegimenCode(code);
      if (!parsed)
        continue;
      const { regimenValue, minQuantity } = parsed;
      const matchingCount = items.filter(
        (item) => !isZeroDollarItem(item) && getCompleteRegimenValue(item) === regimenValue
      ).length;
      if (matchingCount < minQuantity)
        cartDebug(
          `enforceCartRules rule 7: discount code "${code}" requires ${minQuantity} items with _complete_regimen="${regimenValue}", found ${matchingCount}; removing code`
        );
      messages.push(
        `The discount code "${code}" has been removed because the required regimen items are no longer in your cart.`
      );
      const cartToken = cartData?.cartRaw?.token;
      if (cartToken) {
        const remainingCodes = rawDiscountCodes.map((d) => d.code).filter((c) => c !== code);
        const { endpoint, storefrontToken } = getStorefrontConfig();
        if (storefrontToken) {
          updateCartDiscountCodes(cartToken, remainingCodes, {
            endpoint,
            storefrontToken
          }).catch((err) => {
            cartDebug("enforceCartRules rule 7: failed to remove discount code", err);
          });
        } else {
          cartDebug("enforceCartRules rule 7: no storefront token found; cannot remove discount code");
        }
      } else {
        cartDebug("enforceCartRules rule 7: no cart token found; cannot remove discount code");
      }
    }
  }
  cartDebug("enforceCartRules result:", { updates, changes: changes.length, messages: messages.length });
  return { messages, shouldClear: false, updates, changes };
}

// src/js/cart/cartHub.js
function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`
    }
  };
}
function getIsCartPage() {
  return window.location.pathname.includes("/cart");
}
function getSectionsToRender(isCartPage) {
  const cartDrawerComponents = [
    {
      section: "header",
      id: "cart-drawer-wrapper",
      // selector: "#cart-drawer-ajax",
      // selector: "[data-ajax-cart-content]",
      selector: ".cart-items-ajax"
    },
    {
      section: "header",
      id: "cart-drawer-wrapper",
      selector: "#cart-drawer-toggle-ajax"
    },
    {
      section: "header",
      id: "cart-drawer-wrapper",
      selector: ".cart-checkout-cta-ajax"
    },
    {
      section: "header",
      id: "cart-drawer-wrapper",
      selector: "#cart-drawer-title"
    },
    {
      section: "header",
      id: "cart-drawer-wrapper",
      selector: "#cart-drawer-checkout-cta"
    },
    {
      section: "header",
      id: "cart-drawer-wrapper",
      selector: "#cart-free-ship-drawer [data-remaining]"
    }
  ];
  const cartPageComponents = [
    {
      section: "cart-page",
      id: "cart-page",
      // section: document.getElementById("main-cart-items")?.dataset.id,
      selector: ".cart-items-ajax"
    },
    {
      section: "cart-page",
      id: "cart-page",
      selector: "#cart-title-mobile"
    },
    {
      section: "cart-page",
      id: "cart-page",
      selector: "#cart-title-desktop"
    },
    {
      section: "cart-page",
      id: "cart-page-footer",
      selector: ".cart-checkout-cta-ajax"
    },
    {
      section: "cart-page",
      id: "cart-page",
      selector: "#cart-page-yotpo-rewards-ajax .yotpo-points-anon"
    },
    {
      section: "cart-page",
      id: "cart-page",
      selector: "#cart-page-additional-checkout-buttons-ajax"
    },
    {
      section: "cart-page",
      id: "cart-page",
      selector: "#cart-free-ship-mobile [data-remaining]"
    },
    {
      section: "cart-page",
      id: "cart-page",
      selector: "#cart-free-ship-desktop [data-remaining]"
    }
    // note: don't attempt to use Shopify bundled section rendering to update carousels; as originally built, they will fail because JS behind the Splide component will not be retriggered by the re-render;
    // {
    //   id: "cart-page",
    //   section: "cart-page",
    //   selector: "#cart-page-free-samples-ajax",
    // },
  ];
  return [...cartDrawerComponents, ...isCartPage ? cartPageComponents : []];
}
function getSectionInnerHTML(html, selector) {
  return new DOMParser().parseFromString(html, "text/html").querySelector(selector)?.innerHTML;
}
function replaceSections(sectionsToRender, resSections) {
  sectionsToRender.forEach((section) => {
    const elementToReplace = document.getElementById(section.id)?.querySelector(section.selector);
    if (elementToReplace === null) {
      cartDebug(
        `Cannot find element to replace for section: ${section.section}; id: ${section.id}; selector: ${section.selector}.  This may be fine b/c some elements shouldn't be rendered (e.g. alternate rewards display if user is logged-in or anonymous)`
      );
      return;
    }
    const updatedSectionHTML = resSections[section.section];
    if (elementToReplace && updatedSectionHTML) {
      elementToReplace.innerHTML = getSectionInnerHTML(
        updatedSectionHTML,
        section.selector
      );
    } else {
      cartDebug(
        `Updated section HTML not found; cannot update ${section.section}; id: ${section.id}; selector: ${section.selector}`
      );
    }
  });
}
var fetchURLs = {
  // window.Shopify.routes.root + "cart/add.js
  // add: `${window.routes.cart_add_url}`,
  add: `/cart/add`,
  // change: `${window.routes.cart_change_url}`,
  // mutate one line item at a time;
  change: `/cart/change`,
  // mutate cart line item quantities
  update: `/cart/update`,
  clear: `/cart/clear`
};
function revealCartDrawer() {
  if (getIsCartPage()) {
    return;
  }
  const cartDrawerToggle = document.querySelector("#cart-drawer-toggle");
  if (cartDrawerToggle) {
    cartDrawerToggle.checked = true;
  }
}
function openCartDrawer() {
  return function openCartDrawerHandler(payload) {
    if (payload?.openDrawer === false) {
      return;
    }
    revealCartDrawer();
  };
}
subscribe(
  PUB_SUB_EVENTS.cartMutationComplete,
  openCartDrawer(),
  "openCartDrawer"
);
async function readCartErrorReason(res) {
  const fallback = res.statusText || "Cart update failed. Please try again.";
  try {
    const body = await res.json();
    const { errors, description, message } = body ?? {};
    if (typeof errors === "string")
      return errors;
    if (Array.isArray(errors) && errors.length)
      return errors.join(" ");
    if (errors && typeof errors === "object") {
      const flattened = Object.values(errors).flat().filter(Boolean);
      if (flattened.length)
        return flattened.join(" ");
    }
    return description || message || fallback;
  } catch (err) {
    cartDebug("could not parse cart error body", err);
    return fallback;
  }
}
function showCartRulesNotification(messages) {
  const NOTIFICATION_ID = "cart-rules-notification";
  const PARENT_ID = "cart-drawer-content";
  const DISMISS_AFTER_MS = 1e4;
  let el = document.getElementById(NOTIFICATION_ID);
  if (!el) {
    el = document.createElement("div");
    el.id = NOTIFICATION_ID;
    el.setAttribute("role", "status");
    el.style.cssText = [
      "position:absolute",
      "top:4.5rem",
      "left:50%",
      "transform:translateX(-50%)",
      //"z-index:9999",
      "max-width:28rem",
      "width:calc(100% - 2rem)",
      "min-height:5rem",
      "background:#fef3c7",
      "border:1px solid #f59e0b",
      "color:#92400e",
      "font-size:0.875rem",
      "border-radius:0.375rem",
      "padding:0.75rem 1rem",
      "box-shadow:0 4px 12px rgba(0,0,0,0.12)"
    ].join(";");
    document.getElementById(PARENT_ID)?.appendChild(el);
  }
  el.innerHTML = `<button onclick="this.closest('#${NOTIFICATION_ID}').hidden=true" aria-label="Dismiss" style="position:absolute;top:0;right:0;background:none;border:none;cursor:pointer;font-size:1.5rem;line-height:0.6;color:#92400e;padding:0.5rem">&times;</button>` + messages.map((msg) => `<p style="margin:0 0 0.25rem;padding-right:1.25rem">${msg}</p>`).join("");
  el.hidden = false;
  clearTimeout(el._dismissTimer);
  el._dismissTimer = setTimeout(() => {
    el.hidden = true;
  }, DISMISS_AFTER_MS);
}
async function mutateCartAndPublish({
  onSuccess,
  action,
  payload,
  operations = void 0,
  line = void 0,
  id = void 0,
  openDrawer = true,
  redirectToCheckout = false,
  redirectUrl = `${window.Shopify.routes.root}checkout`
}) {
  const steps = operations?.length ? operations : [{ action, payload }];
  const isCartPage = getIsCartPage();
  try {
    publish(
      PUB_SUB_EVENTS.cartInFlight,
      PUB_SUB_PAYLOADS[PUB_SUB_EVENTS.cartInFlight]({
        isInFlight: true,
        id,
        line,
        action
      })
    );
    const sectionsToRender = getSectionsToRender(isCartPage);
    if (getCartDebugState() === true) {
      debugOutlineElements(
        sectionsToRender.map(
          (section) => document.getElementById(section.id)?.querySelector(section.selector)
        ),
        "red"
      );
    }
    const uniqueSectionArray = sectionsToRender.reduce((prev, curr) => {
      if (!prev.includes(curr.section)) {
        prev.push(curr.section);
      }
      return prev;
    }, []);
    let res;
    let resJson;
    for (const step of steps) {
      cartDebug("cart fetch", step.action);
      const body = JSON.stringify({
        ...step.payload,
        sections: uniqueSectionArray
      });
      res = await fetch(`${fetchURLs[step.action]}`, {
        ...fetchConfig(),
        ...{ body }
      });
      if (res.status !== 200)
        break;
      resJson = await res.json();
      if (resJson.errors) {
        throw new Error(resJson.errors);
      }
    }
    if (res.status === 200) {
      if (action === "add" && onSuccess) {
        try {
          Promise.resolve(onSuccess(resJson.items || [])).catch(console.error);
        } catch (error) {
          console.error(error);
        }
      }
      let cartData;
      if (steps.some((step) => step.action === "add")) {
        cartData = await getCartData(true);
      } else {
        cartData = shapeCartData(resJson);
      }
      const { cartRaw, cartTotalPrice, cartItemVariantIds } = cartData;
      setCartDataLocalStorage(cartData);
      const { doAddGifts, doRemoveGifts } = addRemoveGwpItems(cartData);
      const { doAddGifts: doAddByobGifts, doRemoveGifts: doRemoveByobGifts } = addRemoveByobGiftItems(cartData);
      if (doAddGifts || doRemoveGifts || doAddByobGifts || doRemoveByobGifts) {
      }
      const MAX_CORRECTION_CYCLES = 5;
      const allViolationMessages = [];
      let lastCorrectionSections = null;
      for (let correctionCycle = 0; correctionCycle < MAX_CORRECTION_CYCLES; correctionCycle++) {
        const { messages: ruleMessages, shouldClear, updates, changes } = enforceCartRules(cartData);
        allViolationMessages.push(...ruleMessages);
        const hasCorrections = shouldClear || Object.keys(updates).length > 0 || changes.length > 0;
        cartDebug(`enforceCartRules correction cycle ${correctionCycle + 1}: shouldClear=${shouldClear} updates=${JSON.stringify(updates)} changes=${changes.length} hasCorrections=${hasCorrections}`);
        if (!hasCorrections)
          break;
        if (shouldClear) {
          const clearRes = await fetch(fetchURLs.clear, {
            ...fetchConfig(),
            body: JSON.stringify({ sections: uniqueSectionArray })
          });
          if (clearRes.status === 200) {
            const clearJson = await clearRes.json();
            cartData = shapeCartData(clearJson);
            setCartDataLocalStorage(cartData);
            lastCorrectionSections = clearJson.sections ?? null;
          }
          break;
        }
        if (Object.keys(updates).length > 0) {
          const updateRes = await fetch(fetchURLs.update, {
            ...fetchConfig(),
            body: JSON.stringify({ updates, sections: uniqueSectionArray })
          });
          if (updateRes.status === 200) {
            const updateJson = await updateRes.json();
            cartData = shapeCartData(updateJson);
            setCartDataLocalStorage(cartData);
            lastCorrectionSections = updateJson.sections ?? null;
          }
        }
        for (const { id: changeId, quantity: changeQty } of changes) {
          const changeRes = await fetch(fetchURLs.change, {
            ...fetchConfig(),
            body: JSON.stringify({ id: changeId, quantity: changeQty, sections: uniqueSectionArray })
          });
          if (changeRes.status === 200) {
            const changeJson = await changeRes.json();
            cartData = shapeCartData(changeJson);
            setCartDataLocalStorage(cartData);
            lastCorrectionSections = changeJson.sections ?? null;
          }
        }
        if (correctionCycle === MAX_CORRECTION_CYCLES - 1) {
          console.error(`[cartRules] Max correction cycles (${MAX_CORRECTION_CYCLES}) reached \u2014 suppressing further enforcement to prevent an infinite loop.`);
        }
      }
      if (allViolationMessages.length > 0) {
        showCartRulesNotification(allViolationMessages);
      }
      publish(
        PUB_SUB_EVENTS.cartMutationComplete,
        PUB_SUB_PAYLOADS[PUB_SUB_EVENTS.cartMutationComplete]({
          cartData,
          openDrawer,
          redirectToCheckout,
          redirectUrl
        })
      );
      publish(
        PUB_SUB_EVENTS.cartInFlight,
        PUB_SUB_PAYLOADS[PUB_SUB_EVENTS.cartInFlight]({
          isInFlight: false,
          id,
          line
        })
      );
      if (isCartPage && cartData.cartRaw.item_count === 0) {
        window.location.reload();
      } else {
        replaceSections(sectionsToRender, lastCorrectionSections ?? resJson.sections);
      }
      if (redirectToCheckout) {
        window.location.assign(redirectUrl);
      }
    } else if (res.status === 422) {
      const reason = await readCartErrorReason(res);
      revealCartDrawer();
      showCartRulesNotification([reason]);
      throw new Error(reason);
    } else {
      throw new Error(res.statusText);
    }
  } catch (err) {
    console.error(err);
    publish(
      PUB_SUB_EVENTS.cartInFlight,
      PUB_SUB_PAYLOADS[PUB_SUB_EVENTS.cartInFlight]({
        isInFlight: false,
        id,
        line
      })
    );
    publish(
      PUB_SUB_EVENTS.cartMutationError,
      PUB_SUB_PAYLOADS[PUB_SUB_EVENTS.cartMutationError]({
        message: err?.message ?? "Cart update failed. Please try again."
      })
    );
  } finally {
  }
}
var mutationInFlight = false;
var queuedMutation = null;
async function runMutationQueued(args) {
  if (mutationInFlight) {
    queuedMutation = args;
    return;
  }
  mutationInFlight = true;
  queuedMutation = null;
  try {
    await mutateCartAndPublish(args);
  } finally {
    mutationInFlight = false;
    if (queuedMutation) {
      const next = queuedMutation;
      queuedMutation = null;
      runMutationQueued(next);
    }
  }
}
var mutateCartDebounce = debounce(({ action, payload, operations, line, id, onSuccess }) => {
  runMutationQueued({
    onSuccess,
    action,
    payload,
    operations,
    line,
    id,
    openDrawer: payload?.openDrawer ?? true,
    redirectToCheckout: payload?.redirectToCheckout ?? false,
    redirectUrl: payload?.redirectUrl ?? `${window.Shopify.routes.root}checkout`
  });
}, ON_CHANGE_DEBOUNCE_TIMER);
function normalizeAddItems(variantArray) {
  return (variantArray ?? []).map((lineItem) => ({
    ...lineItem,
    properties: {
      ...lineItem.properties,
      [PRODUCT_PROPERTY_KEYS.orderInitiatedBy]: lineItem.properties?.[PRODUCT_PROPERTY_KEYS.orderInitiatedBy] ?? "user"
    }
  }));
}
async function cartAdd({
  onSuccess,
  openDrawer = true,
  redirectToCheckout = false,
  redirectUrl = `${window.Shopify.routes.root}checkout`,
  variantArray = [
    {
      id: 0,
      quantity: 0,
      selling_plan: null,
      properties: {
        [PRODUCT_PROPERTY_KEYS.size]: "",
        [PRODUCT_PROPERTY_KEYS.orderInitiatedBy]: "user"
      }
    }
  ]
}) {
  const payload = {
    items: normalizeAddItems(variantArray),
    openDrawer,
    redirectToCheckout,
    redirectUrl
  };
  mutateCartDebounce({ action: "add", payload, onSuccess });
}
async function cartSequence({
  operations,
  openDrawer = true,
  redirectToCheckout = false,
  redirectUrl = `${window.Shopify.routes.root}checkout`
}) {
  const steps = operations.map(
    (step) => step.action === "add" ? { ...step, payload: { ...step.payload, items: normalizeAddItems(step.payload.items) } } : step
  );
  mutateCartDebounce({
    action: steps[steps.length - 1].action,
    payload: { openDrawer, redirectToCheckout, redirectUrl },
    operations: steps
  });
}
async function cartChange2({ id, line, quantity, sellingPlanId, properties }) {
  let currentProperties = {};
  if (properties) {
    currentProperties = await cartDataFncs.getItemPropertiesByItemKey(id);
  }
  const payload = {
    // id can be line item key or variant id: https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-change-js
    id,
    line,
    quantity,
    selling_plan: sellingPlanId,
    ...properties && { properties: { ...currentProperties, ...properties } }
  };
  mutateCartDebounce({ action: "change", payload, line, id });
}
async function cartUpdate({ variantQuantityMap }) {
  const payload = { updates: variantQuantityMap };
  mutateCartDebounce({ action: "update", payload });
}
async function cartClear() {
  const payload = null;
  mutateCartDebounce({ action: "clear", payload });
}
var cartHub = {
  cartAdd,
  cartChange: cartChange2,
  cartUpdate,
  cartClear,
  cartSequence
};
subscribe(PUB_SUB_EVENTS.cartAddRequest, cartHub.cartAdd, "cartHub");
subscribe(PUB_SUB_EVENTS.cartChangeRequest, cartHub.cartChange, "cartHub");
subscribe(PUB_SUB_EVENTS.cartUpdateRequest, cartHub.cartUpdate, "cartHub");
subscribe(PUB_SUB_EVENTS.cartClearRequest, cartHub.cartClear, "cartHub");
subscribe(PUB_SUB_EVENTS.cartSequenceRequest, cartHub.cartSequence, "cartHub");

// src/js/cart/afterpay.js
var MIN_AFTERPAY_CART_TOTAL = 3500;
var MAX_AFTERPAY_CART_TOTAL = 1e5;
var afterpayCheckTimeout = null;
function checkForAfterPayTotalPrice(cartIncludesSubscription = false) {
  const showAfterpay = window.afterpay_cart_total_price >= MIN_AFTERPAY_CART_TOTAL && window.afterpay_cart_total_price <= MAX_AFTERPAY_CART_TOTAL && !cartIncludesSubscription;
  const afterpayParentEls = document.querySelectorAll("div.afterpay-parent");
  if (afterpayParentEls.length > 0) {
    const classNameHideAfterpay = "hide-afterpay";
    afterpayParentEls.forEach((parent) => {
      if (showAfterpay) {
        parent.classList.remove(classNameHideAfterpay);
      } else {
        parent.classList.add(classNameHideAfterpay);
      }
    });
  }
}
function doesCartIncludeSubscription(cartRaw = { items: [] }) {
  return cartRaw.items?.some(
    (item) => item.selling_plan_allocation !== void 0
  );
}
function debouncedCheckForAfterPayTotalPrice({ cartData }) {
  const { cartRaw } = cartData;
  if (afterpayCheckTimeout) {
    clearTimeout(afterpayCheckTimeout);
    afterpayCheckTimeout = null;
  }
  afterpayCheckTimeout = setTimeout(() => {
    checkForAfterPayTotalPrice(doesCartIncludeSubscription(cartRaw));
  }, 1e3);
}
async function handlePageLoad() {
  const cartData = await getCartData();
  if (!cartData) {
    cartDebug("afterpay.js: no cart data on page load");
    return;
  }
  checkForAfterPayTotalPrice(
    doesCartIncludeSubscription(cartData.cartRaw)
  );
}
document.addEventListener("DOMContentLoaded", () => {
  handlePageLoad();
});
subscribe(
  PUB_SUB_EVENTS.cartMutationComplete,
  debouncedCheckForAfterPayTotalPrice,
  "afterpay"
);

// src/js/cart/cartRules.test-console.js
(function() {
  if (localStorage.getItem("muradCartDebug") !== "2" && localStorage.getItem("muradCartDebug") !== "3") {
    return;
  }
  const CONFIG = {
    // Variant IDs
    PAID_VARIANT_ID: null,
    // ← replace with a paid variant ID
    FREE_VARIANT_ID_1: null,
    // ← replace with a zero-dollar variant ID
    FREE_VARIANT_ID_2: null,
    // ← replace with a *different* zero-dollar variant ID
    // Rule 1 — paid item qty cap
    RULE_1_ENABLED: true,
    RULE_1_SETUP_QTY: 8,
    // quantity forced via direct API (must exceed RULE_1_CAP)
    RULE_1_CAP: 6,
    // expected enforced maximum (matches MAX_QTY_PER_LINE_ITEM in cartRules.js)
    // Rule 2 — zero-dollar item qty cap
    RULE_2_ENABLED: true,
    RULE_2_SETUP_QTY: 3,
    // quantity forced via direct API (must exceed RULE_2_CAP)
    RULE_2_CAP: 1,
    // expected enforced maximum (matches MAX_QTY_PER_ZERO_DOLLAR_LINE_ITEM in cartRules.js)
    // Rule 3 — duplicate zero-dollar variant collapsed to one line item
    RULE_3_ENABLED: true,
    // Rule 4 — total zero-dollar qty cap across all line items
    RULE_4_ENABLED: true,
    RULE_4_SETUP_QTY: 6,
    // quantity forced per free variant (two variants used, so total = RULE_4_SETUP_QTY * 2)
    RULE_4_CAP: 10,
    // expected enforced maximum total (matches MAX_TOTAL_ZERO_DOLLAR_QTY in cartRules.js)
    // Rule 5 — zero-dollar-only cart is cleared
    RULE_5_ENABLED: true,
    // Interactive variant selection
    // When true, selectVariantsInteractively() will prompt you to add each required
    // variant to your cart before tests run, rather than requiring IDs up front.
    // runAllTests() calls this automatically when enabled.
    USE_INTERACTIVE_VARIANT_SELECTION: true
  };
  const LS_VARIANTS_KEY = "muradCartRulesTestVariants";
  function loadCachedVariants() {
    try {
      const cached = JSON.parse(localStorage.getItem(LS_VARIANTS_KEY));
      if (cached && cached.PAID_VARIANT_ID && cached.FREE_VARIANT_ID_1 && cached.FREE_VARIANT_ID_2) {
        CONFIG.PAID_VARIANT_ID = cached.PAID_VARIANT_ID;
        CONFIG.FREE_VARIANT_ID_1 = cached.FREE_VARIANT_ID_1;
        CONFIG.FREE_VARIANT_ID_2 = cached.FREE_VARIANT_ID_2;
        console.log("\u2139\uFE0F  Loaded cached variant IDs from localStorage:", {
          PAID_VARIANT_ID: CONFIG.PAID_VARIANT_ID,
          FREE_VARIANT_ID_1: CONFIG.FREE_VARIANT_ID_1,
          FREE_VARIANT_ID_2: CONFIG.FREE_VARIANT_ID_2
        });
      }
    } catch (_) {
    }
  }
  function saveCachedVariants() {
    localStorage.setItem(LS_VARIANTS_KEY, JSON.stringify({
      PAID_VARIANT_ID: CONFIG.PAID_VARIANT_ID,
      FREE_VARIANT_ID_1: CONFIG.FREE_VARIANT_ID_1,
      FREE_VARIANT_ID_2: CONFIG.FREE_VARIANT_ID_2
    }));
  }
  function clearCachedVariants() {
    localStorage.removeItem(LS_VARIANTS_KEY);
    CONFIG.PAID_VARIANT_ID = null;
    CONFIG.FREE_VARIANT_ID_1 = null;
    CONFIG.FREE_VARIANT_ID_2 = null;
    console.log("\u{1F5D1}\uFE0F  Cached variant IDs cleared. Run selectVariantsInteractively() or set them manually.");
  }
  loadCachedVariants();
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));
  async function waitForCart(predicate, maxMs = 5e3, intervalMs = 200) {
    const deadline = Date.now() + maxMs;
    let cart;
    do {
      await wait(intervalMs);
      cart = await getCart2();
    } while (!predicate(cart) && Date.now() < deadline);
    return cart;
  }
  async function getCart2() {
    const res = await fetch("/cart.js");
    return res.json();
  }
  async function directAdd(items) {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items })
    });
    return res.json();
  }
  async function directUpdate(updates) {
    const res = await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ updates })
    });
    return res.json();
  }
  async function clearCart() {
    await fetch("/cart/clear.js", { method: "POST" });
    console.log("\u{1F9F9} Cart cleared.");
  }
  function logCart(cart) {
    console.table(
      cart.items.map((i) => ({
        title: i.title,
        variant_id: i.variant_id,
        key: i.key,
        quantity: i.quantity,
        original_price: i.original_price
      }))
    );
    console.log(
      `  item_count: ${cart.item_count} | total_price: ${cart.total_price} (cents)`
    );
  }
  function validateConfig() {
    const VARIANT_ID_KEYS = ["PAID_VARIANT_ID", "FREE_VARIANT_ID_1", "FREE_VARIANT_ID_2"];
    const keysToCheck = CONFIG.USE_INTERACTIVE_VARIANT_SELECTION ? Object.keys(CONFIG).filter((k) => !VARIANT_ID_KEYS.includes(k)) : Object.keys(CONFIG);
    const missing = keysToCheck.filter((k) => CONFIG[k] === 0).map((k) => k);
    if (missing.length) {
      console.log(
        "\u274C Fill in CONFIG values before running tests:",
        missing
      );
      return false;
    }
    if (!window.muradCartActions?.cartRefresh) {
      console.log(
        "\u274C window.muradCartActions not found \u2014 is this the right page/store?"
      );
      return false;
    }
    return true;
  }
  async function testRule1() {
    if (!CONFIG.RULE_1_ENABLED) {
      console.log("Rule 1 \u2014 skipped (RULE_1_ENABLED is false)");
      return { name: "Rule 1", skipped: true };
    }
    console.group(`Rule 1 \u2014 paid item qty capped at ${CONFIG.RULE_1_CAP}`);
    await clearCart();
    await directAdd([{ id: CONFIG.PAID_VARIANT_ID, quantity: 1 }]);
    await directUpdate({ [CONFIG.PAID_VARIANT_ID]: CONFIG.RULE_1_SETUP_QTY });
    const before = await getCart2();
    console.log(`BEFORE enforcement (expect qty ${CONFIG.RULE_1_SETUP_QTY}):`);
    logCart(before);
    const beforeItem = before.items.find((i) => i.variant_id == CONFIG.PAID_VARIANT_ID);
    if (!beforeItem || beforeItem.quantity !== CONFIG.RULE_1_SETUP_QTY) {
      const got = beforeItem ? `qty ${beforeItem.quantity}` : "item not found";
      console.log(`\u23ED\uFE0F SKIP: pre-condition not met \u2014 expected paid item at qty ${CONFIG.RULE_1_SETUP_QTY}, got ${got}`);
      console.groupEnd();
      return { name: "Rule 1", skipped: true, reason: `pre-condition: ${got}` };
    }
    window.muradCartActions.cartRefresh();
    console.log("Polling for enforcement result\u2026");
    const after = await waitForCart(
      (cart) => {
        const item2 = cart.items.find((i) => i.variant_id == CONFIG.PAID_VARIANT_ID);
        return item2 && item2.quantity <= CONFIG.RULE_1_CAP;
      }
    );
    console.log(`AFTER enforcement (expect qty ${CONFIG.RULE_1_CAP}):`);
    logCart(after);
    const item = after.items.find(
      (i) => i.variant_id == CONFIG.PAID_VARIANT_ID
    );
    const passed = item?.quantity === CONFIG.RULE_1_CAP;
    if (passed) {
      console.log("\u2705 PASS");
    } else {
      console.log(`\u274C FAIL: expected qty ${CONFIG.RULE_1_CAP}, got ${item?.quantity}`);
      console.log("BEFORE (full):", before);
      console.log("AFTER (full):", after);
    }
    console.assert(passed, `FAIL: expected qty ${CONFIG.RULE_1_CAP}, got ${item?.quantity}`);
    console.groupEnd();
    return { name: "Rule 1", passed };
  }
  async function testRule2() {
    if (!CONFIG.RULE_2_ENABLED) {
      console.log("Rule 2 \u2014 skipped (RULE_2_ENABLED is false)");
      return { name: "Rule 2", skipped: true };
    }
    console.group(`Rule 2 \u2014 zero-dollar item qty capped at ${CONFIG.RULE_2_CAP}`);
    await clearCart();
    await directAdd([
      { id: CONFIG.PAID_VARIANT_ID, quantity: 1 },
      { id: CONFIG.FREE_VARIANT_ID_1, quantity: 1 }
    ]);
    await directUpdate({ [CONFIG.FREE_VARIANT_ID_1]: CONFIG.RULE_2_SETUP_QTY });
    const before = await getCart2();
    console.log(`BEFORE enforcement (expect free item qty ${CONFIG.RULE_2_SETUP_QTY}):`);
    logCart(before);
    const beforeItem = before.items.find((i) => i.variant_id == CONFIG.FREE_VARIANT_ID_1);
    if (!beforeItem || beforeItem.quantity !== CONFIG.RULE_2_SETUP_QTY) {
      const got = beforeItem ? `qty ${beforeItem.quantity}` : "item not found";
      console.log(`\u23ED\uFE0F SKIP: pre-condition not met \u2014 expected free item at qty ${CONFIG.RULE_2_SETUP_QTY}, got ${got}`);
      console.groupEnd();
      return { name: "Rule 2", skipped: true, reason: `pre-condition: ${got}` };
    }
    window.muradCartActions.cartRefresh();
    console.log("Polling for enforcement result\u2026");
    const after = await waitForCart(
      (cart) => {
        const item2 = cart.items.find((i) => i.variant_id == CONFIG.FREE_VARIANT_ID_1);
        return item2 && item2.quantity <= CONFIG.RULE_2_CAP;
      }
    );
    console.log(`AFTER enforcement (expect free item qty ${CONFIG.RULE_2_CAP}):`);
    logCart(after);
    const item = after.items.find(
      (i) => i.variant_id == CONFIG.FREE_VARIANT_ID_1
    );
    const passed = item?.quantity === CONFIG.RULE_2_CAP;
    if (passed) {
      console.log("\u2705 PASS");
    } else {
      console.log(`\u274C FAIL: expected qty ${CONFIG.RULE_2_CAP}, got ${item?.quantity}`);
      console.log("BEFORE (full):", before);
      console.log("AFTER (full):", after);
    }
    console.assert(passed, `FAIL: expected qty ${CONFIG.RULE_2_CAP}, got ${item?.quantity}`);
    console.groupEnd();
    return { name: "Rule 2", passed };
  }
  async function testRule3() {
    if (!CONFIG.RULE_3_ENABLED) {
      console.log("Rule 3 \u2014 skipped (RULE_3_ENABLED is false)");
      return { name: "Rule 3", skipped: true };
    }
    console.group("Rule 3 \u2014 duplicate zero-dollar variant removed");
    await clearCart();
    await directAdd([{ id: CONFIG.PAID_VARIANT_ID, quantity: 1 }]);
    await directAdd([
      { id: CONFIG.FREE_VARIANT_ID_1, quantity: 1, properties: { _test: "a" } }
    ]);
    await directAdd([
      { id: CONFIG.FREE_VARIANT_ID_1, quantity: 1, properties: { _test: "b" } }
    ]);
    const before = await getCart2();
    const dupCount = before.items.filter(
      (i) => i.variant_id == CONFIG.FREE_VARIANT_ID_1
    ).length;
    console.log(
      `BEFORE enforcement (${dupCount} line item(s) for free variant \u2014 need 2 to test Rule 3):`
    );
    logCart(before);
    if (dupCount < 2) {
      console.log("\u23ED\uFE0F SKIP: pre-condition not met \u2014 Shopify merged the two adds into one line item. This variant does not support differing properties; try a different free variant or test against a real Yotpo/Swell redemption.");
      console.groupEnd();
      return { name: "Rule 3", skipped: true, reason: "pre-condition: Shopify merged duplicate adds into one line item" };
    }
    window.muradCartActions.cartRefresh();
    console.log("Polling for enforcement result\u2026");
    const after = await waitForCart(
      (cart) => cart.items.filter((i) => i.variant_id == CONFIG.FREE_VARIANT_ID_1).length === 1
    );
    const afterCount = after.items.filter(
      (i) => i.variant_id == CONFIG.FREE_VARIANT_ID_1
    ).length;
    console.log(
      `AFTER enforcement (expect 1 line item for free variant, got ${afterCount}):`
    );
    logCart(after);
    const passed = afterCount === 1;
    if (passed) {
      console.log("\u2705 PASS");
    } else {
      console.log(`\u274C FAIL: expected 1 line item, got ${afterCount}`);
      console.log("BEFORE (full):", before);
      console.log("AFTER (full):", after);
    }
    console.assert(passed, `FAIL: expected 1 line item, got ${afterCount}`);
    console.groupEnd();
    return { name: "Rule 3", passed };
  }
  async function testRule4() {
    if (!CONFIG.RULE_4_ENABLED) {
      console.log("Rule 4 \u2014 skipped (RULE_4_ENABLED is false)");
      return { name: "Rule 4", skipped: true };
    }
    console.group(`Rule 4 \u2014 total zero-dollar qty capped at ${CONFIG.RULE_4_CAP}`);
    await clearCart();
    await directAdd([{ id: CONFIG.PAID_VARIANT_ID, quantity: 1 }]);
    await directAdd([
      { id: CONFIG.FREE_VARIANT_ID_1, quantity: 1 },
      { id: CONFIG.FREE_VARIANT_ID_2, quantity: 1 }
    ]);
    await directUpdate({
      [CONFIG.FREE_VARIANT_ID_1]: CONFIG.RULE_4_SETUP_QTY,
      [CONFIG.FREE_VARIANT_ID_2]: CONFIG.RULE_4_SETUP_QTY
    });
    const before = await getCart2();
    const totalFreeBefore = before.items.filter((i) => i.original_price === 0).reduce((sum, i) => sum + i.quantity, 0);
    const expectedSetupTotal = CONFIG.RULE_4_SETUP_QTY * 2;
    console.log(
      `BEFORE enforcement (total free qty: ${totalFreeBefore}, expect ${expectedSetupTotal}):`
    );
    logCart(before);
    if (totalFreeBefore !== expectedSetupTotal) {
      console.log(`\u23ED\uFE0F SKIP: pre-condition not met \u2014 expected total free qty ${expectedSetupTotal}, got ${totalFreeBefore}`);
      console.groupEnd();
      return { name: "Rule 4", skipped: true, reason: `pre-condition: total free qty ${totalFreeBefore}` };
    }
    window.muradCartActions.cartRefresh();
    console.log("Polling for enforcement result\u2026");
    const after = await waitForCart(
      (cart) => cart.items.filter((i) => i.original_price === 0).reduce((sum, i) => sum + i.quantity, 0) <= CONFIG.RULE_4_CAP
    );
    const totalFreeAfter = after.items.filter((i) => i.original_price === 0).reduce((sum, i) => sum + i.quantity, 0);
    console.log(
      `AFTER enforcement (total free qty: ${totalFreeAfter}, expect <= ${CONFIG.RULE_4_CAP}):`
    );
    logCart(after);
    const passed = totalFreeAfter <= CONFIG.RULE_4_CAP;
    if (passed) {
      console.log("\u2705 PASS");
    } else {
      console.log(`\u274C FAIL: total free qty ${totalFreeAfter} exceeds ${CONFIG.RULE_4_CAP}`);
      console.log("BEFORE (full):", before);
      console.log("AFTER (full):", after);
    }
    console.assert(
      passed,
      `FAIL: total free qty ${totalFreeAfter} exceeds ${CONFIG.RULE_4_CAP}`
    );
    console.groupEnd();
    return { name: "Rule 4", passed };
  }
  async function testRule5() {
    if (!CONFIG.RULE_5_ENABLED) {
      console.log("Rule 5 \u2014 skipped (RULE_5_ENABLED is false)");
      return { name: "Rule 5", skipped: true };
    }
    console.group("Rule 5 \u2014 zero-dollar-only cart is cleared");
    await clearCart();
    await directAdd([{ id: CONFIG.FREE_VARIANT_ID_1, quantity: 1 }]);
    const before = await getCart2();
    console.log(
      `BEFORE enforcement (total_price: ${before.total_price}, expect 0; item_count: ${before.item_count}):`
    );
    logCart(before);
    if (before.total_price !== 0 || before.item_count === 0) {
      const reason = before.item_count === 0 ? "cart is empty (free item failed to add)" : `cart has non-zero total_price ${before.total_price} (item may not be zero-dollar)`;
      console.log(`\u23ED\uFE0F SKIP: pre-condition not met \u2014 ${reason}`);
      console.groupEnd();
      return { name: "Rule 5", skipped: true, reason };
    }
    window.muradCartActions.cartRefresh();
    console.log("Polling for enforcement result\u2026");
    const after = await waitForCart((cart) => cart.item_count === 0);
    console.log(
      `AFTER enforcement (item_count: ${after.item_count}, expect 0):`
    );
    logCart(after);
    const passed = after.item_count === 0;
    if (passed) {
      console.log("\u2705 PASS");
    } else {
      console.log(`\u274C FAIL: expected empty cart, got ${after.item_count} items`);
      console.log("BEFORE (full):", before);
      console.log("AFTER (full):", after);
    }
    console.assert(passed, `FAIL: expected empty cart, got ${after.item_count} items`);
    console.groupEnd();
    return { name: "Rule 5", passed };
  }
  async function selectVariantsInteractively() {
    const POLL_INTERVAL_MS = 1500;
    const POLL_TIMEOUT_MS = 9e4;
    const PROMPTS = [
      {
        key: "PAID_VARIANT_ID",
        instruction: "Add any PAID item (original_price > 0) to your cart now, then wait\u2026",
        validate: (item) => item.original_price > 0,
        errorHint: "The item added has original_price === 0. Please add a paid product."
      },
      {
        key: "FREE_VARIANT_ID_1",
        instruction: "Add a ZERO-DOLLAR item (free sample / GWP) to your cart now, then wait\u2026",
        validate: (item) => item.original_price === 0,
        errorHint: "The item added has original_price > 0. Please add a free/zero-dollar product."
      },
      {
        key: "FREE_VARIANT_ID_2",
        instruction: "Add a SECOND, DIFFERENT zero-dollar item to your cart now, then wait\u2026",
        validate: (item) => item.original_price === 0,
        errorHint: "The item added has original_price > 0. Please add a free/zero-dollar product."
      }
    ];
    console.group("\u{1F527} Interactive variant selection");
    console.log(
      "Each step will wait up to " + POLL_TIMEOUT_MS / 1e3 + " s for you to add an item.\nThe cart will be cleared before the tests begin \u2014 your additions here are temporary."
    );
    const capturedVariantIds = /* @__PURE__ */ new Set();
    for (const prompt of PROMPTS) {
      const snapshot = await getCart2();
      const knownIds = /* @__PURE__ */ new Set([
        ...snapshot.items.map((i) => String(i.variant_id)),
        ...capturedVariantIds
      ]);
      console.log(`
\u{1F449} [${prompt.key}] ${prompt.instruction}`);
      const newItem = await (async () => {
        const deadline = Date.now() + POLL_TIMEOUT_MS;
        while (Date.now() < deadline) {
          await wait(POLL_INTERVAL_MS);
          const current = await getCart2();
          const added = current.items.find(
            (i) => !knownIds.has(String(i.variant_id))
          );
          if (added)
            return added;
        }
        return null;
      })();
      if (!newItem) {
        console.log(`\u274C Timed out waiting for ${prompt.key}. Aborting interactive selection.`);
        console.groupEnd();
        return false;
      }
      if (!prompt.validate(newItem)) {
        console.log(
          `\u274C Unexpected item added for ${prompt.key}: "${newItem.title}" (original_price: ${newItem.original_price}).`,
          prompt.errorHint
        );
        console.groupEnd();
        return false;
      }
      CONFIG[prompt.key] = newItem.variant_id;
      capturedVariantIds.add(String(newItem.variant_id));
      console.log(
        `  \u2705 ${prompt.key} set to ${newItem.variant_id} ("${newItem.title}")`
      );
    }
    console.log("\nAll variant IDs captured:", {
      PAID_VARIANT_ID: CONFIG.PAID_VARIANT_ID,
      FREE_VARIANT_ID_1: CONFIG.FREE_VARIANT_ID_1,
      FREE_VARIANT_ID_2: CONFIG.FREE_VARIANT_ID_2
    });
    saveCachedVariants();
    console.log("\u{1F4BE} Variant IDs saved to localStorage \u2014 won't need to re-select next run.");
    console.groupEnd();
    return true;
  }
  async function runAllTests() {
    const variantIdsDefined = CONFIG.PAID_VARIANT_ID !== null && CONFIG.FREE_VARIANT_ID_1 !== null && CONFIG.FREE_VARIANT_ID_2 !== null;
    if (CONFIG.USE_INTERACTIVE_VARIANT_SELECTION && !variantIdsDefined) {
      const ok = await selectVariantsInteractively();
      if (!ok)
        return;
    } else if (variantIdsDefined) {
      console.log("\u2139\uFE0F  Variant IDs already set \u2014 skipping interactive selection.");
    }
    if (!validateConfig())
      return;
    console.group("\u{1F9EA} Cart Rules \u2014 Full Test Suite");
    const results = [
      await testRule1(),
      await testRule2(),
      await testRule3(),
      await testRule4(),
      await testRule5()
    ].filter(Boolean);
    await clearCart();
    console.log("Suite complete. Cart cleared.");
    const passed = results.filter((r) => r.passed === true);
    const failed = results.filter((r) => r.passed === false);
    const skipped = results.filter((r) => r.skipped);
    console.group(
      `\u{1F4CB} Results: ${passed.length} passed, ${failed.length} failed, ${skipped.length} skipped`
    );
    results.forEach((r) => {
      if (r.skipped)
        console.log(`  \u23ED\uFE0F  SKIP \u2014 ${r.name}`);
      else if (r.passed)
        console.log(`  \u2705 PASS \u2014 ${r.name}`);
      else
        console.log(`  \u274C FAIL \u2014 ${r.name}`);
    });
    console.groupEnd();
    console.groupEnd();
  }
  window._cartRulesTest = {
    CONFIG,
    testRule1,
    testRule2,
    testRule3,
    testRule4,
    testRule5,
    runAllTests,
    selectVariantsInteractively,
    clearCachedVariants,
    clearCart,
    getCart: getCart2
  };
  console.log(
    [
      "Cart rules test helpers loaded on window._cartRulesTest.",
      "",
      "  Option A \u2014 provide variant IDs manually:",
      "    window._cartRulesTest.CONFIG.PAID_VARIANT_ID   = <id>",
      "    window._cartRulesTest.CONFIG.FREE_VARIANT_ID_1 = <id>",
      "    window._cartRulesTest.CONFIG.FREE_VARIANT_ID_2 = <id>",
      "    window._cartRulesTest.runAllTests()",
      "",
      "  Option B \u2014 select variants interactively (add items to cart when prompted):",
      "    window._cartRulesTest.CONFIG.USE_INTERACTIVE_VARIANT_SELECTION = true",
      "    window._cartRulesTest.runAllTests()  // prompts before tests run",
      "    // or run the selection step alone:",
      "    window._cartRulesTest.selectVariantsInteractively()",
      "    // IDs are cached in localStorage after selection \u2014 won't prompt again until cleared:",
      "    window._cartRulesTest.clearCachedVariants()  // clears cache + resets CONFIG IDs",
      "",
      "  Run a single test: window._cartRulesTest.testRule1()",
      "",
      "  Tip \u2014 inspect variant IDs in the current cart:",
      "    fetch('/cart.js').then(r=>r.json()).then(c=>console.table(c.items.map(i=>({title:i.title,variant_id:i.variant_id,original_price:i.original_price}))))"
    ].join("\n")
  );
})();

// src/global.vbt.js
window.logout = async function() {
  const res = await fetch("/account/logout/");
  if (res.status === 200) {
    location = "/account/login/?logged_out=true";
  }
};
document.addEventListener(
  "DOMContentLoaded",
  async function globalComponents() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("disableSignup")) {
      if (urlParams.get("disableSignup") == "1") {
        document.cookie = "disableSignup=1";
      }
    }
    if (document.querySelectorAll("[data-login-modal]")) {
      document.querySelectorAll("[data-login-modal]").forEach(
        (login) => login.addEventListener("click", () => {
          if (window.innerWidth < 1024) {
            document.querySelector("#account-modal--mobile").checked = true;
          } else {
            document.querySelector("#account-modal--desktop").checked = true;
          }
          if (login.hasAttribute("data-create-account")) {
            document.querySelector("#register--desktop").checked = true;
          } else if (login.hasAttribute("data-sign-in")) {
            document.querySelector("#register--desktop").checked = false;
          }
        })
      );
    }
    if (window.location.href.includes("cart=open")) {
      document.querySelector("#cart-drawer-toggle").checked = true;
    }
    handleScrollLock();
    handleSwell();
    handleYotpoStyles();
    const components = {};
    if (document.querySelector("[data-countdown]")) {
      const Countdown = await import("./chunk--Countdown-FJ7KFU2G.vbt.js");
      components["countdown"] = Countdown.default;
    }
    if (document.querySelector("[data-gwp]")) {
      const GWP = await import("./chunk--GWP-4OCBTFOV.vbt.js");
      components["gwp"] = GWP.default;
    }
    if (document.querySelector("[data-verify-password]")) {
      const VerifyPassword = await import("./chunk--VerifyPassword-XPDFJKY7.vbt.js");
      components["verify-password"] = VerifyPassword.default;
    }
    if (document.querySelector("[data-promo-bar]")) {
      const PromoBar = await import("./chunk--PromoBar-WCNRDM6Y.vbt.js");
      components["promo-bar"] = PromoBar.default;
    }
    if (document.querySelector("[data-ajaxinate]")) {
      const AjaxinateComponent = await import("./chunk--Ajaxinate-5BEN7YRK.vbt.js");
      components["ajaxinate"] = AjaxinateComponent.default;
    }
    ComponentConstructor(components);
    document.dispatchEvent(new CustomEvent("global components loaded"));
    this.removeEventListener("DOMContentLoaded", globalComponents);
  }
);
document.addEventListener(
  "global components loaded",
  async function asyncComponents() {
    const components = {};
    if (document.querySelector("[data-slider]")) {
      const Slider = await import("./chunk--Slider-D6YNBQKI.vbt.js");
      components["slider"] = Slider.default;
    }
    if (document.querySelector("[data-smooth-scroll]")) {
      const SmoothScroll = await import("./chunk--SmoothScroll-A7TVKOK2.vbt.js");
      components["smooth-scroll"] = SmoothScroll.default;
    }
    if (document.querySelector("[data-intersection-change]")) {
      const ChangeOnIntersection = await import("./chunk--ChangeOnIntersection-LFYXFGY5.vbt.js");
      components["intersection-change"] = ChangeOnIntersection.default;
    }
    if (document.querySelector("[data-accordion]")) {
      const Accordion = await import("./chunk--Accordion-JHYKLMR6.vbt.js");
      components["accordion"] = Accordion.default;
    }
    if (document.querySelector("[data-sticky-cta]")) {
      const StickyCTA = await import("./chunk--StickyCTA-LYBDEVUY.vbt.js");
      components["sticky-cta"] = StickyCTA.default;
    }
    if (document.querySelector("[data-verify-form]")) {
      const VerifyForm = await import("./chunk--VerifyForm-V3JZYEFK.vbt.js");
      components["verify-form"] = VerifyForm.default;
    }
    if (document.querySelector("[data-recharge-widget]")) {
      const RechargeWidget = await import("./chunk--RechargeWidget-TZ42FF7F.vbt.js");
      components["recharge-widget"] = RechargeWidget.default;
    }
    if (document.querySelector("[data-tabs]")) {
      const Tabs = await import("./chunk--Tabs-XDGVW46J.vbt.js");
      components["tabs"] = Tabs.default;
    }
    if (document.querySelector("[data-estimated-delivery-api]")) {
      const EstimatedDelivery = await import("./chunk--EstimatedDelivery-NIZGQAS4.vbt.js");
      components["estimated-delivery-api"] = EstimatedDelivery.default;
    }
    if (document.querySelector("[data-storemapper-id]")) {
      const Storemapper = await import("./chunk--Storemapper-IN54Q57Z.vbt.js");
      new Storemapper.default().init();
    }
    if (document.querySelector("[data-scroll-to]")) {
      const ScrollTo = await import("./chunk--ScrollTo-7GHXG22R.vbt.js");
      components["scroll-to"] = ScrollTo.default;
    }
    if (document.querySelector("[data-variant]")) {
      const Variant = await import("./chunk--Variant-JZT7VUB4.vbt.js");
      components["variant"] = Variant.default;
    }
    ComponentConstructor(components);
    document.dispatchEvent(new CustomEvent("async components loaded"));
    this.removeEventListener("global components loaded", asyncComponents);
  }
);
document.addEventListener(
  "async components loaded",
  async function deferredComponentListeners() {
    const components = {};
    if (document.querySelector("[data-yotpo-rewards]")) {
      const YotpoRewards = await import("./chunk--YotpoRewards-PZEPKZK5.vbt.js");
      components["yotpo-rewards"] = [
        YotpoRewards.default,
        "url:/pages/rewards"
      ];
    }
    if (document.querySelector("[data-searchspring]")) {
      const Searchspring = await import("./chunk--Searchspring-USZJHMDS.vbt.js");
      components["searchspring"] = [
        Searchspring.default,
        "mouseover",
        "url:/collections/",
        "url:/search"
      ];
    }
    if (document.querySelector("[data-filters]")) {
      const Filters = await import("./chunk--Filters-G26NZQIR.vbt.js");
      components["filters"] = Filters.default;
    }
    if (document.querySelector("[data-iframe]")) {
      const IFrame = await import("./chunk--IFrame-ULTPB7IU.vbt.js");
      components["iframe"] = [IFrame.default, "mouseover"];
    }
    if (document.querySelector("[data-range-reveal]")) {
      const RangeReveal = await import("./chunk--RangeReveal-FNVXLKF2.vbt.js");
      components["range-reveal"] = RangeReveal.default;
    }
    if (document.querySelector("[data-predictive-search]")) {
      const PredictiveSearch = await import("./chunk--PredictiveSearch-25OVNKD6.vbt.js");
      components["predictive-search"] = [
        PredictiveSearch.default,
        "mouseover",
        "click"
      ];
    }
    if (document.querySelector("[data-product-form]")) {
      const ProductForm = await import("./chunk--ProductForm-7XDXWH5P.vbt.js");
      components["product-form"] = [
        ProductForm.default,
        "mouseover",
        "url:/products/"
      ];
    }
    ComponentConstructor(components);
    this.removeEventListener(
      "async components loaded",
      deferredComponentListeners
    );
  }
);
document.addEventListener(
  "deferred component listeners attached",
  async function asyncComponents2() {
    const components = {};
    ComponentConstructor(components);
    document.removeEventListener(
      "deferred component listeners attached",
      asyncComponents2
    );
    this.removeEventListener("async components loaded", asyncComponents2);
  }
);
function handleScrollLock() {
  document.querySelectorAll("[data-scroll-lock]").forEach((el) => {
    el.addEventListener("change", function() {
      if (this.checked) {
        document.body.setAttribute("style", "overflow:hidden;");
      } else {
        document.body.removeAttribute("style");
      }
      if (el.id === "search-modal-toggle") {
        document.querySelector("#search").focus();
      }
      if (el.id === "account-modal--mobile") {
        document.querySelector("#account-modal--mobile--content").style.height = window.innerHeight + "px";
      }
    });
  });
}
function handleSwell() {
  const STORE_KEY = "muradYotpoPoints";
  const store = window[STORE_KEY] = window[STORE_KEY] || {
    balance: null,
    setBalance(value) {
      if (typeof value !== "number" || Number.isNaN(value)) {
        return;
      }
      this.balance = value;
      updatePointNodes(value);
    },
    adjustBalance(delta) {
      if (typeof this.balance !== "number")
        return;
      const next = Math.max(this.balance + delta, 0);
      this.setBalance(next);
    }
  };
  const rewardTracker = {
    active: /* @__PURE__ */ new Map()
  };
  function updatePointNodes(balance) {
    document.querySelectorAll("[data-yotpo-points]").forEach((el) => {
      el.innerHTML = balance.toLocaleString();
      const parent = el.parentElement?.hasAttribute("data-hide-container") && el.parentElement;
      const wrapper = parent || el.closest("[data-hide-container]");
      if (wrapper) {
        wrapper.style.removeProperty("display");
        wrapper.style.removeProperty("opacity");
      }
    });
  }
  function getRewardCost(item) {
    const costKeys = [
      "_yotpo_redemption_points_cost",
      "_swell_points_used",
      "_yotpo_points_cost"
    ];
    for (const key of costKeys) {
      const raw = item?.properties?.[key];
      if (raw === void 0 || raw === null || raw === "")
        continue;
      const cost = parseInt(raw, 10);
      if (!Number.isNaN(cost)) {
        return cost;
      }
    }
    return null;
  }
  function getRewardIdentifier(item) {
    const candidates = [
      item?.properties?._yotpo_redemption_option_id,
      item?.properties?._swell_reward_id,
      item?.key,
      item?.id,
      item?.variant_id
    ];
    return candidates.find((candidate) => candidate !== void 0 && candidate !== null);
  }
  function extractRewardMap(items = []) {
    const map = /* @__PURE__ */ new Map();
    items.forEach((item) => {
      const cost = getRewardCost(item);
      const identifier = getRewardIdentifier(item);
      if (cost === null || identifier === void 0)
        return;
      map.set(String(identifier), cost);
    });
    return map;
  }
  function syncRewards(nextMap, { skipAdjust = false } = {}) {
    if (!skipAdjust && typeof store.balance === "number") {
      nextMap.forEach((cost, optionId) => {
        if (!rewardTracker.active.has(optionId)) {
          store.adjustBalance(-cost);
        }
      });
      rewardTracker.active.forEach((cost, optionId) => {
        if (!nextMap.has(optionId)) {
          store.adjustBalance(cost);
          if (typeof window.swellAPI?.removePointRedemptionIdFromCart === "function") {
            window.swellAPI.removePointRedemptionIdFromCart(optionId, () => {
            });
          }
        }
      });
    }
    rewardTracker.active = nextMap;
  }
  async function primeRewards() {
    try {
      const res = await fetch("/cart.js");
      if (!res.ok)
        return;
      const cartJson = await res.json();
      syncRewards(extractRewardMap(cartJson.items), { skipAdjust: true });
    } catch (err) {
      console.error(err);
    }
  }
  subscribe(
    PUB_SUB_EVENTS.cartMutationComplete,
    ({ cartData }) => {
      const items = cartData?.cartRaw?.items;
      if (!items)
        return;
      syncRewards(extractRewardMap(items));
    },
    "yotpo-reward-sync"
  );
  document.addEventListener("swell:setup", () => {
    const { vipTier, pointsBalance } = window.Swell.Customer.getCustomerDetails();
    if (typeof pointsBalance === "number") {
      store.setBalance(pointsBalance);
    }
    if (vipTier) {
      let tier = vipTier.name;
      if (!tier) {
        tier = "Bronze";
      }
      document.querySelectorAll("[data-yotpo-tier]").forEach((el) => {
        el.innerHTML = tier;
        const wrapper = el.closest("[data-hide-container]");
        if (wrapper) {
          wrapper.style.removeProperty("display");
          wrapper.style.removeProperty("opacity");
        }
      });
    }
    if (document.querySelector("#rewards-header")) {
      document.querySelector("#rewards-header").classList.remove("hidden");
    }
    primeRewards();
  });
}
function handleYotpoStyles() {
  new MutationObserver(function() {
    const yotpoTitle = document.querySelector(
      ".yotpo-bottom-line .yotpo-bottom-line-left-panel.yotpo-bottom-line-score"
    );
    if (yotpoTitle) {
      yotpoTitle.style.fontFamily = "MatterSemiMono";
      this.disconnect();
    }
  }).observe(document.body, {
    childList: true,
    subtree: true
  });
}
